import React, { Component } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { Card } from 'primereact/card';
import { Panel } from 'primereact/panel';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { InputNumber } from 'primereact/inputnumber';
import { Dropdown } from 'primereact/dropdown';
import { ColorPicker } from 'primereact/colorpicker';
import { Checkbox } from 'primereact/checkbox';
import { TabView, TabPanel } from 'primereact/tabview';
import { Toast } from 'primereact/toast';
import { ProgressBar } from 'primereact/progressbar';
import { Row, Col } from 'react-bootstrap';
import { withTranslation } from 'react-i18next';
import CarteV2Service from '../../../../services/CarteV2Service';
import './TemplateDragDropEditor.css';

// Dimensions de carte standard (85.6 x 53.98 mm à 300 DPI)
const CARD_WIDTH = 1012;
const CARD_HEIGHT = 638;
const SCALE_FACTOR = 0.4; // Pour l'affichage dans l'éditeur

// Contraintes de validation basées sur le backend
const VALIDATION_RULES = {
    position: {
        x: { min: 0, max: 1000 },
        y: { min: 0, max: 1000 },
        width: { min: 10, max: 500 },
        height: { min: 10, max: 500 }
    },
    colors: {
        pattern: /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/
    },
    typography: {
        titleFontSize: { min: 8, max: 48 },
        nameFontSize: { min: 12, max: 64 },
        infoFontSize: { min: 8, max: 32 }
    }
};

// Scrubby Label Component - Similar to Photoshop's drag-to-adjust labels
class ScrubbyLabel extends Component {
    constructor(props) {
        super(props);
        this.state = {
            isDragging: false,
            dragStarted: false,
            startX: 0,
            startValue: 0
        };
        this.dragThreshold = 3; // pixels to move before drag starts
    }

    handleMouseDown = (e) => {
        // Only handle left mouse button
        if (e.button !== 0) return;

        // Remove any existing listeners first (safety)
        this.cleanupListeners();

        this.setState({
            isDragging: true,
            dragStarted: false,
            startX: e.clientX,
            startValue: this.props.value || 0
        });

        document.addEventListener('mousemove', this.handleMouseMove, { passive: false });
        document.addEventListener('mouseup', this.handleMouseUp, { passive: false });
    };

    handleMouseMove = (e) => {
        if (!this.state.isDragging) return;

        const { startX, startValue, dragStarted } = this.state;
        const { min = -Infinity, max = Infinity, step = 1, onChange } = this.props;

        // Calculate delta (pixels moved)
        const deltaX = e.clientX - startX;

        // Check if we've moved enough to start dragging
        if (!dragStarted && Math.abs(deltaX) < this.dragThreshold) {
            return; // Don't start dragging until threshold is met
        }

        // Mark as drag started and apply drag styles
        if (!dragStarted) {
            e.preventDefault();
            this.setState({ dragStarted: true });
            document.body.style.cursor = 'ew-resize';
            document.body.style.userSelect = 'none';
        }

        e.preventDefault();

        // Determine step size (larger step with Shift key)
        const adjustedStep = e.shiftKey ? step * 10 : step;

        // Calculate new value (1 pixel = adjustedStep units)
        const valueChange = Math.round(deltaX / 2) * adjustedStep; // Divide by 2 for smoother control
        let newValue = startValue + valueChange;

        // Clamp to min/max
        newValue = Math.max(min, Math.min(max, newValue));

        // Call onChange if value changed
        if (newValue !== this.props.value && onChange) {
            onChange(newValue);
        }
    };

    handleMouseUp = () => {
        this.setState({ isDragging: false, dragStarted: false });
        this.cleanupListeners();
    };

    cleanupListeners = () => {
        document.removeEventListener('mousemove', this.handleMouseMove);
        document.removeEventListener('mouseup', this.handleMouseUp);

        // Restore cursor
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
    };

    componentWillUnmount() {
        // Cleanup listeners if component unmounts while dragging
        this.cleanupListeners();
    }

    render() {
        const { children, style, icon } = this.props;
        const { isDragging } = this.state;

        return (
            <div
                onMouseDown={this.handleMouseDown}
                style={{
                    cursor: isDragging ? 'ew-resize' : 'ew-resize',
                    userSelect: 'none',
                    fontSize: '12px',
                    fontWeight: 500,
                    color: isDragging ? '#007bff' : '#495057',
                    transition: 'color 0.1s',
                    display: 'inline-flex',
                    alignItems: 'center',
                    ...style
                }}
            >
                {icon && <i className={icon} style={{ fontSize: '10px', marginRight: '6px', color: '#6c757d' }}></i>}
                {children}
            </div>
        );
    }
}

class TemplateDragDropEditor extends Component {
    constructor(props) {
        super(props);
        this.state = {
            // Template de base
            baseTemplate: props.template || null,
            templateName: props.template?.name || 'Nouveau Template',
            templateCategory: props.template?.category || 'Modern',

            // Éléments configurables - Design basé sur Activa Medical Insurance ID Card
            elements: [
                // ============ FRONT SIDE ============

                // 1. Organization Title (STREET CHILD INTERNATIONAL)
                {
                    id: 'org-title',
                    type: 'text',
                    subType: 'organizationTitle',
                    side: 'front',
                    enabled: true,
                    position: { x: 25, y: 25 },
                    properties: {
                        text: 'STREET CHILD INTERNATIONAL (S.C.I)',
                        fontSize: 18,
                        fontFamily: 'Arial',
                        fontWeight: 'bold',
                        color: '#003087'  // Bleu foncé Activa
                    }
                },

                // 2. Organization Subtitle (MEDICAL INSURANCE ID CARD)
                {
                    id: 'org-subtitle',
                    type: 'text',
                    subType: 'organizationSubtitle',
                    side: 'front',
                    enabled: true,
                    position: { x: 25, y: 50 },
                    properties: {
                        text: 'MEDICAL INSURANCE ID CARD',
                        fontSize: 14,
                        fontFamily: 'Arial',
                        fontWeight: 'bold',
                        color: '#DC143C'  // Rouge Activa
                    }
                },

                // 3. Logo Activa (top right)
                {
                    id: 'logo',
                    type: 'logo',
                    side: 'front',
                    enabled: true,
                    position: { x: 480, y: 20, width: 140, height: 80 },
                    properties: {
                        alignment: 'right',
                        imageUrl: null
                    }
                },

                // 4. Photo avec bordure bleue arrondie
                {
                    id: 'photo',
                    type: 'photo',
                    side: 'front',
                    enabled: true,
                    position: { x: 20, y: 100, width: 180, height: 200 },
                    properties: {
                        shape: 'roundedRectangle',
                        borderColor: '#003087',  // Bleu Activa
                        borderWidth: 3
                    }
                },

                // 5. Label EMPLOYEE:
                {
                    id: 'employee-label',
                    type: 'text',
                    subType: 'customLabel',
                    side: 'front',
                    enabled: true,
                    position: { x: 220, y: 165 },
                    properties: {
                        text: 'EMPLOYEE:',
                        fontSize: 16,
                        fontFamily: 'Arial',
                        fontWeight: 'bold',
                        color: '#003087'
                    }
                },

                // 6. Employee Name
                {
                    id: 'name',
                    type: 'text',
                    subType: 'name',
                    side: 'front',
                    enabled: true,
                    position: { x: 345, y: 165 },
                    properties: {
                        text: '{{NOM}} {{PRENOM}}',
                        fontSize: 16,
                        fontFamily: 'Arial',
                        fontWeight: 'normal',
                        color: '#000000'
                    }
                },

                // 7. Label DATE OF BIRTH:
                {
                    id: 'dob-label',
                    type: 'text',
                    subType: 'customLabel',
                    side: 'front',
                    enabled: true,
                    position: { x: 220, y: 205 },
                    properties: {
                        text: 'DATE OF BIRTH:',
                        fontSize: 16,
                        fontFamily: 'Arial',
                        fontWeight: 'bold',
                        color: '#003087'
                    }
                },

                // 8. Date of Birth
                {
                    id: 'dob-value',
                    type: 'text',
                    subType: 'info',
                    side: 'front',
                    enabled: true,
                    position: { x: 395, y: 205 },
                    properties: {
                        text: '{{DATE_NAISSANCE}}',
                        fontSize: 16,
                        fontFamily: 'Arial',
                        fontWeight: 'normal',
                        color: '#DC143C'  // Rouge
                    }
                },

                // 9. Label POLICY NUMBER:
                {
                    id: 'policy-label',
                    type: 'text',
                    subType: 'customLabel',
                    side: 'front',
                    enabled: true,
                    position: { x: 220, y: 245 },
                    properties: {
                        text: 'POLICY NUMBER:',
                        fontSize: 16,
                        fontFamily: 'Arial',
                        fontWeight: 'bold',
                        color: '#003087'
                    }
                },

                // 10. Policy Number
                {
                    id: 'policy-value',
                    type: 'text',
                    subType: 'info',
                    side: 'front',
                    enabled: true,
                    position: { x: 395, y: 245 },
                    properties: {
                        text: '{{NUMERO_POLICE}}',
                        fontSize: 16,
                        fontFamily: 'Arial',
                        fontWeight: 'normal',
                        color: '#000000'
                    }
                },

                // 11. Rectangle jaune/or pour signature
                {
                    id: 'signature-box',
                    type: 'shape',
                    subType: 'rectangle',
                    side: 'front',
                    enabled: true,
                    position: { x: 30, y: 315, width: 130, height: 80 },
                    properties: {
                        fillColor: '#DAA520',  // Or/Jaune
                        borderColor: '#B8860B',  // Or foncé
                        borderWidth: 1,
                        cornerRadius: 0
                    }
                },

                // 12. Image de signature manuscrite
                {
                    id: 'signature-image',
                    type: 'image',
                    subType: 'signature',
                    side: 'front',
                    enabled: true,
                    position: { x: 220, y: 320, width: 180, height: 60 },
                    properties: {
                        imageUrl: null,  // L'utilisateur peut uploader sa signature
                        opacity: 1.0
                    }
                },

                // 13. Texte "Authorised Signature"
                {
                    id: 'signature-text',
                    type: 'text',
                    subType: 'label',
                    side: 'front',
                    enabled: true,
                    position: { x: 260, y: 385 },
                    properties: {
                        text: 'Authorised Signature',
                        fontSize: 12,
                        fontFamily: 'Arial',
                        fontWeight: 'normal',
                        color: '#000000'
                    }
                },

                // 14. Bande verticale tricolore (DROITE)
                {
                    id: 'color-strip-blue',
                    type: 'shape',
                    subType: 'rectangle',
                    side: 'front',
                    enabled: true,
                    position: { x: 610, y: 290, width: 15, height: 36 },
                    properties: {
                        fillColor: '#003087',  // Bleu Activa
                        borderColor: 'transparent',
                        borderWidth: 0,
                        cornerRadius: 0
                    }
                },
                {
                    id: 'color-strip-green',
                    type: 'shape',
                    subType: 'rectangle',
                    side: 'front',
                    enabled: true,
                    position: { x: 610, y: 326, width: 15, height: 37 },
                    properties: {
                        fillColor: '#00A651',  // Vert Activa
                        borderColor: 'transparent',
                        borderWidth: 0,
                        cornerRadius: 0
                    }
                },
                {
                    id: 'color-strip-red',
                    type: 'shape',
                    subType: 'rectangle',
                    side: 'front',
                    enabled: true,
                    position: { x: 610, y: 363, width: 15, height: 37 },
                    properties: {
                        fillColor: '#DC143C',  // Rouge Activa
                        borderColor: 'transparent',
                        borderWidth: 0,
                        cornerRadius: 0
                    }
                },

                // 13. QR Code (si nécessaire - optionnel pour Activa)
                {
                    id: 'qrcode',
                    type: 'qrcode',
                    side: 'front',
                    enabled: false,
                    position: { x: 850, y: 450, width: 100, height: 100 },
                    properties: {
                        style: 'standard',
                        data: '{{CARD_DATA}}',
                        color: '#000000'
                    }
                },

                // Éléments de sécurité (désactivés par défaut)
                {
                    id: 'watermark',
                    type: 'security',
                    subType: 'watermark',
                    side: 'front',
                    enabled: false,
                    properties: {
                        text: 'SECURISE',
                        opacity: 0.1,
                        rotation: -45,
                        fontSize: 48,
                        color: '#000000'
                    }
                },
                {
                    id: 'microtext',
                    type: 'security',
                    subType: 'microtext',
                    side: 'front',
                    enabled: false,
                    position: { x: 50, y: 560 },
                    properties: {
                        footerText1: 'Document sécurisé',
                        footerText2: 'Ne peut être dupliqué',
                        fontSize: 8,
                        color: '#666666'
                    }
                },

                // ============ BACK SIDE ============

                // 14. Instructions texte
                {
                    id: 'back-instructions',
                    type: 'text',
                    subType: 'info',
                    side: 'back',
                    enabled: true,
                    position: { x: 50, y: 40 },
                    properties: {
                        text: 'If found, please deliver to the address below or call',
                        fontSize: 14,
                        fontFamily: 'Arial',
                        fontWeight: 'normal',
                        color: '#000000'
                    }
                },

                // 15. Numéros de téléphone
                {
                    id: 'back-phones',
                    type: 'text',
                    subType: 'contact',
                    side: 'back',
                    enabled: true,
                    position: { x: 120, y: 75 },
                    properties: {
                        text: '+231-886-655-914/+231-770-033-510',
                        fontSize: 16,
                        fontFamily: 'Arial',
                        fontWeight: 'bold',
                        color: '#000000'
                    }
                },

                // 16. Adresse
                {
                    id: 'back-address',
                    type: 'text',
                    subType: 'address',
                    side: 'back',
                    enabled: true,
                    position: { x: 150, y: 110 },
                    properties: {
                        text: '5th Street, Tubman Boulevard,\nOpposite Conex Gas Station, Sinkor\nMonrovia, Liberia',
                        fontSize: 16,
                        fontFamily: 'Arial',
                        fontWeight: 'bold',
                        color: '#000000',
                        lineHeight: 24
                    }
                },

                // 17. Website
                {
                    id: 'back-website',
                    type: 'text',
                    subType: 'url',
                    side: 'back',
                    enabled: true,
                    position: { x: 180, y: 190 },
                    properties: {
                        text: 'Website: www.group-activa.com/Liberia',
                        fontSize: 14,
                        fontFamily: 'Arial',
                        fontWeight: 'bold',
                        color: '#0000FF'
                    }
                },

                // 18. Code-barres
                {
                    id: 'barcode',
                    type: 'qrcode',
                    subType: 'barcode',
                    side: 'back',
                    enabled: true,
                    position: { x: 140, y: 240, width: 350, height: 120 },
                    properties: {
                        style: 'barcode',
                        data: '{{BARCODE_DATA}}',
                        color: '#000000',
                        displayValue: true,
                        text: 'SCI 00226'
                    }
                }
            ],

            // Schéma de couleurs - Activa Theme
            colorScheme: {
                primaryColor: '#003087',      // Bleu foncé Activa
                secondaryColor: '#00A651',    // Vert Activa
                accentColor: '#DC143C',       // Rouge Activa
                textColor: '#000000',         // Noir
                backgroundColor: '#FFFFFF',   // Blanc
                gradient: {
                    enabled: false,
                    startColor: '#003087',
                    endColor: '#00A651',
                    direction: 'TopToBottom'
                }
            },

            // Background images for front and back
            backgroundImage: {
                frontImageUrl: null,
                frontImageBase64: null,
                backImageUrl: null,
                backImageBase64: null,
                frontImageOpacity: 1.0,
                backImageOpacity: 1.0
            },

            // Logo configuration
            logoConfig: {
                logoUrl: null,
                logoBase64: null,
                useDefaultLogo: true
            },

            // Custom texts - Activa defaults
            customTexts: {
                organizationTitle: 'STREET CHILD INTERNATIONAL (S.C.I)',
                organizationSubtitle: 'MEDICAL INSURANCE ID CARD',
                securityMessage: '',
                contactMessage: 'If found, please deliver to the address below or call'
            },

            // État de l'éditeur
            selectedElement: null,
            draggedElement: null,
            isDragging: false,
            isResizing: false,
            resizeHandle: null, // 'nw', 'ne', 'sw', 'se', 'n', 's', 'e', 'w'
            resizeStartPos: null,
            resizeStartDimensions: null,
            isPreviewLoading: false,
            previewUrl: null,
            showPreviewModal: false,
            validationErrors: [],
            isDirty: false,
            showVariablesHelp: false, // Collapsible variables panel

            // Undo/Redo history
            history: [],
            historyIndex: -1,
            maxHistorySize: 50,

            // Template Gallery
            showTemplateGallery: false,

            // Clipboard for copy/paste
            clipboard: null,

            // Grouping system
            selectedElements: [], // Array of element IDs for multi-selection
            groups: {}, // Map of groupId -> { id, name, elementIds: [] }
            nextGroupId: 1,

            // Mode d'édition
            currentSide: 'front', // front ou back
            gridEnabled: true,
            snapToGrid: true,
            snapToGuides: true,
            gridSize: 10,
            showGuides: true,
            guides: {
                vertical: [],
                horizontal: []
            }
        };

        this.toast = React.createRef();
        this.canvasRef = React.createRef();
        this.previewCanvasRef = React.createRef();

        // Debounce timer for property updates
        this.propertyUpdateTimer = null;

        // Debounce timer for text input to prevent focus loss
        this.textInputDebounceTimer = null;
        this.tempTextValues = {}; // Store temporary text values by element ID
    }

    componentDidMount() {
        if (this.props.template) {
            this.loadTemplateParameters();
        }

        // Capture initial state for undo/redo
        this.pushHistory();

        this.renderCanvas();
        this.addCanvasEventListeners();
        // Add keyboard event listener for delete key
        document.addEventListener('keydown', this.handleKeyDown);
    }

    // ===== TEMPLATE DEFINITIONS =====
    getTemplateDefinitions = () => {
        return {
            rdc: {
                name: 'RDC - Activa Assurances',
                description: 'Template pour carte de santé RDC avec design Activa',
                thumbnail: null,
                elements: [
                    // ===== FRONT SIDE =====
                    // Activa Logo (top left)
                    {
                        id: 'logo-front',
                        type: 'logo',
                        subType: 'organizationLogo',
                        side: 'front',
                        enabled: true,
                        position: { x: 30, y: 30, width: 120, height: 80 },
                        properties: {
                            imageUrl: '',
                            placeholder: 'Activa Logo'
                        },
                        zIndex: 5
                    },
                    // Large blue diagonal shape (right side)
                    {
                        id: 'blue-diagonal-shape',
                        type: 'shape',
                        subType: 'rectangle',
                        side: 'front',
                        enabled: true,
                        position: { x: 320, y: 0, width: 500, height: 500 },
                        properties: {
                            fillColor: '#2E5C9A',
                            borderColor: 'transparent',
                            borderWidth: 0,
                            cornerRadius: 0
                        },
                        zIndex: 1
                    },
                    // White decorative curved shape
                    {
                        id: 'white-curve-front',
                        type: 'shape',
                        subType: 'ellipse',
                        side: 'front',
                        enabled: true,
                        position: { x: 600, y: 420, width: 380, height: 218 },
                        properties: {
                            fillColor: '#FFFFFF',
                            borderColor: 'transparent',
                            borderWidth: 0
                        },
                        zIndex: 2
                    },
                    // Green curved shape (bottom)
                    {
                        id: 'green-curve-front',
                        type: 'shape',
                        subType: 'ellipse',
                        side: 'front',
                        enabled: true,
                        position: { x: 250, y: 480, width: 500, height: 158 },
                        properties: {
                            fillColor: '#7AB436',
                            borderColor: 'transparent',
                            borderWidth: 0
                        },
                        zIndex: 3
                    },
                // Photo placeholder (oval)
                {
                    id: 'photo-member',
                    type: 'photo',
                    subType: 'memberPhoto',
                    side: 'front',
                    enabled: true,
                    position: { x: 45, y: 180, width: 210, height: 280 },
                    properties: {
                        imageUrl: '',
                        placeholder: 'Photo',
                        borderRadius: 15
                    },
                    zIndex: 4
                },
                // Title "CARTE DE SANTÉ"
                {
                    id: 'text-title',
                    type: 'text',
                    subType: 'title',
                    side: 'front',
                    enabled: true,
                    position: { x: 420, y: 30, width: 500, height: 50 },
                    properties: {
                        text: 'CARTE DE SANTÉ',
                        fontSize: 38,
                        fontFamily: 'Arial',
                        fontWeight: 'bold',
                        color: '#FFFFFF'
                    },
                    zIndex: 10
                },
                // Card number
                {
                    id: 'text-card-number',
                    type: 'text',
                    subType: 'cardNumber',
                    side: 'front',
                    enabled: true,
                    position: { x: 420, y: 90, width: 500, height: 40 },
                    properties: {
                        text: 'N° {{CARD_NUMBER}}',
                        fontSize: 32,
                        fontFamily: 'Arial',
                        fontWeight: 'normal',
                        color: '#FFFFFF'
                    },
                    zIndex: 10
                },
                // Nom (Last Name)
                {
                    id: 'text-nom',
                    type: 'text',
                    subType: 'name',
                    side: 'front',
                    enabled: true,
                    position: { x: 420, y: 145, width: 500, height: 30 },
                    properties: {
                        text: 'Nom : {{LAST_NAME}}',
                        fontSize: 20,
                        fontFamily: 'Arial',
                        fontWeight: 'normal',
                        color: '#FFFFFF'
                    },
                    zIndex: 10
                },
                // Prénom (First Name)
                {
                    id: 'text-prenom',
                    type: 'text',
                    subType: 'firstName',
                    side: 'front',
                    enabled: true,
                    position: { x: 420, y: 185, width: 500, height: 30 },
                    properties: {
                        text: 'Prénom : {{FIRST_NAME}}',
                        fontSize: 20,
                        fontFamily: 'Arial',
                        fontWeight: 'normal',
                        color: '#FFFFFF'
                    },
                    zIndex: 10
                },
                // Birth Date
                {
                    id: 'text-birthdate',
                    type: 'text',
                    subType: 'birthDate',
                    side: 'front',
                    enabled: true,
                    position: { x: 420, y: 225, width: 500, height: 30 },
                    properties: {
                        text: 'Né(e) le : {{BIRTH_DATE}}',
                        fontSize: 20,
                        fontFamily: 'Arial',
                        fontWeight: 'normal',
                        color: '#FFFFFF'
                    },
                    zIndex: 10
                },
                // Souscripteur (Subscriber)
                {
                    id: 'text-souscripteur',
                    type: 'text',
                    subType: 'subscriber',
                    side: 'front',
                    enabled: true,
                    position: { x: 420, y: 265, width: 500, height: 60 },
                    properties: {
                        text: 'Souscripteur : {{SUBSCRIBER}}',
                        fontSize: 18,
                        fontFamily: 'Arial',
                        fontWeight: 'normal',
                        color: '#FFFFFF'
                    },
                    zIndex: 10
                },
                // Date d'effet (Effective Date)
                {
                    id: 'text-effective-date',
                    type: 'text',
                    subType: 'effectiveDate',
                    side: 'front',
                    enabled: true,
                    position: { x: 420, y: 335, width: 500, height: 30 },
                    properties: {
                        text: "Date d'effet : {{EFFECTIVE_DATE}}",
                        fontSize: 20,
                        fontFamily: 'Arial',
                        fontWeight: 'normal',
                        color: '#FFFFFF'
                    },
                    zIndex: 10
                },

                // ===== BACK SIDE =====
                // Blue header bar
                {
                    id: 'blue-header-back',
                    type: 'shape',
                    subType: 'rectangle',
                    side: 'back',
                    enabled: true,
                    position: { x: 250, y: 60, width: 500, height: 80 },
                    properties: {
                        fillColor: '#2E5C9A',
                        borderColor: 'transparent',
                        borderWidth: 0,
                        cornerRadius: 0
                    },
                    zIndex: 1
                },
                // Header title
                {
                    id: 'text-back-header',
                    type: 'text',
                    subType: 'title',
                    side: 'back',
                    enabled: true,
                    position: { x: 250, y: 75, width: 500, height: 50 },
                    properties: {
                        text: 'PRISE EN CHARGE / HOSPITALISATION',
                        fontSize: 32,
                        fontFamily: 'Arial',
                        fontWeight: 'bold',
                        color: '#FFFFFF'
                    },
                    zIndex: 10
                },
                // Left column text
                {
                    id: 'text-left-column',
                    type: 'text',
                    subType: 'info',
                    side: 'back',
                    enabled: true,
                    position: { x: 30, y: 165, width: 470, height: 180 },
                    properties: {
                        text: "Cette carte Santé vous permet d'avoir Accès au réseau de soins tiers payant\n\nACTIVA ASSURANCES RDC\n\nEn cas de radiation du bénéficiaire, la carte doit être restituée à\n\nACTIVA ASSURANCES RDC",
                        fontSize: 14,
                        fontFamily: 'Arial',
                        fontWeight: 'normal',
                        color: '#2E5C9A'
                    },
                    zIndex: 10
                },
                // Right column text
                {
                    id: 'text-right-column',
                    type: 'text',
                    subType: 'info',
                    side: 'back',
                    enabled: true,
                    position: { x: 520, y: 165, width: 470, height: 180 },
                    properties: {
                        text: "Toute hospitalisation doit être déclarée dans les 24h à :\n\nACTIVA ASSURANCES RDC\n+243 80 85 90 038\nsante.rdc@group-activa.com",
                        fontSize: 14,
                        fontFamily: 'Arial',
                        fontWeight: 'normal',
                        color: '#2E5C9A'
                    },
                    zIndex: 10
                },
                // Activa Logo (bottom left)
                {
                    id: 'logo-activa-back',
                    type: 'logo',
                    subType: 'organizationLogo',
                    side: 'back',
                    enabled: true,
                    position: { x: 150, y: 400, width: 180, height: 120 },
                    properties: {
                        imageUrl: '',
                        placeholder: 'Activa Logo'
                    },
                    zIndex: 10
                },
                // Globus Logo (bottom right)
                {
                    id: 'logo-globus-back',
                    type: 'logo',
                    subType: 'partnerLogo',
                    side: 'back',
                    enabled: true,
                    position: { x: 680, y: 400, width: 180, height: 120 },
                    properties: {
                        imageUrl: '',
                        placeholder: 'Globus Logo'
                    },
                    zIndex: 10
                },
                // Blue decorative corner (top-left)
                {
                    id: 'corner-top-left',
                    type: 'shape',
                    subType: 'rectangle',
                    side: 'back',
                    enabled: true,
                    position: { x: 0, y: 0, width: 40, height: 40 },
                    properties: {
                        fillColor: '#2E5C9A',
                        borderColor: 'transparent',
                        borderWidth: 0,
                        cornerRadius: 0
                    },
                    zIndex: 5
                },
                // Blue decorative corner (bottom-right)
                {
                    id: 'corner-bottom-right',
                    type: 'shape',
                    subType: 'rectangle',
                    side: 'back',
                    enabled: true,
                    position: { x: 972, y: 598, width: 40, height: 40 },
                    properties: {
                        fillColor: '#2E5C9A',
                        borderColor: 'transparent',
                        borderWidth: 0,
                        cornerRadius: 0
                    },
                    zIndex: 5
                }
                ],
                colorScheme: {
                    primaryColor: '#2E5C9A',
                    secondaryColor: '#7AB436',
                    backgroundColor: '#FFFFFF',
                    textColor: '#000000',
                    gradient: {
                        enabled: false,
                        startColor: '#2E5C9A',
                        endColor: '#7AB436',
                        direction: 'TopToBottom'
                    }
                }
            },
            civ: {
                name: 'CIV - Activa Assurances',
                description: 'Template pour carte de santé CIV avec design Activa',
                thumbnail: null,
                elements: [
                    // ===== FRONT SIDE =====
                    // Blue curved header
                    {
                        id: 'header-curve',
                        type: 'shape',
                        subType: 'arc',
                        side: 'front',
                        enabled: true,
                        position: { x: 0, y: 0, width: 500, height: 150 },
                        properties: {
                            fillColor: '#2E5C9A',
                            borderColor: 'transparent',
                            borderWidth: 0,
                            arcHeight: 150,
                            arcDirection: 'down'
                        },
                        zIndex: 1
                    },
                    // Header title
                    {
                        id: 'header-title',
                        type: 'text',
                        subType: 'title',
                        side: 'front',
                        enabled: true,
                        position: { x: 200, y: 30, width: 500, height: 60 },
                        properties: {
                            text: 'CARTE SANTE ACTIVA',
                            fontSize: 42,
                            fontFamily: 'Arial',
                            fontWeight: 'bold',
                            color: '#FFFFFF'
                        },
                        zIndex: 10
                    },
                    // Photo placeholder
                    {
                        id: 'photo-member',
                        type: 'photo',
                        subType: 'memberPhoto',
                        side: 'front',
                        enabled: true,
                        position: { x: 30, y: 220, width: 260, height: 280 },
                        properties: {
                            imageUrl: '',
                            placeholder: 'Photo',
                            borderRadius: 0,
                            showBorder: true,
                            borderColor: '#2E5C9A',
                            borderWidth: 3
                        },
                        zIndex: 5
                    },
                    // QR Code
                    {
                        id: 'qrcode',
                        type: 'qrcode',
                        subType: 'qrCode',
                        side: 'front',
                        enabled: true,
                        position: { x: 80, y: 520, width: 140, height: 140 },
                        properties: {
                            data: '{{NUMERO_ASSURE}}',
                            errorCorrection: 'M'
                        },
                        zIndex: 5
                    },
                    // Bénéficiaire title
                    {
                        id: 'beneficiaire-title',
                        type: 'text',
                        subType: 'info',
                        side: 'front',
                        enabled: true,
                        position: { x: 310, y: 190, width: 400, height: 40 },
                        properties: {
                            text: 'Bénéficiaire',
                            fontSize: 32,
                            fontFamily: 'Arial',
                            fontWeight: 'bold',
                            color: '#2E5C9A'
                        },
                        zIndex: 10
                    },
                    // Bénéficiaire - Matricule
                    {
                        id: 'beneficiaire-matricule',
                        type: 'text',
                        subType: 'info',
                        side: 'front',
                        enabled: true,
                        position: { x: 310, y: 240, width: 500, height: 25 },
                        properties: {
                            text: 'Matricule                    : {{NUMERO_ASSURE}}',
                            fontSize: 16,
                            fontFamily: 'Arial',
                            fontWeight: 'normal',
                            color: '#000000'
                        },
                        zIndex: 10
                    },
                    // Bénéficiaire - Nom
                    {
                        id: 'beneficiaire-nom',
                        type: 'text',
                        subType: 'info',
                        side: 'front',
                        enabled: true,
                        position: { x: 310, y: 270, width: 500, height: 25 },
                        properties: {
                            text: 'Nom                           : {{NOM}}',
                            fontSize: 16,
                            fontFamily: 'Arial',
                            fontWeight: 'normal',
                            color: '#000000'
                        },
                        zIndex: 10
                    },
                    // Bénéficiaire - Prenoms
                    {
                        id: 'beneficiaire-prenoms',
                        type: 'text',
                        subType: 'info',
                        side: 'front',
                        enabled: true,
                        position: { x: 310, y: 300, width: 500, height: 25 },
                        properties: {
                            text: 'Prenoms                   : {{PRENOM}}',
                            fontSize: 16,
                            fontFamily: 'Arial',
                            fontWeight: 'normal',
                            color: '#000000'
                        },
                        zIndex: 10
                    },
                    // Bénéficiaire - Date de naissance
                    {
                        id: 'beneficiaire-dob',
                        type: 'text',
                        subType: 'info',
                        side: 'front',
                        enabled: true,
                        position: { x: 310, y: 330, width: 500, height: 25 },
                        properties: {
                            text: 'Date de naissance   : {{DATE_NAISSANCE}}',
                            fontSize: 16,
                            fontFamily: 'Arial',
                            fontWeight: 'normal',
                            color: '#000000'
                        },
                        zIndex: 10
                    },
                    // Assuré.e title
                    {
                        id: 'assure-title',
                        type: 'text',
                        subType: 'info',
                        side: 'front',
                        enabled: true,
                        position: { x: 310, y: 380, width: 400, height: 40 },
                        properties: {
                            text: 'Assuré.e',
                            fontSize: 32,
                            fontFamily: 'Arial',
                            fontWeight: 'bold',
                            color: '#2E5C9A'
                        },
                        zIndex: 10
                    },
                    // Assuré - Matricule
                    {
                        id: 'assure-matricule',
                        type: 'text',
                        subType: 'info',
                        side: 'front',
                        enabled: true,
                        position: { x: 310, y: 430, width: 500, height: 25 },
                        properties: {
                            text: 'Matricule                    : {{ASSURE_PRINCIPAL}}',
                            fontSize: 16,
                            fontFamily: 'Arial',
                            fontWeight: 'normal',
                            color: '#000000'
                        },
                        zIndex: 10
                    },
                    // Assuré - Nom et Prenoms
                    {
                        id: 'assure-nom',
                        type: 'text',
                        subType: 'info',
                        side: 'front',
                        enabled: true,
                        position: { x: 310, y: 460, width: 500, height: 25 },
                        properties: {
                            text: 'Nom et Prenoms       : {{nomComplet}}',
                            fontSize: 16,
                            fontFamily: 'Arial',
                            fontWeight: 'normal',
                            color: '#000000'
                        },
                        zIndex: 10
                    },
                    // Activa Logo (bottom right)
                    {
                        id: 'logo-bottom',
                        type: 'logo',
                        subType: 'organizationLogo',
                        side: 'front',
                        enabled: true,
                        position: { x: 770, y: 540, width: 200, height: 120 },
                        properties: {
                            imageUrl: '',
                            placeholder: 'Activa Logo'
                        },
                        zIndex: 10
                    }
                ],
                colorScheme: {
                    primaryColor: '#2E5C9A',
                    secondaryColor: '#FFFFFF',
                    backgroundColor: '#F5F5F5',
                    textColor: '#000000',
                    gradient: {
                        enabled: false,
                        startColor: '#2E5C9A',
                        endColor: '#FFFFFF',
                        direction: 'TopToBottom'
                    }
                }
            },
            civv2: {
                name: 'CIV V2 - Activa Assurances',
                description: 'Template pour carte de santé CIV version 2 avec design Activa',
                thumbnail: null,
                elements: [
                    // ===== FRONT SIDE =====
                    // Activa Logo (top left)
                    {
                        id: 'logo-top-left',
                        type: 'logo',
                        subType: 'organizationLogo',
                        side: 'front',
                        enabled: true,
                        position: { x: 30, y: 20, width: 160, height: 100 },
                        properties: {
                            imageUrl: '',
                            placeholder: 'Activa Logo'
                        },
                        zIndex: 10
                    },
                    // Contact info text
                    {
                        id: 'contact-tel',
                        type: 'text',
                        subType: 'info',
                        side: 'front',
                        enabled: true,
                        position: { x: 30, y: 125, width: 180, height: 20 },
                        properties: {
                            text: 'Tel : ...........................',
                            fontSize: 10,
                            fontFamily: 'Arial',
                            fontWeight: 'normal',
                            color: '#000000'
                        },
                        zIndex: 10
                    },
                    {
                        id: 'contact-mobile',
                        type: 'text',
                        subType: 'info',
                        side: 'front',
                        enabled: true,
                        position: { x: 30, y: 148, width: 180, height: 20 },
                        properties: {
                            text: 'Mobile : ..............................',
                            fontSize: 10,
                            fontFamily: 'Arial',
                            fontWeight: 'normal',
                            color: '#000000'
                        },
                        zIndex: 10
                    },
                    // Header title box
                    {
                        id: 'header-box',
                        type: 'shape',
                        subType: 'rectangle',
                        side: 'front',
                        enabled: true,
                        position: { x: 330, y: 20, width: 490, height: 70 },
                        properties: {
                            fillColor: 'transparent',
                            borderColor: '#2E5C9A',
                            borderWidth: 3,
                            cornerRadius: 0
                        },
                        zIndex: 5
                    },
                    {
                        id: 'header-title',
                        type: 'text',
                        subType: 'title',
                        side: 'front',
                        enabled: true,
                        position: { x: 340, y: 30, width: 470, height: 50 },
                        properties: {
                            text: 'CARTE ACTIVA SANTE',
                            fontSize: 28,
                            fontFamily: 'Arial',
                            fontWeight: 'bold',
                            color: '#2E5C9A',
                            textDecoration: 'underline'
                        },
                        zIndex: 10
                    },
                    // Bénéficiaire title
                    {
                        id: 'beneficiaire-title',
                        type: 'text',
                        subType: 'info',
                        side: 'front',
                        enabled: true,
                        position: { x: 330, y: 110, width: 400, height: 35 },
                        properties: {
                            text: 'Bénéficiaire',
                            fontSize: 28,
                            fontFamily: 'Arial',
                            fontWeight: 'bold',
                            color: '#2E5C9A'
                        },
                        zIndex: 10
                    },
                    // Bénéficiaire - Matricule
                    {
                        id: 'beneficiaire-matricule',
                        type: 'text',
                        subType: 'info',
                        side: 'front',
                        enabled: true,
                        position: { x: 330, y: 150, width: 500, height: 22 },
                        properties: {
                            text: 'Matricule                     : {{NUMERO_ASSURE}}',
                            fontSize: 14,
                            fontFamily: 'Arial',
                            fontWeight: 'normal',
                            color: '#000000'
                        },
                        zIndex: 10
                    },
                    // Bénéficiaire - Nom
                    {
                        id: 'beneficiaire-nom',
                        type: 'text',
                        subType: 'info',
                        side: 'front',
                        enabled: true,
                        position: { x: 330, y: 175, width: 500, height: 22 },
                        properties: {
                            text: 'Nom                            : {{NOM}}',
                            fontSize: 14,
                            fontFamily: 'Arial',
                            fontWeight: 'normal',
                            color: '#000000'
                        },
                        zIndex: 10
                    },
                    // Bénéficiaire - Prénoms
                    {
                        id: 'beneficiaire-prenoms',
                        type: 'text',
                        subType: 'info',
                        side: 'front',
                        enabled: true,
                        position: { x: 330, y: 200, width: 500, height: 22 },
                        properties: {
                            text: 'Prénoms                    : {{PRENOM}}',
                            fontSize: 14,
                            fontFamily: 'Arial',
                            fontWeight: 'normal',
                            color: '#000000'
                        },
                        zIndex: 10
                    },
                    // Bénéficiaire - Date de naissance
                    {
                        id: 'beneficiaire-dob',
                        type: 'text',
                        subType: 'info',
                        side: 'front',
                        enabled: true,
                        position: { x: 330, y: 225, width: 500, height: 22 },
                        properties: {
                            text: 'Date de naissance    : {{DATE_NAISSANCE}}',
                            fontSize: 14,
                            fontFamily: 'Arial',
                            fontWeight: 'normal',
                            color: '#000000'
                        },
                        zIndex: 10
                    },
                    // Assuré(e) title
                    {
                        id: 'assure-title',
                        type: 'text',
                        subType: 'info',
                        side: 'front',
                        enabled: true,
                        position: { x: 330, y: 270, width: 400, height: 35 },
                        properties: {
                            text: 'Assuré(e)',
                            fontSize: 28,
                            fontFamily: 'Arial',
                            fontWeight: 'bold',
                            color: '#2E5C9A'
                        },
                        zIndex: 10
                    },
                    // Assuré - Matricule
                    {
                        id: 'assure-matricule',
                        type: 'text',
                        subType: 'info',
                        side: 'front',
                        enabled: true,
                        position: { x: 330, y: 310, width: 500, height: 22 },
                        properties: {
                            text: 'Matricule                     : {{ASSURE_PRINCIPAL}}',
                            fontSize: 14,
                            fontFamily: 'Arial',
                            fontWeight: 'normal',
                            color: '#000000'
                        },
                        zIndex: 10
                    },
                    // Assuré - Nom et prénoms
                    {
                        id: 'assure-nom',
                        type: 'text',
                        subType: 'info',
                        side: 'front',
                        enabled: true,
                        position: { x: 330, y: 335, width: 500, height: 22 },
                        properties: {
                            text: 'Nom et prénoms        : {{nomComplet}}',
                            fontSize: 14,
                            fontFamily: 'Arial',
                            fontWeight: 'normal',
                            color: '#000000'
                        },
                        zIndex: 10
                    },
                    // Photo placeholder with border
                    {
                        id: 'photo-border',
                        type: 'shape',
                        subType: 'rectangle',
                        side: 'front',
                        enabled: true,
                        position: { x: 35, y: 200, width: 250, height: 210 },
                        properties: {
                            fillColor: 'transparent',
                            borderColor: '#000000',
                            borderWidth: 2,
                            cornerRadius: 0
                        },
                        zIndex: 5
                    },
                    {
                        id: 'photo-member',
                        type: 'photo',
                        subType: 'memberPhoto',
                        side: 'front',
                        enabled: true,
                        position: { x: 37, y: 202, width: 246, height: 206 },
                        properties: {
                            imageUrl: '',
                            placeholder: 'PHOTO',
                            borderRadius: 0
                        },
                        zIndex: 6
                    },
                    // QR Code box
                    {
                        id: 'qrcode-box',
                        type: 'shape',
                        subType: 'rectangle',
                        side: 'front',
                        enabled: true,
                        position: { x: 582, y: 435, width: 120, height: 80 },
                        properties: {
                            fillColor: 'transparent',
                            borderColor: '#000000',
                            borderWidth: 2,
                            cornerRadius: 0
                        },
                        zIndex: 5
                    },
                    {
                        id: 'qrcode',
                        type: 'qrcode',
                        subType: 'qrCode',
                        side: 'front',
                        enabled: true,
                        position: { x: 592, y: 445, width: 100, height: 60 },
                        properties: {
                            data: '{{NUMERO_ASSURE}}',
                            errorCorrection: 'M'
                        },
                        zIndex: 6
                    },
                    {
                        id: 'qrcode-label',
                        type: 'text',
                        subType: 'info',
                        side: 'front',
                        enabled: true,
                        position: { x: 600, y: 470, width: 80, height: 20 },
                        properties: {
                            text: 'CODE QR',
                            fontSize: 11,
                            fontFamily: 'Arial',
                            fontWeight: 'bold',
                            color: '#FF0000'
                        },
                        zIndex: 10
                    },
                    // Small Activa Logo (bottom right)
                    {
                        id: 'logo-bottom',
                        type: 'logo',
                        subType: 'organizationLogo',
                        side: 'front',
                        enabled: true,
                        position: { x: 785, y: 460, width: 55, height: 55 },
                        properties: {
                            imageUrl: '',
                            placeholder: 'A'
                        },
                        zIndex: 10
                    }
                ],
                colorScheme: {
                    primaryColor: '#2E5C9A',
                    secondaryColor: '#FFFFFF',
                    backgroundColor: '#FFFFFF',
                    textColor: '#000000',
                    gradient: {
                        enabled: false,
                        startColor: '#2E5C9A',
                        endColor: '#FFFFFF',
                        direction: 'TopToBottom'
                    }
                }
            },
            blank: {
                name: 'Vierge',
                description: 'Template vide pour créer un design personnalisé',
                thumbnail: null,
                elements: [],
                colorScheme: {
                    primaryColor: '#667EEA',
                    secondaryColor: '#764BA2',
                    backgroundColor: '#FFFFFF',
                    textColor: '#000000',
                    gradient: {
                        enabled: false,
                        startColor: '#667EEA',
                        endColor: '#764BA2',
                        direction: 'TopToBottom'
                    }
                }
            }
        };
    };

    loadTemplate = (templateKey) => {
        const templates = this.getTemplateDefinitions();
        const template = templates[templateKey];

        if (!template) {
            this.showToast('error', 'Erreur', 'Template non trouvé');
            return;
        }

        // Load the template
        this.setState({
            elements: template.elements,
            colorScheme: template.colorScheme,
            isDirty: true,
            showTemplateGallery: false
        }, () => {
            this.pushHistory();
            this.renderCanvas();
            this.showToast('success', 'Template chargé', `${template.name} chargé avec succès`);
        });
    };

    componentDidUpdate(prevProps, prevState) {
        // Load template parameters when template prop changes
        if (prevProps.template !== this.props.template && this.props.template) {

            this.loadTemplateParameters();
        }

        if (prevState.elements !== this.state.elements ||
            prevState.colorScheme !== this.state.colorScheme ||
            prevState.gridEnabled !== this.state.gridEnabled ||
            prevState.currentSide !== this.state.currentSide ||
            prevState.selectedElement !== this.state.selectedElement) {
            this.renderCanvas();
            if (prevState.elements !== this.state.elements ||
                prevState.colorScheme !== this.state.colorScheme) {
                this.setState({ isDirty: true });
            }
        }
    }

    showToast = (severity, summary, detail) => {
        this.toast.current.show({ severity, summary, detail });
    };

    // ===== CHARGEMENT ET SAUVEGARDE =====

    loadTemplateParameters = async () => {
        try {
            const { template } = this.props;
            if (!template) return;

            console.log('=== LOADING TEMPLATE ===');
            console.log('Template ID:', template.id);
            console.log('Template object:', template);

            // Parse configuration from backend (check both configuration and layoutConfig)
            const configString = template.configuration || template.layoutConfig;
            console.log('Config string length:', configString?.length || 0);

            if (configString) {
                try {
                    const config = JSON.parse(configString);

                    console.log('Parsed config:', config);
                    console.log('Elements in config:', config.Elements?.length || 0);
                    console.log('Background images in config:', {
                        front: config.BackgroundImage?.FrontImageBase64 ? 'YES' : 'NO',
                        back: config.BackgroundImage?.BackImageBase64 ? 'YES' : 'NO'
                    });

                    // Check if it's the new TemplateParameters format (Pascal case)
                    if (config.ColorScheme || config.Layout || config.Typography) {
                        // New TemplateParameters format - pass directly to mapper
                        this.mapParametersToElements(config);
                    }
                    // Check if it's a combined config with layout and colors (old format)
                    else if (config.layout && config.colors) {
                        // Old combined config format - map to new structure
                        const templateParameters = {
                            ColorScheme: config.colors,
                            Layout: config.layout,
                            Typography: config.typography,
                            Security: config.security
                        };
                        this.mapParametersToElements(templateParameters);
                    }
                    // Legacy format - camelCase layout params
                    else {
                        this.mapParametersToElements(config);
                    }
                } catch (error) {
                    console.error('Error parsing layoutConfig:', error);
                }
            }

            // Parse separate colorConfig if exists (legacy support)
            if (template.colorConfig) {
                try {
                    const colorScheme = JSON.parse(template.colorConfig);

                    this.setState({ colorScheme });
                } catch (error) {
                    console.error('Error parsing colorConfig:', error);
                }
            }

            // Legacy support: If template has parameters directly, use them
            if (template.parameters) {

                this.mapParametersToElements(template.parameters);

                // Also load color scheme if available
                if (template.colorScheme) {
                    this.setState({ colorScheme: template.colorScheme });
                }
                return;
            }

            // Otherwise, try to fetch from API if template has an ID
            if (template.id && !template.layoutConfig) {
                const response = await fetch(
                    `${process.env.REACT_APP_BASEURL_CLIENT_BACKEND}/api/card/templates/${template.id}/default-parameters`,
                    { method: 'GET', headers: { 'Content-Type': 'application/json' } }
                );

                if (response.ok) {
                    const parameters = await response.json();
                    this.mapParametersToElements(parameters);
                }
            }
        } catch (error) {
            console.error('Erreur lors du chargement des paramètres:', error);
        }
    };

    mapParametersToElements = (parameters) => {
        // Map TemplateParameters (Pascal case) from backend to frontend elements
        let { elements } = this.state;

        // PRIORITY 1: Load complete Elements array if present (new format)
        if (parameters.Elements && Array.isArray(parameters.Elements) && parameters.Elements.length > 0) {
            console.log('[LOAD TEMPLATE] Loading Elements array from configuration:', parameters.Elements.length, 'elements');

            // Convert backend Elements (Pascal case) to frontend format (camelCase)
            elements = parameters.Elements.map(el => {
                // Convert Type from Pascal case to camelCase, preserving multi-word types
                let type = el.Type || 'text';
                // Convert first letter to lowercase but preserve the rest (e.g., FormField -> formField)
                if (type) {
                    type = type.charAt(0).toLowerCase() + type.slice(1);
                }

                // Clone properties
                const properties = { ...el.Properties } || {};

                // FIX: If this is a Text element that was saved as formField, restore the formField type
                if (type === 'text' && properties.isFormField) {
                    type = 'formField';
                    // Clean up the marker property
                    delete properties.isFormField;
                }

                return {
                    id: el.Id || `element-${Date.now()}-${Math.random()}`,
                    type: type,
                    subType: el.SubType || null,
                    side: el.Side?.toLowerCase() || 'front',
                    enabled: el.Enabled !== false,
                    position: {
                        x: el.Position?.X || 0,
                        y: el.Position?.Y || 0,
                        width: el.Position?.Width || 100,
                        height: el.Position?.Height || 50
                    },
                    properties: properties,
                    zIndex: el.ZIndex || 0
                };
            });

            console.log('[LOAD TEMPLATE] Converted elements:', elements);
        }

        // Extract color scheme from TemplateParameters
        let colorScheme = this.state.colorScheme;
        if (parameters.ColorScheme) {
            colorScheme = {
                primaryColor: parameters.ColorScheme.PrimaryColor || '#3B82F6',
                secondaryColor: parameters.ColorScheme.SecondaryColor || '#10B981',
                accentColor: parameters.ColorScheme.AccentColor || '#F59E0B',
                textColor: parameters.ColorScheme.TextColor || '#1F2937',
                backgroundColor: parameters.ColorScheme.BackgroundColor || '#FFFFFF',
                gradient: parameters.ColorScheme.Gradient ? {
                    enabled: parameters.ColorScheme.Gradient.Enabled || false,
                    startColor: parameters.ColorScheme.Gradient.StartColor || '#667EEA',
                    endColor: parameters.ColorScheme.Gradient.EndColor || '#764BA2',
                    direction: parameters.ColorScheme.Gradient.Direction || 'TopToBottom'
                } : {
                    enabled: false,
                    startColor: '#667EEA',
                    endColor: '#764BA2',
                    direction: 'TopToBottom'
                }
            };
        }

        // Load background images if present
        let backgroundImage = this.state.backgroundImage;
        if (parameters.BackgroundImage) {
            backgroundImage = {
                frontImageUrl: parameters.BackgroundImage.FrontImageBase64
                    ? `data:image/png;base64,${parameters.BackgroundImage.FrontImageBase64}`
                    : null,
                frontImageBase64: parameters.BackgroundImage.FrontImageBase64 || null,
                backImageUrl: parameters.BackgroundImage.BackImageBase64
                    ? `data:image/png;base64,${parameters.BackgroundImage.BackImageBase64}`
                    : null,
                backImageBase64: parameters.BackgroundImage.BackImageBase64 || null,
                frontImageOpacity: parameters.BackgroundImage.FrontImageOpacity || 1.0,
                backImageOpacity: parameters.BackgroundImage.BackImageOpacity || 1.0
            };
        }

        // PRIORITY 2: If we loaded Elements array, skip the old format mapping
        let updatedElements;
        if (parameters.Elements && Array.isArray(parameters.Elements) && parameters.Elements.length > 0) {
            console.log('[LOAD TEMPLATE] Skipping old format mapping - using Elements array');
            updatedElements = elements; // Already converted above
        } else {
            // Legacy format: map old TemplateParameters to existing elements
            console.log('[LOAD TEMPLATE] Using legacy format mapping');
            updatedElements = elements.map(element => {
                switch (element.type) {
                    case 'logo':
                        if (parameters.Layout?.LogoPosition) {
                        element.position = {
                            x: parameters.Layout.LogoPosition.X,
                            y: parameters.Layout.LogoPosition.Y,
                            width: parameters.Layout.LogoPosition.Width,
                            height: parameters.Layout.LogoPosition.Height
                        };
                    }
                    break;
                case 'photo':
                    if (parameters.Layout?.PhotoPosition) {
                        element.position = {
                            x: parameters.Layout.PhotoPosition.X,
                            y: parameters.Layout.PhotoPosition.Y,
                            width: parameters.Layout.PhotoPosition.Width,
                            height: parameters.Layout.PhotoPosition.Height
                        };
                        element.properties.shape = parameters.Layout.PhotoPosition.Shape || 'rectangle';
                        element.properties.borderColor = parameters.Layout.PhotoPosition.BorderColor;
                        element.properties.borderWidth = parameters.Layout.PhotoPosition.BorderWidth;
                    }
                    break;
                case 'qrcode':
                    if (parameters.Layout?.QRCodePosition) {
                        element.position = {
                            x: parameters.Layout.QRCodePosition.X,
                            y: parameters.Layout.QRCodePosition.Y,
                            width: parameters.Layout.QRCodePosition.Width,
                            height: parameters.Layout.QRCodePosition.Height
                        };
                    }
                    break;
                case 'text':
                    if (element.subType === 'title' && parameters.Layout?.TextPositions?.Title) {
                        element.position.x = parameters.Layout.TextPositions.Title.X;
                        element.position.y = parameters.Layout.TextPositions.Title.Y;
                        if (parameters.Typography?.TitleFont) {
                            element.properties.fontFamily = parameters.Typography.TitleFont.FontFamily;
                            element.properties.fontSize = parameters.Typography.TitleFont.FontSize;
                            element.properties.fontWeight = parameters.Typography.TitleFont.IsBold ? 'bold' : 'normal';
                        }
                    }
                    if (element.subType === 'name' && parameters.Layout?.TextPositions?.Name) {
                        element.position.x = parameters.Layout.TextPositions.Name.X;
                        element.position.y = parameters.Layout.TextPositions.Name.Y;
                        if (parameters.Typography?.NameFont) {
                            element.properties.fontFamily = parameters.Typography.NameFont.FontFamily;
                            element.properties.fontSize = parameters.Typography.NameFont.FontSize;
                            element.properties.fontWeight = parameters.Typography.NameFont.IsBold ? 'bold' : 'normal';
                        }
                    }
                    if (element.subType === 'info' && parameters.Layout?.TextPositions?.Info) {
                        element.position.x = parameters.Layout.TextPositions.Info.X;
                        element.position.y = parameters.Layout.TextPositions.Info.Y;
                        if (parameters.Typography?.InfoFont) {
                            element.properties.fontFamily = parameters.Typography.InfoFont.FontFamily;
                            element.properties.fontSize = parameters.Typography.InfoFont.FontSize;
                            element.properties.fontWeight = parameters.Typography.InfoFont.IsBold ? 'bold' : 'normal';
                        }
                    }
                    break;
                case 'security':
                    if (element.subType === 'watermark' && parameters.Security?.Watermark) {
                        element.properties.text = parameters.Security.Watermark.Text;
                        element.properties.opacity = parameters.Security.Watermark.Opacity;
                        element.properties.rotation = parameters.Security.Watermark.Rotation;
                        element.properties.fontSize = parameters.Security.Watermark.FontSize;
                        element.properties.color = parameters.Security.Watermark.Color;
                    }
                    break;
                    default:
                        break;
                }
                return element;
            });
        }

        console.log('[LOAD TEMPLATE] Final elements to set:', updatedElements);

        // FIX: Extract logo config from logo elements
        let logoConfig = this.state.logoConfig;
        const logoElement = updatedElements.find(el => el.type === 'logo');
        if (logoElement && logoElement.properties) {
            logoConfig = {
                logoBase64: logoElement.properties.logoBase64 || null,
                logoUrl: logoElement.properties.logoUrl || null,
                useDefaultLogo: logoElement.properties.useDefaultLogo !== false
            };
            console.log('[LOAD TEMPLATE] Logo config restored:', logoConfig);
        }

        this.setState({
            elements: updatedElements,
            colorScheme: colorScheme,
            backgroundImage: backgroundImage,
            logoConfig: logoConfig
        }, () => {
            console.log('[LOAD TEMPLATE] State updated, rendering canvas');
            this.renderCanvas();
        });
    };

    saveTemplate = async () => {
        try {
            const { templateName } = this.state;

            // Valider avant de sauvegarder
            if (!this.validateTemplate()) {
                this.showToast('error', 'Erreur', 'Veuillez corriger les erreurs de validation');
                return;
            }

            // Build TemplateParameters matching ParameterizableTemplateService structure
            const templateParameters = this.buildTemplateParameters();

            // DEBUG: Log what we're saving
            console.log('=== SAVING TEMPLATE ===');
            console.log('Elements count:', templateParameters.Elements?.length || 0);
            console.log('Background images:', {
                front: templateParameters.BackgroundImage?.FrontImageBase64 ? 'YES' : 'NO',
                back: templateParameters.BackgroundImage?.BackImageBase64 ? 'YES' : 'NO'
            });
            console.log('Color scheme:', templateParameters.ColorScheme);
            console.log('Full template parameters:', JSON.parse(JSON.stringify(templateParameters)));

            // Convert to JSON string for backend storage
            const configuration = JSON.stringify(templateParameters);

            const templateData = {
                // templateName: templateName,
                configuration: configuration, // Backend maps this to LayoutConfig
                designPath: null, // Logo is already in configuration
                isActive: true
            };




            // Créer ou mettre à jour le template
            const result = this.props.template?.id
                ? await CarteV2Service.updateTemplate(this.props.template.id, templateData)
                : await CarteV2Service.createTemplate(templateData);

            console.log('=== SAVE RESULT ===', result);

            this.showToast('success', 'Succès', 'Template sauvegardé avec succès');
            this.setState({ isDirty: false });

            // Don't redirect - just notify the parent if needed
            // if (this.props.onSave) {
            //     this.props.onSave(result);
            // }
        } catch (error) {
            console.error('Erreur lors de la sauvegarde:', error);
            this.showToast('error', 'Erreur', 'Erreur lors de la sauvegarde du template');
        }
    };

    // ===== BACKGROUND IMAGE UPLOAD =====

    handleBackgroundImageUpload = async (e, side) => {
        const file = e.target.files[0];
        if (!file) return;

        // Validate file type
        if (!file.type.match('image/(png|jpeg|jpg)')) {
            this.showToast('error', 'Erreur', 'Format d\'image non supporté. Utilisez PNG ou JPEG.');
            return;
        }

        // Validate file size (max 5MB)
        if (file.size > 5 * 1024 * 1024) {
            this.showToast('error', 'Erreur', 'L\'image est trop volumineuse (max 5MB)');
            return;
        }

        const reader = new FileReader();
        reader.onload = (event) => {
            const base64String = event.target.result;

            // Create image object to get dimensions
            const img = new Image();
            img.onload = () => {
                console.log(`Background image loaded: ${img.width}x${img.height}`);

                this.setState(prevState => ({
                    backgroundImage: {
                        ...prevState.backgroundImage,
                        [side === 'front' ? 'frontImageUrl' : 'backImageUrl']: base64String,
                        [side === 'front' ? 'frontImageBase64' : 'backImageBase64']: base64String.split(',')[1] // Remove data:image prefix
                    },
                    isDirty: true
                }), () => {
                    this.pushHistory();
                    this.renderCanvas();
                    this.showToast('success', 'Succès', `Image de fond ${side === 'front' ? 'avant' : 'arrière'} chargée`);
                });
            };
            img.src = base64String;
        };
        reader.readAsDataURL(file);
    };

    removeBackgroundImage = (side) => {
        this.setState(prevState => ({
            backgroundImage: {
                ...prevState.backgroundImage,
                [side === 'front' ? 'frontImageUrl' : 'backImageUrl']: null,
                [side === 'front' ? 'frontImageBase64' : 'backImageBase64']: null
            },
            isDirty: true
        }), () => {
            this.pushHistory();
            this.renderCanvas();
        });
    };

    updateBackgroundOpacity = (side, opacity) => {
        this.setState(prevState => ({
            backgroundImage: {
                ...prevState.backgroundImage,
                [side === 'front' ? 'frontImageOpacity' : 'backImageOpacity']: opacity
            }
        }), () => {
            this.renderCanvas();
        });
    };

    // ===== UNDO/REDO SYSTEM =====

    pushHistory = () => {
        const { elements, colorScheme, backgroundImage, groups, history, historyIndex, maxHistorySize } = this.state;

        // Create a snapshot of current state
        const snapshot = {
            elements: JSON.parse(JSON.stringify(elements)),
            colorScheme: JSON.parse(JSON.stringify(colorScheme)),
            backgroundImage: JSON.parse(JSON.stringify(backgroundImage)),
            groups: JSON.parse(JSON.stringify(groups)),
            timestamp: Date.now()
        };

        // Remove any future history if we're not at the end
        const newHistory = history.slice(0, historyIndex + 1);

        // Add new snapshot
        newHistory.push(snapshot);

        // Limit history size
        if (newHistory.length > maxHistorySize) {
            newHistory.shift();
        }

        this.setState({
            history: newHistory,
            historyIndex: newHistory.length - 1
        });
    };

    undo = () => {
        const { history, historyIndex } = this.state;

        if (historyIndex > 0) {
            const previousState = history[historyIndex - 1];
            this.setState({
                elements: JSON.parse(JSON.stringify(previousState.elements)),
                colorScheme: JSON.parse(JSON.stringify(previousState.colorScheme)),
                backgroundImage: JSON.parse(JSON.stringify(previousState.backgroundImage)),
                groups: previousState.groups ? JSON.parse(JSON.stringify(previousState.groups)) : {},
                historyIndex: historyIndex - 1,
                isDirty: true
            }, () => {
                this.renderCanvas();
                this.showToast('info', 'Info', 'Action annulée');
            });
        }
    };

    redo = () => {
        const { history, historyIndex } = this.state;

        if (historyIndex < history.length - 1) {
            const nextState = history[historyIndex + 1];
            this.setState({
                elements: JSON.parse(JSON.stringify(nextState.elements)),
                colorScheme: JSON.parse(JSON.stringify(nextState.colorScheme)),
                backgroundImage: JSON.parse(JSON.stringify(nextState.backgroundImage)),
                groups: nextState.groups ? JSON.parse(JSON.stringify(nextState.groups)) : {},
                historyIndex: historyIndex + 1,
                isDirty: true
            }, () => {
                this.renderCanvas();
                this.showToast('info', 'Info', 'Action rétablie');
            });
        }
    };

    // ===== COPY/PASTE SYSTEM =====

    copyElement = () => {
        const { selectedElement, elements } = this.state;
        if (!selectedElement) {
            this.showToast('warn', 'Attention', 'Aucun élément sélectionné');
            return;
        }

        const element = elements.find(el => el.id === selectedElement.id);
        if (element) {
            this.setState({
                clipboard: JSON.parse(JSON.stringify(element))
            });
            this.showToast('success', 'Succès', 'Élément copié');
        }
    };

    pasteElement = () => {
        const { clipboard, elements, currentSide } = this.state;
        if (!clipboard) {
            this.showToast('warn', 'Attention', 'Presse-papiers vide');
            return;
        }

        // Create a new element with offset position
        const newElement = JSON.parse(JSON.stringify(clipboard));
        newElement.id = `${newElement.type}-${Date.now()}`;
        newElement.side = currentSide;
        if (newElement.position) {
            newElement.position.x += 20;
            newElement.position.y += 20;
        }

        this.setState(prevState => ({
            elements: [...prevState.elements, newElement],
            selectedElement: newElement,
            isDirty: true
        }), () => {
            this.pushHistory();
            this.renderCanvas();
            this.showToast('success', 'Succès', 'Élément collé');
        });
    };

    duplicateElement = () => {
        this.copyElement();
        this.pasteElement();
    };

    // ===== ELEMENT GROUPING =====

    toggleElementSelection = (elementId, isCtrlPressed) => {
        const { selectedElements } = this.state;

        if (isCtrlPressed) {
            // Multi-select mode
            if (selectedElements.includes(elementId)) {
                // Deselect
                this.setState({
                    selectedElements: selectedElements.filter(id => id !== elementId)
                });
            } else {
                // Add to selection
                this.setState({
                    selectedElements: [...selectedElements, elementId]
                });
            }
        } else {
            // Single select mode
            this.setState({
                selectedElements: [elementId],
                selectedElement: this.state.elements.find(el => el.id === elementId)
            });
        }
    };

    selectMultipleElements = (elementIds) => {
        this.setState({
            selectedElements: elementIds,
            selectedElement: null // Clear single selection when multi-selecting
        });
    };

    clearSelection = () => {
        this.setState({
            selectedElements: [],
            selectedElement: null
        });
    };

    createGroup = () => {
        const { selectedElements, groups, nextGroupId, elements } = this.state;

        if (selectedElements.length < 2) {
            this.showToast('warn', 'Attention', 'Sélectionnez au moins 2 éléments pour créer un groupe');
            return;
        }

        // Check if any selected elements are already in a group
        const existingGroups = Object.values(groups).filter(group =>
            group.elementIds.some(id => selectedElements.includes(id))
        );

        if (existingGroups.length > 0) {
            this.showToast('warn', 'Attention', 'Certains éléments sont déjà groupés');
            return;
        }

        const groupId = `group-${nextGroupId}`;
        const newGroup = {
            id: groupId,
            name: `Groupe ${nextGroupId}`,
            elementIds: [...selectedElements]
        };

        // Add group property to elements
        const updatedElements = elements.map(el => {
            if (selectedElements.includes(el.id)) {
                return { ...el, groupId: groupId };
            }
            return el;
        });

        this.setState({
            groups: {
                ...groups,
                [groupId]: newGroup
            },
            elements: updatedElements,
            nextGroupId: nextGroupId + 1,
            isDirty: true
        }, () => {
            this.pushHistory();
            this.renderCanvas();
            this.showToast('success', 'Succès', `Groupe créé avec ${selectedElements.length} éléments`);
        });
    };

    ungroupElements = (groupId) => {
        const { groups, elements } = this.state;

        if (!groupId) {
            // Try to find group from selected elements
            const { selectedElements } = this.state;
            if (selectedElements.length === 0) {
                this.showToast('warn', 'Attention', 'Aucun groupe sélectionné');
                return;
            }

            // Find group containing first selected element
            const element = elements.find(el => el.id === selectedElements[0]);
            groupId = element?.groupId;

            if (!groupId) {
                this.showToast('warn', 'Attention', 'Les éléments sélectionnés ne font pas partie d\'un groupe');
                return;
            }
        }

        const group = groups[groupId];
        if (!group) return;

        // Remove group property from elements
        const updatedElements = elements.map(el => {
            if (el.groupId === groupId) {
                const { groupId: _, ...elementWithoutGroup } = el;
                return elementWithoutGroup;
            }
            return el;
        });

        // Remove group from groups map
        const { [groupId]: removedGroup, ...remainingGroups } = groups;

        this.setState({
            groups: remainingGroups,
            elements: updatedElements,
            isDirty: true
        }, () => {
            this.pushHistory();
            this.renderCanvas();
            this.showToast('success', 'Succès', 'Groupe dissout');
        });
    };

    selectGroup = (groupId) => {
        const { groups } = this.state;
        const group = groups[groupId];

        if (group) {
            this.setState({
                selectedElements: [...group.elementIds],
                selectedElement: null
            }, () => {
                this.renderCanvas();
            });
        }
    };

    getElementGroup = (elementId) => {
        const { groups } = this.state;
        return Object.values(groups).find(group => group.elementIds.includes(elementId));
    };

    getSelectedElementIds = () => {
        const { selectedElements, selectedElement } = this.state;

        // If multi-selection mode
        if (selectedElements.length > 0) {
            return selectedElements;
        }

        // If single selection mode
        if (selectedElement) {
            // Check if element is part of a group
            const group = this.getElementGroup(selectedElement.id);
            if (group) {
                return group.elementIds;
            }
            return [selectedElement.id];
        }

        return [];
    };

    // ===== Z-INDEX MANAGEMENT =====

    moveElementToFront = (elementId) => {
        const { elements } = this.state;
        const maxZ = Math.max(...elements.map(e => e.zIndex || 0), 0);

        this.updateElementProperty(elementId, 'zIndex', maxZ + 1);
        this.pushHistory();
        this.renderCanvas();
        this.showToast('success', 'Succès', 'Élément au premier plan');
    };

    moveElementToBack = (elementId) => {
        const { elements } = this.state;

        // Set element to 0
        const updatedElements = elements.map(el => {
            if (el.id === elementId) {
                return { ...el, zIndex: 0 };
            }
            // Increment others
            if ((el.zIndex || 0) >= 0) {
                return { ...el, zIndex: (el.zIndex || 0) + 1 };
            }
            return el;
        });

        this.setState({ elements: updatedElements, isDirty: true }, () => {
            this.pushHistory();
            this.renderCanvas();
            this.showToast('success', 'Succès', 'Élément à l\'arrière-plan');
        });
    };

    moveElementUp = (elementId) => {
        const { elements } = this.state;
        const element = elements.find(el => el.id === elementId);
        if (!element) return;

        const currentZ = element.zIndex || 0;
        this.updateElementProperty(elementId, 'zIndex', currentZ + 1);
        this.pushHistory();
        this.renderCanvas();
    };

    moveElementDown = (elementId) => {
        const { elements } = this.state;
        const element = elements.find(el => el.id === elementId);
        if (!element) return;

        const currentZ = element.zIndex || 0;
        if (currentZ > 0) {
            this.updateElementProperty(elementId, 'zIndex', currentZ - 1);
            this.pushHistory();
            this.renderCanvas();
        }
    };

    // ===== IMPORT TEMPLATE FROM IMAGE =====

    handleImportTemplateImage = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (!file.type.match('image/(png|jpeg|jpg)')) {
            this.showToast('error', 'Erreur', 'Format d\'image non supporté');
            return;
        }

        const reader = new FileReader();
        reader.onload = async (event) => {
            const base64String = event.target.result;

            // Ask user which side to import to
            const side = this.state.currentSide;

            // Set as background
            this.setState(prevState => ({
                backgroundImage: {
                    ...prevState.backgroundImage,
                    [side === 'front' ? 'frontImageUrl' : 'backImageUrl']: base64String,
                    [side === 'front' ? 'frontImageBase64' : 'backImageBase64']: base64String.split(',')[1]
                },
                isDirty: true
            }), () => {
                this.pushHistory();
                this.renderCanvas();
                this.showToast('success', 'Succès',
                    `Template importé comme fond ${side === 'front' ? 'avant' : 'arrière'}. Vous pouvez maintenant ajouter des éléments par-dessus.`);
            });
        };
        reader.readAsDataURL(file);
    };

    // ===== ALIGNMENT HELPERS =====

    alignElements = (alignment) => {
        const { selectedElement, elements } = this.state;
        if (!selectedElement || !selectedElement.position) return;

        const element = elements.find(el => el.id === selectedElement.id);
        if (!element || !element.position) return;

        const updatedPosition = { ...element.position };
        const CARD_WIDTH = 1012;
        const CARD_HEIGHT = 638;

        switch (alignment) {
            case 'left':
                updatedPosition.x = 0;
                break;
            case 'center-h':
                updatedPosition.x = (CARD_WIDTH - (element.position.width || 100)) / 2;
                break;
            case 'right':
                updatedPosition.x = CARD_WIDTH - (element.position.width || 100);
                break;
            case 'top':
                updatedPosition.y = 0;
                break;
            case 'center-v':
                updatedPosition.y = (CARD_HEIGHT - (element.position.height || 100)) / 2;
                break;
            case 'bottom':
                updatedPosition.y = CARD_HEIGHT - (element.position.height || 100);
                break;
            default:
                break;
        }

        this.setState(prevState => ({
            elements: prevState.elements.map(el =>
                el.id === selectedElement.id
                    ? { ...el, position: updatedPosition }
                    : el
            ),
            isDirty: true
        }), () => {
            this.pushHistory();
            this.renderCanvas();
        });
    };

    distributeElements = (direction) => {
        // TODO: Implement distribute functionality for multiple selected elements
        this.showToast('info', 'Info', 'Fonctionnalité à venir');
    };

    // ===== HELPER METHOD FOR REDRAWING ELEMENTS =====

    redrawElementsOnly = (ctx, elements, currentSide, gridEnabled, gridSize) => {
        // Dessiner la grille si activée
        if (gridEnabled) {
            ctx.strokeStyle = '#e0e0e0';
            ctx.lineWidth = 1;
            for (let x = 0; x < ctx.canvas.width; x += gridSize) {
                ctx.beginPath();
                ctx.moveTo(x, 0);
                ctx.lineTo(x, ctx.canvas.height);
                ctx.stroke();
            }
            for (let y = 0; y < ctx.canvas.height; y += gridSize) {
                ctx.beginPath();
                ctx.moveTo(0, y);
                ctx.lineTo(ctx.canvas.width, y);
                ctx.stroke();
            }
        }

        // Filtrer et trier les éléments par zIndex
        const visibleElements = elements
            .filter(el => el.side === currentSide)
            .sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0));

        // Dessiner chaque élément
        visibleElements.forEach(element => {
            this.renderElement(ctx, element);
        });
    };

    buildTemplateParameters = () => {
        const { elements, colorScheme, logoConfig, customTexts, backgroundImage } = this.state;

        // Build layout parameters with Pascal case for backend
        const layoutParams = {
            LogoPosition: null,
            PhotoPosition: null,
            QRCodePosition: null,
            TextPositions: {
                Title: null,
                Name: null,
                Info: null,
                Footer: null
            }
        };

        // Build typography parameters
        const typography = {
            TitleFont: { FontFamily: 'Arial', FontSize: 24, IsBold: true },
            TitleFontFamily: 'Arial',
            TitleFontSize: 24,
            TitleIsBold: true,
            NameFont: { FontFamily: 'Arial', FontSize: 18, IsBold: false },
            NameFontFamily: 'Arial',
            NameFontSize: 18,
            NameIsBold: false,
            InfoFont: { FontFamily: 'Arial', FontSize: 12, IsBold: false },
            InfoFontFamily: 'Arial',
            InfoFontSize: 12,
            InfoTextColor: '#333333'
        };

        // Build texts parameters to store actual text content
        const texts = {
            OrganizationTitle: null,
            TitleText: null,
            NameText: null,
            InfoText: null
        };

        // Build security parameters
        const security = {
            Watermark: null,
            MicroText: null,
            SecurityBorder: null
        };

        elements.forEach(element => {
            if (!element.enabled) return;

            switch (element.type) {
                case 'logo':
                    layoutParams.LogoPosition = {
                        X: element.position.x,
                        Y: element.position.y,
                        Width: element.position.width,
                        Height: element.position.height
                    };
                    break;
                case 'photo':
                    layoutParams.PhotoPosition = {
                        X: element.position.x,
                        Y: element.position.y,
                        Width: element.position.width,
                        Height: element.position.height,
                        Shape: element.properties.shape || 'rectangle',
                        BorderColor: element.properties.borderColor,
                        BorderWidth: element.properties.borderWidth || 0
                    };
                    break;
                case 'qrcode':
                    layoutParams.QRCodePosition = {
                        X: element.position.x,
                        Y: element.position.y,
                        Width: element.position.width,
                        Height: element.position.height
                    };
                    break;
                case 'text':
                    const textPosition = {
                        X: element.position.x,
                        Y: element.position.y,
                        Width: element.position.width,
                        Height: element.position.height
                    };

                    if (element.subType === 'title') {
                        layoutParams.TextPositions.Title = textPosition;
                        typography.TitleFont = {
                            FontFamily: element.properties.fontFamily || 'Arial',
                            FontSize: element.properties.fontSize || 24,
                            IsBold: element.properties.fontWeight === 'bold'
                        };
                        typography.TitleFontFamily = element.properties.fontFamily || 'Arial';
                        typography.TitleFontSize = element.properties.fontSize || 24;
                        typography.TitleIsBold = element.properties.fontWeight === 'bold';
                        texts.TitleText = element.properties.text || '';
                    } else if (element.subType === 'name') {
                        layoutParams.TextPositions.Name = textPosition;
                        typography.NameFont = {
                            FontFamily: element.properties.fontFamily || 'Arial',
                            FontSize: element.properties.fontSize || 18,
                            IsBold: element.properties.fontWeight === 'bold'
                        };
                        typography.NameFontFamily = element.properties.fontFamily || 'Arial';
                        typography.NameFontSize = element.properties.fontSize || 18;
                        typography.NameIsBold = element.properties.fontWeight === 'bold';
                        texts.NameText = element.properties.text || '';
                    } else if (element.subType === 'info') {
                        layoutParams.TextPositions.Info = textPosition;
                        typography.InfoFont = {
                            FontFamily: element.properties.fontFamily || 'Arial',
                            FontSize: element.properties.fontSize || 12,
                            IsBold: element.properties.fontWeight === 'bold'
                        };
                        typography.InfoFontFamily = element.properties.fontFamily || 'Arial';
                        typography.InfoFontSize = element.properties.fontSize || 12;
                        typography.InfoTextColor = element.properties.color || '#333333';
                        texts.InfoText = element.properties.text || '';
                    }
                    break;
                case 'security':
                    if (element.subType === 'watermark') {
                        security.Watermark = {
                            Enabled: element.enabled,
                            Text: element.properties.text || 'CONFIDENTIAL',
                            Opacity: element.properties.opacity || 0.1,
                            Rotation: element.properties.rotation || 45,
                            FontSize: element.properties.fontSize || 48,
                            Color: element.properties.color || '#CCCCCC'
                        };
                    } else if (element.subType === 'microtext') {
                        security.MicroText = {
                            Enabled: element.enabled,
                            FooterText1: element.properties.footerText1 || '',
                            FooterText2: element.properties.footerText2 || '',
                            FontSize: element.properties.fontSize || 8,
                            TextColor: element.properties.color || '#666666'
                        };
                    } else if (element.subType === 'securityBorder') {
                        security.SecurityBorder = {
                            Enabled: element.enabled,
                            Width: element.properties.width || 2,
                            Color: element.properties.color || '#FF0000',
                            Pattern: element.properties.pattern || 'Security'
                        };
                    }
                    break;
                default:
                    break;
            }
        });

        // Build complete TemplateParameters structure matching backend
        return {
            ColorScheme: {
                PrimaryColor: colorScheme.primaryColor || '#3B82F6',
                SecondaryColor: colorScheme.secondaryColor || '#10B981',
                AccentColor: colorScheme.accentColor || '#F59E0B',
                TextColor: colorScheme.textColor || '#1F2937',
                BackgroundColor: colorScheme.backgroundColor || '#FFFFFF',
                Gradient: colorScheme.gradient && colorScheme.gradient.enabled ? {
                    Enabled: true,
                    StartColor: colorScheme.gradient.startColor || '#667EEA',
                    EndColor: colorScheme.gradient.endColor || '#764BA2',
                    Direction: colorScheme.gradient.direction || 'TopToBottom'
                } : null
            },
            Layout: layoutParams,
            Typography: typography,
            Security: security,
            Texts: {
                OrganizationTitle: customTexts?.organizationTitle || null,
                TitleText: texts.TitleText || null,
                NameText: texts.NameText || null,
                InfoText: texts.InfoText || null,
                OrganizationSubtitle: customTexts?.organizationSubtitle || null,
                SecurityMessage: customTexts?.securityMessage || null,
                ContactMessage: customTexts?.contactMessage || null
            },
            Logo: logoConfig && (logoConfig.logoUrl || logoConfig.logoBase64) ? {
                LogoUrl: logoConfig.logoUrl || null,
                LogoBase64: logoConfig.logoBase64 || null,
                UseDefaultLogo: logoConfig.useDefaultLogo !== false
            } : null,
            BackgroundImage: {
                FrontImageBase64: backgroundImage?.frontImageBase64 || null,
                BackImageBase64: backgroundImage?.backImageBase64 || null,
                FrontImageOpacity: backgroundImage?.frontImageOpacity || 1.0,
                BackImageOpacity: backgroundImage?.backImageOpacity || 1.0
            },
            // NEW: Save complete element configurations
            Elements: elements.map(el => {
                const position = el.position || { x: 0, y: 0 };

                // Convert type from camelCase to PascalCase (e.g., formField -> FormField)
                let type = el.type || 'Text';
                if (type) {
                    type = type.charAt(0).toUpperCase() + type.slice(1);
                }

                // Convert side to PascalCase (e.g., front -> Front)
                let side = el.side || 'Front';
                if (side) {
                    side = side.charAt(0).toUpperCase() + side.slice(1);
                }

                // Clone properties to avoid mutation
                const properties = { ...el.properties };

                // FIX 1: Add logo data to logo element properties
                if (el.type === 'logo' && logoConfig) {
                    properties.logoBase64 = logoConfig.logoBase64 || null;
                    properties.logoUrl = logoConfig.logoUrl || null;
                    properties.useDefaultLogo = logoConfig.useDefaultLogo !== false;
                }

                // FIX 2: Convert formField to Text element (backend doesn't support FormField)
                if (el.type === 'formField') {
                    type = 'Text';
                    // Add formField-specific properties to help identify it
                    properties.isFormField = true;
                    properties.formFieldType = el.subType; // 'input', 'date', 'select'
                }

                // FIX 3: Add shape type to properties for backend ShapeRenderer
                if (el.type === 'shape' && el.subType) {
                    properties.shape = el.subType.toLowerCase(); // 'rectangle', 'ellipse', 'circle', 'line'
                }

                return {
                    Id: el.id,
                    Type: type,
                    SubType: el.subType || null,
                    Side: side,
                    Enabled: el.enabled !== false,
                    Position: {
                        X: Math.round(position.x || 0),
                        Y: Math.round(position.y || 0),
                        Width: position.width ? Math.round(position.width) : null,
                        Height: position.height ? Math.round(position.height) : null
                    },
                    Properties: properties,
                    ZIndex: el.zIndex || 0
                };
            })
        };
    };

    // ===== DRAG AND DROP =====

    handleDragStart = (e, elementOrType) => {
        console.log('[DRAG START] Type:', elementOrType);
        // If dragging from palette (string type), create new element
        if (typeof elementOrType === 'string') {
            const newElement = this.createNewElement(elementOrType);
            console.log('[DRAG START] New element created:', newElement.id);
            this.setState({
                draggedElement: newElement,
                isDragging: true
            });
            e.dataTransfer.effectAllowed = 'copy';
            e.dataTransfer.setData('elementType', elementOrType);
        } else {
            // Dragging existing element
            console.log('[DRAG START] Existing element:', elementOrType.id);
            this.setState({
                draggedElement: elementOrType,
                isDragging: true
            });
            e.dataTransfer.effectAllowed = 'move';
            e.dataTransfer.setData('elementId', elementOrType.id);
        }
    };

    createNewElement = (type) => {
        const { currentSide } = this.state;
        const newId = `${type}-${Date.now()}`;

        const elementTemplates = {
            text: {
                id: newId,
                type: 'text',
                subType: 'title',
                side: currentSide,
                enabled: true,
                position: { x: 100, y: 100 },
                properties: {
                    text: 'Nouveau texte',
                    fontSize: 16,
                    fontFamily: 'Arial',
                    fontWeight: 'normal',
                    color: '#000000',
                    textAlign: 'left'
                }
            },
            logo: {
                id: newId,
                type: 'logo',
                subType: 'organizationLogo',
                side: currentSide,
                enabled: true,
                position: { x: 50, y: 50, width: 100, height: 100 },
                properties: {
                    imageUrl: '',
                    placeholder: 'Logo'
                }
            },
            photo: {
                id: newId,
                type: 'photo',
                subType: 'memberPhoto',
                side: currentSide,
                enabled: true,
                position: { x: 50, y: 50, width: 80, height: 100 },
                properties: {
                    imageUrl: '',
                    placeholder: 'Photo',
                    borderRadius: 0
                }
            },
            qrcode: {
                id: newId,
                type: 'qrcode',
                subType: 'memberQR',
                side: currentSide,
                enabled: true,
                position: { x: 400, y: 400, width: 100, height: 100 },
                properties: {
                    data: '{{MEMBER_ID}}',
                    size: 100
                }
            },
            watermark: {
                id: newId,
                type: 'security',
                subType: 'watermark',
                side: currentSide,
                enabled: true,
                position: { x: 0, y: 0 },
                properties: {
                    text: 'SECURISE',
                    opacity: 0.1,
                    rotation: -45,
                    fontSize: 48,
                    color: '#CCCCCC'
                }
            },
            microtext: {
                id: newId,
                type: 'security',
                subType: 'microtext',
                side: currentSide,
                enabled: true,
                position: { x: 50, y: 560 },
                properties: {
                    footerText1: 'Document sécurisé',
                    footerText2: 'Ne peut être dupliqué',
                    fontSize: 8,
                    color: '#666666'
                }
            },
            securityBorder: {
                id: newId,
                type: 'security',
                subType: 'securityBorder',
                side: currentSide,
                enabled: true,
                position: { x: 0, y: 0 },
                properties: {
                    width: 2,
                    color: '#FF0000',
                    pattern: 'Security'
                }
            },
            organizationTitle: {
                id: newId,
                type: 'text',
                subType: 'organizationTitle',
                side: currentSide,
                enabled: true,
                position: { x: 200, y: 50 },
                properties: {
                    text: '{{ORGANIZATION_NAME}}',
                    fontSize: 18,
                    fontFamily: 'Arial',
                    fontWeight: 'bold',
                    color: '#000000',
                    textAlign: 'left'
                }
            },
            organizationSubtitle: {
                id: newId,
                type: 'text',
                subType: 'organizationSubtitle',
                side: currentSide,
                enabled: true,
                position: { x: 200, y: 80 },
                properties: {
                    text: '{{ORGANIZATION_SUBTITLE}}',
                    fontSize: 12,
                    fontFamily: 'Arial',
                    fontWeight: 'normal',
                    color: '#666666',
                    textAlign: 'left'
                }
            },
            securityMessage: {
                id: newId,
                type: 'text',
                subType: 'securityMessage',
                side: currentSide,
                enabled: true,
                position: { x: 50, y: 200 },
                properties: {
                    text: '{{SECURITY_MESSAGE}}',
                    fontSize: 10,
                    fontFamily: 'Arial',
                    fontWeight: 'normal',
                    color: '#FF0000',
                    textAlign: 'left'
                }
            },
            contactMessage: {
                id: newId,
                type: 'text',
                subType: 'contactMessage',
                side: currentSide,
                enabled: true,
                position: { x: 50, y: 530 },
                properties: {
                    text: '{{CONTACT_MESSAGE}}',
                    fontSize: 10,
                    fontFamily: 'Arial',
                    fontWeight: 'normal',
                    color: '#333333',
                    textAlign: 'left'
                }
            },
            rectangle: {
                id: newId,
                type: 'shape',
                subType: 'rectangle',
                side: currentSide,
                enabled: true,
                position: { x: 100, y: 100, width: 150, height: 100 },
                properties: {
                    fillColor: '#DAA520',
                    borderColor: '#000000',
                    borderWidth: 1,
                    cornerRadius: 0
                }
            },
            circle: {
                id: newId,
                type: 'shape',
                subType: 'circle',
                side: currentSide,
                enabled: true,
                position: { x: 100, y: 100, width: 100, height: 100 },
                properties: {
                    fillColor: '#3B82F6',
                    borderColor: '#000000',
                    borderWidth: 2,
                    cornerRadius: 0
                }
            },
            line: {
                id: newId,
                type: 'shape',
                subType: 'line',
                side: currentSide,
                enabled: true,
                position: { x: 100, y: 100, width: 200, height: 0 },
                properties: {
                    fillColor: 'transparent',
                    borderColor: '#000000',
                    borderWidth: 2,
                    cornerRadius: 0
                }
            },
            ellipse: {
                id: newId,
                type: 'shape',
                subType: 'ellipse',
                side: currentSide,
                enabled: true,
                position: { x: 100, y: 100, width: 150, height: 80 },
                properties: {
                    fillColor: '#F59E0B',
                    borderColor: '#000000',
                    borderWidth: 2,
                    cornerRadius: 0
                }
            },
            arc: {
                id: newId,
                type: 'shape',
                subType: 'arc',
                side: currentSide,
                enabled: true,
                position: { x: 0, y: 0, width: 500, height: 120 },
                properties: {
                    fillColor: '#6366F1',
                    borderColor: 'transparent',
                    borderWidth: 0,
                    arcHeight: 120,
                    arcDirection: 'down'
                }
            },
            barcode: {
                id: newId,
                type: 'barcode',
                subType: 'code128',
                side: currentSide,
                enabled: true,
                position: { x: 150, y: 500, width: 320, height: 60 },
                properties: {
                    data: '{{barcodeData}}',
                    format: 'CODE128',
                    showText: true,
                    textSize: 12,
                    textColor: '#000000'
                }
            },
            image: {
                id: newId,
                type: 'image',
                subType: 'signature',
                side: currentSide,
                enabled: true,
                position: { x: 400, y: 450, width: 100, height: 40 },
                properties: {
                    imageUrl: '',
                    imageBase64: '',
                    placeholder: 'Signature'
                }
            },
            inputField: {
                id: newId,
                type: 'formField',
                subType: 'input',
                side: currentSide,
                enabled: true,
                position: { x: 100, y: 150, width: 200, height: 25 },
                properties: {
                    label: 'Champ de texte',
                    placeholder: '{{FIELD_VALUE}}',
                    fontSize: 12,
                    fontFamily: 'Arial',
                    fontWeight: 'normal',
                    color: '#000000',
                    borderColor: '#CCCCCC',
                    borderWidth: 1,
                    backgroundColor: '#FFFFFF'
                }
            },
            dateField: {
                id: newId,
                type: 'formField',
                subType: 'date',
                side: currentSide,
                enabled: true,
                position: { x: 100, y: 200, width: 150, height: 25 },
                properties: {
                    label: 'Date',
                    placeholder: '{{DATE_VALUE}}',
                    fontSize: 12,
                    fontFamily: 'Arial',
                    fontWeight: 'normal',
                    color: '#000000',
                    borderColor: '#CCCCCC',
                    borderWidth: 1,
                    backgroundColor: '#FFFFFF'
                }
            },
            selectField: {
                id: newId,
                type: 'formField',
                subType: 'select',
                side: currentSide,
                enabled: true,
                position: { x: 100, y: 250, width: 200, height: 25 },
                properties: {
                    label: 'Liste déroulante',
                    placeholder: '{{SELECT_VALUE}}',
                    fontSize: 12,
                    fontFamily: 'Arial',
                    fontWeight: 'normal',
                    color: '#000000',
                    borderColor: '#CCCCCC',
                    borderWidth: 1,
                    backgroundColor: '#FFFFFF'
                }
            }
        };

        return elementTemplates[type] || elementTemplates.text;
    };

    handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        e.dataTransfer.dropEffect = 'copy';
        console.log('[DRAG OVER] Position:', e.clientX, e.clientY);
    };

    handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        console.log('[DROP] Event triggered');

        const { draggedElement, elements, snapToGrid, gridSize } = this.state;
        if (!draggedElement) {
            console.log('[DROP] No dragged element in state');
            return;
        }

        console.log('[DROP] Dragged element:', draggedElement.id);

        // Get the canvas element's bounding rectangle
        const canvas = this.canvasRef.current;
        if (!canvas) {
            console.log('[DROP] No canvas ref');
            return;
        }

        const rect = canvas.getBoundingClientRect();

        // Calculer la nouvelle position relative au canvas
        let newX = (e.clientX - rect.left) / SCALE_FACTOR;
        let newY = (e.clientY - rect.top) / SCALE_FACTOR;
        console.log('[DROP] Position calculated:', { newX, newY });

        // Snap to grid si activé
        if (snapToGrid) {
            newX = Math.round(newX / gridSize) * gridSize;
            newY = Math.round(newY / gridSize) * gridSize;
        }

        // Valider les limites
        newX = Math.max(0, Math.min(newX, VALIDATION_RULES.position.x.max));
        newY = Math.max(0, Math.min(newY, VALIDATION_RULES.position.y.max));

        // Check if this is a new element from palette or moving existing element
        const elementExists = elements.some(el => el.id === draggedElement.id);

        let updatedElements;
        if (elementExists) {
            // Move existing element
            updatedElements = elements.map(el => {
                if (el.id === draggedElement.id) {
                    return {
                        ...el,
                        position: {
                            ...el.position,
                            x: newX,
                            y: newY
                        }
                    };
                }
                return el;
            });
        } else {
            // Add new element from palette
            console.log('[DROP] Adding new element to canvas');
            const newElement = {
                ...draggedElement,
                enabled: true,
                side: this.state.currentSide,
                position: {
                    x: newX,
                    y: newY,
                    width: draggedElement.position?.width || 100,
                    height: draggedElement.position?.height || 20
                }
            };
            updatedElements = [...elements, newElement];
            console.log('[DROP] New element added:', newElement.id);
        }

        this.setState({
            elements: updatedElements,
            draggedElement: null,
            isDragging: false,
            selectedElement: elementExists ? null : updatedElements[updatedElements.length - 1],
            isDirty: true
        }, () => {
            console.log('[DROP] State updated, rendering canvas');
            this.pushHistory();
            this.renderCanvas();
            this.validateTemplate();
        });
    };

    handleDragEnd = () => {
        this.setState({
            draggedElement: null,
            isDragging: false
        });
    };

    // ===== CANVAS EVENT LISTENERS =====

    addCanvasEventListeners = () => {
        const canvas = this.canvasRef.current;
        if (!canvas) return;

        canvas.addEventListener('mousedown', this.handleCanvasMouseDown);
        canvas.addEventListener('mousemove', this.handleCanvasMouseMoveAndHover);
        canvas.addEventListener('mouseup', this.handleCanvasMouseUp);
        canvas.addEventListener('click', this.handleCanvasClick);
    };

    removeCanvasEventListeners = () => {
        const canvas = this.canvasRef.current;
        if (!canvas) return;

        canvas.removeEventListener('mousedown', this.handleCanvasMouseDown);
        canvas.removeEventListener('mousemove', this.handleCanvasMouseMoveAndHover);
        canvas.removeEventListener('mouseup', this.handleCanvasMouseUp);
        canvas.removeEventListener('click', this.handleCanvasClick);
    };

    componentWillUnmount() {
        this.removeCanvasEventListeners();
        // Clean up keyboard event listener
        document.removeEventListener('keydown', this.handleKeyDown);
        // Clean up property update timer
        if (this.propertyUpdateTimer) {
            clearTimeout(this.propertyUpdateTimer);
        }
    }

    handleCanvasClick = (e) => {
        const rect = this.canvasRef.current.getBoundingClientRect();
        const x = (e.clientX - rect.left) / SCALE_FACTOR;
        const y = (e.clientY - rect.top) / SCALE_FACTOR;

        // Trouver l'élément cliqué (en partant du dernier pour gérer les superpositions)
        const clickedElement = this.findElementAt(x, y);

        if (clickedElement) {
            this.setState({
                selectedElement: clickedElement
            });
        } else {
            // Clic sur le canvas vide, désélectionner
            this.setState({ selectedElement: null });
        }
    };

    handleCanvasMouseDown = (e) => {
        const rect = this.canvasRef.current.getBoundingClientRect();
        const x = (e.clientX - rect.left) / SCALE_FACTOR;
        const y = (e.clientY - rect.top) / SCALE_FACTOR;

        // Trouver l'élément sous la souris
        const element = this.findElementAt(x, y);

        if (element) {
            // Check for Ctrl+click for multi-selection
            if (e.ctrlKey) {
                this.toggleElementSelection(element.id, true);
                return;
            }

            // If element is part of a group and not in multi-select mode, select whole group
            const group = this.getElementGroup(element.id);
            if (group && this.state.selectedElements.length === 0) {
                this.selectGroup(group.id);
                return;
            }

            // Vérifier si on clique sur une poignée de redimensionnement
            const resizeHandle = this.getResizeHandle(x, y, element);

            if (resizeHandle) {
                // Mode redimensionnement
                this.setState({
                    selectedElement: element,
                    isResizing: true,
                    resizeHandle: resizeHandle,
                    resizeStartPos: { x, y },
                    resizeStartDimensions: {
                        x: element.position.x,
                        y: element.position.y,
                        width: element.position.width || 100,
                        height: element.position.height || 20
                    }
                });
            } else {
                // Mode déplacement
                this.setState({
                    selectedElement: element,
                    selectedElements: [], // Clear multi-selection when starting drag
                    isDragging: true,
                    draggedElement: element,
                    dragOffset: {
                        x: x - element.position.x,
                        y: y - element.position.y
                    }
                });
            }
        } else {
            // Clicked on empty canvas - clear selection
            this.clearSelection();
        }
    };

    handleCanvasMouseMoveAndHover = (e) => {
        const rect = this.canvasRef.current.getBoundingClientRect();
        const x = (e.clientX - rect.left) / SCALE_FACTOR;
        const y = (e.clientY - rect.top) / SCALE_FACTOR;

        // Si en train de redimensionner
        if (this.state.isResizing && this.state.selectedElement) {
            const { resizeHandle, resizeStartPos, resizeStartDimensions, snapToGrid, gridSize } = this.state;

            let deltaX = x - resizeStartPos.x;
            let deltaY = y - resizeStartPos.y;

            let newX = resizeStartDimensions.x;
            let newY = resizeStartDimensions.y;
            let newWidth = resizeStartDimensions.width;
            let newHeight = resizeStartDimensions.height;

            // Calculer les nouvelles dimensions selon la poignée
            switch (resizeHandle) {
                case 'se': // Bottom-right
                    newWidth = resizeStartDimensions.width + deltaX;
                    newHeight = resizeStartDimensions.height + deltaY;
                    break;
                case 'sw': // Bottom-left
                    newX = resizeStartDimensions.x + deltaX;
                    newWidth = resizeStartDimensions.width - deltaX;
                    newHeight = resizeStartDimensions.height + deltaY;
                    break;
                case 'ne': // Top-right
                    newY = resizeStartDimensions.y + deltaY;
                    newWidth = resizeStartDimensions.width + deltaX;
                    newHeight = resizeStartDimensions.height - deltaY;
                    break;
                case 'nw': // Top-left
                    newX = resizeStartDimensions.x + deltaX;
                    newY = resizeStartDimensions.y + deltaY;
                    newWidth = resizeStartDimensions.width - deltaX;
                    newHeight = resizeStartDimensions.height - deltaY;
                    break;
                case 'e': // Right
                    newWidth = resizeStartDimensions.width + deltaX;
                    break;
                case 'w': // Left
                    newX = resizeStartDimensions.x + deltaX;
                    newWidth = resizeStartDimensions.width - deltaX;
                    break;
                case 's': // Bottom
                    newHeight = resizeStartDimensions.height + deltaY;
                    break;
                case 'n': // Top
                    newY = resizeStartDimensions.y + deltaY;
                    newHeight = resizeStartDimensions.height - deltaY;
                    break;
            }

            // Appliquer les contraintes minimales
            newWidth = Math.max(VALIDATION_RULES.position.width.min, newWidth);
            newHeight = Math.max(VALIDATION_RULES.position.height.min, newHeight);

            // Snap to grid si activé
            if (snapToGrid) {
                newX = Math.round(newX / gridSize) * gridSize;
                newY = Math.round(newY / gridSize) * gridSize;
                newWidth = Math.round(newWidth / gridSize) * gridSize;
                newHeight = Math.round(newHeight / gridSize) * gridSize;
            }

            // Mettre à jour l'élément
            const updatedElements = this.state.elements.map(el => {
                if (el.id === this.state.selectedElement.id) {
                    return {
                        ...el,
                        position: {
                            ...el.position,
                            x: newX,
                            y: newY,
                            width: newWidth,
                            height: newHeight
                        }
                    };
                }
                return el;
            });

            this.setState({ elements: updatedElements });
        }
        // Si en train de glisser, gérer le déplacement
        else if (this.state.isDragging && this.state.draggedElement) {
            const { dragOffset, snapToGrid, gridSize } = this.state;

            // Vérifier que dragOffset existe
            if (!dragOffset) return;

            // Calculer la nouvelle position en tenant compte de l'offset
            let newX = x - dragOffset.x;
            let newY = y - dragOffset.y;

            // Snap to grid si activé
            if (snapToGrid) {
                newX = Math.round(newX / gridSize) * gridSize;
                newY = Math.round(newY / gridSize) * gridSize;
            }

            // Valider les limites
            newX = Math.max(0, Math.min(newX, VALIDATION_RULES.position.x.max));
            newY = Math.max(0, Math.min(newY, VALIDATION_RULES.position.y.max));

            // Mettre à jour la position de l'élément
            const updatedElements = this.state.elements.map(el => {
                if (el.id === this.state.draggedElement.id) {
                    return {
                        ...el,
                        position: {
                            ...el.position,
                            x: newX,
                            y: newY
                        }
                    };
                }
                return el;
            });

            this.setState({
                elements: updatedElements
            });
        } else {
            // Gérer le hover (changement de curseur)
            const hoveredElement = this.findElementAt(x, y);
            const canvas = this.canvasRef.current;

            if (hoveredElement) {
                const resizeHandle = this.getResizeHandle(x, y, hoveredElement);
                if (resizeHandle) {
                    canvas.style.cursor = this.getCursorForHandle(resizeHandle);
                } else {
                    canvas.style.cursor = 'move';
                }
            } else {
                canvas.style.cursor = 'crosshair';
            }
        }
    };

    handleCanvasMouseUp = (e) => {
        if (this.state.isDragging || this.state.isResizing) {
            this.setState({
                isDragging: false,
                draggedElement: null,
                dragOffset: null,
                isResizing: false,
                resizeHandle: null,
                resizeStartPos: null,
                resizeStartDimensions: null
            }, () => {
                // Push to history after drag/resize completes
                this.pushHistory();
            });
            this.validateTemplate();
        }
    };

    findElementAt = (x, y) => {
        const { elements, currentSide } = this.state;



        // Parcourir les éléments en ordre inverse pour gérer les superpositions
        for (let i = elements.length - 1; i >= 0; i--) {
            const element = elements[i];
            if (!element.enabled) continue;

            // Ignorer les éléments qui ne sont pas sur le côté actuel
            if (element.side && element.side !== currentSide) continue;

            const pos = element.position;


            // Vérifier si le point est dans les limites de l'élément
            if (pos.width && pos.height) {
                // Élément avec dimensions (logo, photo, qrcode)
                const isInBounds = x >= pos.x && x <= pos.x + pos.width &&
                                   y >= pos.y && y <= pos.y + pos.height;


                if (isInBounds) {

                    return element;
                }
            } else if (element.type === 'text') {
                // Élément de texte (approximation basée sur la taille du texte)
                const fontSize = element.properties?.fontSize || 14;
                const text = element.properties?.text || 'Texte';
                const textWidth = text.length * fontSize * 0.6;
                const textHeight = fontSize + 4; // Marge pour la hauteur

                const isInTextBounds = x >= pos.x && x <= pos.x + textWidth &&
                                       y >= pos.y && y <= pos.y + textHeight;


                if (isInTextBounds) {

                    return element;
                }
            }
        }


        return null;
    };

    handleCanvasHover = (e) => {
        if (this.state.isDragging) return;

        const rect = this.canvasRef.current.getBoundingClientRect();
        const x = (e.clientX - rect.left) / SCALE_FACTOR;
        const y = (e.clientY - rect.top) / SCALE_FACTOR;

        // Vérifier si la souris survole un élément
        const hoveredElement = this.findElementAt(x, y);
        const canvas = this.canvasRef.current;

        if (hoveredElement) {
            canvas.style.cursor = 'move';
        } else {
            canvas.style.cursor = 'crosshair';
        }
    };

    // ===== RENDU CANVAS =====

    renderCanvas = () => {
        const canvas = this.canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        const { elements, colorScheme, gridEnabled, gridSize, currentSide, backgroundImage } = this.state;

        // Clear canvas
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        // Dessiner l'arrière-plan (couleur ou dégradé)
        if (colorScheme.gradient.enabled) {
            const gradient = ctx.createLinearGradient(0, 0,
                colorScheme.gradient.direction === 'LeftToRight' ? canvas.width : 0,
                colorScheme.gradient.direction === 'TopToBottom' ? canvas.height : 0
            );
            gradient.addColorStop(0, colorScheme.gradient.startColor);
            gradient.addColorStop(1, colorScheme.gradient.endColor);
            ctx.fillStyle = gradient;
        } else {
            ctx.fillStyle = colorScheme.backgroundColor;
        }
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        // Dessiner l'image de fond si présente
        const backgroundImageUrl = currentSide === 'front' ? backgroundImage.frontImageUrl : backgroundImage.backImageUrl;
        const backgroundOpacity = currentSide === 'front' ? backgroundImage.frontImageOpacity : backgroundImage.backImageOpacity;

        if (backgroundImageUrl) {
            const img = new Image();
            img.src = backgroundImageUrl;

            // Si l'image est déjà chargée
            if (img.complete) {
                ctx.save();
                ctx.globalAlpha = backgroundOpacity || 1.0;
                ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
                ctx.restore();
            } else {
                // Charger l'image de manière asynchrone
                img.onload = () => {
                    ctx.save();
                    ctx.globalAlpha = backgroundOpacity || 1.0;
                    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
                    ctx.restore();
                    // Redessiner les autres éléments après le chargement de l'image
                    this.redrawElementsOnly(ctx, elements, currentSide, gridEnabled, gridSize);
                };
            }
        }

        // Dessiner la grille si activée
        if (gridEnabled) {
            ctx.strokeStyle = '#e0e0e0';
            ctx.lineWidth = 0.5;
            for (let x = 0; x <= canvas.width; x += gridSize * SCALE_FACTOR) {
                ctx.beginPath();
                ctx.moveTo(x, 0);
                ctx.lineTo(x, canvas.height);
                ctx.stroke();
            }
            for (let y = 0; y <= canvas.height; y += gridSize * SCALE_FACTOR) {
                ctx.beginPath();
                ctx.moveTo(0, y);
                ctx.lineTo(canvas.width, y);
                ctx.stroke();
            }
        }

        // Dessiner uniquement les éléments du côté actuel, triés par zIndex
        const visibleElements = elements
            .filter(element => {
                if (!element.enabled) return false;
                if (element.side && element.side !== currentSide) return false;
                return true;
            })
            .sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0));

        visibleElements.forEach(element => {
            this.renderElement(ctx, element);
        });

        // Draw selection indicators for multi-selected elements and groups
        this.drawSelectionIndicators(ctx, visibleElements);
    };

    drawSelectionIndicators = (ctx, visibleElements) => {
        const { selectedElements, selectedElement, groups } = this.state;

        // Draw multi-selection indicators
        if (selectedElements.length > 0) {
            selectedElements.forEach(elementId => {
                const element = visibleElements.find(el => el.id === elementId);
                if (element && element.position) {
                    const x = element.position.x * SCALE_FACTOR;
                    const y = element.position.y * SCALE_FACTOR;
                    const width = (element.position.width || 100) * SCALE_FACTOR;
                    const height = (element.position.height || 20) * SCALE_FACTOR;

                    // Draw blue dashed border for multi-selected elements
                    ctx.strokeStyle = '#2196F3';
                    ctx.lineWidth = 2;
                    ctx.setLineDash([5, 5]);
                    ctx.strokeRect(x - 2, y - 2, width + 4, height + 4);
                    ctx.setLineDash([]);
                }
            });

            // Draw group bounding box if all selected elements belong to the same group
            const firstElement = visibleElements.find(el => el.id === selectedElements[0]);
            if (firstElement && firstElement.groupId) {
                const group = groups[firstElement.groupId];
                const allInSameGroup = group && selectedElements.every(id =>
                    visibleElements.find(el => el.id === id)?.groupId === firstElement.groupId
                );

                if (allInSameGroup) {
                    // Calculate bounding box for the group
                    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;

                    selectedElements.forEach(elementId => {
                        const element = visibleElements.find(el => el.id === elementId);
                        if (element && element.position) {
                            const x = element.position.x;
                            const y = element.position.y;
                            const width = element.position.width || 100;
                            const height = element.position.height || 20;

                            minX = Math.min(minX, x);
                            minY = Math.min(minY, y);
                            maxX = Math.max(maxX, x + width);
                            maxY = Math.max(maxY, y + height);
                        }
                    });

                    if (minX !== Infinity) {
                        // Draw purple dashed border for group bounding box
                        ctx.strokeStyle = '#9C27B0';
                        ctx.lineWidth = 3;
                        ctx.setLineDash([8, 4]);
                        ctx.strokeRect(
                            minX * SCALE_FACTOR - 5,
                            minY * SCALE_FACTOR - 5,
                            (maxX - minX) * SCALE_FACTOR + 10,
                            (maxY - minY) * SCALE_FACTOR + 10
                        );
                        ctx.setLineDash([]);

                        // Draw group label
                        ctx.fillStyle = '#9C27B0';
                        ctx.font = '12px Arial';
                        ctx.fillText(
                            group.name,
                            minX * SCALE_FACTOR - 5,
                            minY * SCALE_FACTOR - 10
                        );
                    }
                }
            }
        }

        // Draw single selection indicator (green border)
        if (selectedElement && selectedElement.position) {
            const x = selectedElement.position.x * SCALE_FACTOR;
            const y = selectedElement.position.y * SCALE_FACTOR;
            const width = (selectedElement.position.width || 100) * SCALE_FACTOR;
            const height = (selectedElement.position.height || 20) * SCALE_FACTOR;

            // Draw green solid border for single selected element
            ctx.strokeStyle = '#00A651';
            ctx.lineWidth = 2;
            ctx.strokeRect(x - 2, y - 2, width + 4, height + 4);

            // Draw resize handles
            this.drawResizeHandles(ctx, x, y, width, height);
        }
    };

    drawResizeHandles = (ctx, x, y, width, height) => {
        const handleSize = 8;
        const handles = [
            { x: x - handleSize / 2, y: y - handleSize / 2 }, // nw
            { x: x + width / 2 - handleSize / 2, y: y - handleSize / 2 }, // n
            { x: x + width - handleSize / 2, y: y - handleSize / 2 }, // ne
            { x: x + width - handleSize / 2, y: y + height / 2 - handleSize / 2 }, // e
            { x: x + width - handleSize / 2, y: y + height - handleSize / 2 }, // se
            { x: x + width / 2 - handleSize / 2, y: y + height - handleSize / 2 }, // s
            { x: x - handleSize / 2, y: y + height - handleSize / 2 }, // sw
            { x: x - handleSize / 2, y: y + height / 2 - handleSize / 2 } // w
        ];

        handles.forEach(handle => {
            ctx.fillStyle = '#FFFFFF';
            ctx.strokeStyle = '#00A651';
            ctx.lineWidth = 2;
            ctx.fillRect(handle.x, handle.y, handleSize, handleSize);
            ctx.strokeRect(handle.x, handle.y, handleSize, handleSize);
        });
    };

    renderElement = (ctx, element) => {
        const x = element.position.x * SCALE_FACTOR;
        const y = element.position.y * SCALE_FACTOR;

        ctx.save();

        switch (element.type) {
            case 'logo':
                this.renderLogo(ctx, element, x, y);
                break;
            case 'photo':
                this.renderPhoto(ctx, element, x, y);
                break;
            case 'text':
                this.renderText(ctx, element, x, y);
                break;
            case 'qrcode':
                this.renderQRCode(ctx, element, x, y);
                break;
            case 'shape':
                this.renderShape(ctx, element, x, y);
                break;
            case 'image':
                this.renderImage(ctx, element, x, y);
                break;
            case 'barcode':
                this.renderBarcode(ctx, element, x, y);
                break;
            case 'security':
                if (element.subType === 'watermark') {
                    this.renderWatermark(ctx, element);
                } else if (element.subType === 'microtext') {
                    this.renderMicrotext(ctx, element, x, y);
                } else if (element.subType === 'securityBorder') {
                    this.renderSecurityBorder(ctx, element, x, y);
                }
                break;
            case 'formField':
                this.renderFormField(ctx, element, x, y);
                break;
            default:
                break;
        }

        // Dessiner la bordure de sélection si l'élément est sélectionné
        if (this.state.selectedElement?.id === element.id) {
            ctx.strokeStyle = '#3B82F6';
            ctx.lineWidth = 2;
            ctx.setLineDash([5, 5]);

            if (element.position.width && element.position.height) {
                // Élément avec dimensions définie
                ctx.strokeRect(x - 2, y - 2,
                    element.position.width * SCALE_FACTOR + 4,
                    element.position.height * SCALE_FACTOR + 4
                );

                // Ajouter des poignées de redimensionnement
                this.drawResizeHandles(ctx, x, y, element.position.width * SCALE_FACTOR, element.position.height * SCALE_FACTOR);
            } else {
                // Élément de texte
                const textWidth = element.properties.text ? element.properties.text.length * element.properties.fontSize * 0.6 : 100;
                const textHeight = element.properties.fontSize || 20;
                ctx.strokeRect(x - 2, y - 2, textWidth + 4, textHeight + 4);
            }
        }

        ctx.restore();
    };

    drawResizeHandles = (ctx, x, y, width, height) => {
        const handleSize = 8;
        ctx.fillStyle = '#3B82F6';
        ctx.strokeStyle = '#FFFFFF';
        ctx.lineWidth = 2;
        ctx.setLineDash([]);

        // 8 poignées: 4 coins + 4 milieux
        const handles = [
            { x: x - handleSize/2, y: y - handleSize/2, type: 'nw' }, // Top-left
            { x: x + width/2 - handleSize/2, y: y - handleSize/2, type: 'n' }, // Top-center
            { x: x + width - handleSize/2, y: y - handleSize/2, type: 'ne' }, // Top-right
            { x: x + width - handleSize/2, y: y + height/2 - handleSize/2, type: 'e' }, // Middle-right
            { x: x + width - handleSize/2, y: y + height - handleSize/2, type: 'se' }, // Bottom-right
            { x: x + width/2 - handleSize/2, y: y + height - handleSize/2, type: 's' }, // Bottom-center
            { x: x - handleSize/2, y: y + height - handleSize/2, type: 'sw' }, // Bottom-left
            { x: x - handleSize/2, y: y + height/2 - handleSize/2, type: 'w' }, // Middle-left
        ];

        handles.forEach(handle => {
            ctx.fillRect(handle.x, handle.y, handleSize, handleSize);
            ctx.strokeRect(handle.x, handle.y, handleSize, handleSize);
        });
    };

    getResizeHandle = (x, y, element) => {
        if (!element || !element.position.width || !element.position.height) return null;

        const pos = element.position;
        const handleSize = 8 / SCALE_FACTOR; // Ajuster pour la taille réelle
        const tolerance = 5 / SCALE_FACTOR;

        const ex = pos.x;
        const ey = pos.y;
        const ew = pos.width;
        const eh = pos.height;

        // Vérifier chaque poignée
        const handles = [
            { x: ex, y: ey, type: 'nw' },
            { x: ex + ew/2, y: ey, type: 'n' },
            { x: ex + ew, y: ey, type: 'ne' },
            { x: ex + ew, y: ey + eh/2, type: 'e' },
            { x: ex + ew, y: ey + eh, type: 'se' },
            { x: ex + ew/2, y: ey + eh, type: 's' },
            { x: ex, y: ey + eh, type: 'sw' },
            { x: ex, y: ey + eh/2, type: 'w' },
        ];

        for (const handle of handles) {
            if (Math.abs(x - handle.x) <= handleSize + tolerance &&
                Math.abs(y - handle.y) <= handleSize + tolerance) {
                return handle.type;
            }
        }

        return null;
    };

    getCursorForHandle = (handle) => {
        const cursors = {
            'nw': 'nwse-resize',
            'ne': 'nesw-resize',
            'sw': 'nesw-resize',
            'se': 'nwse-resize',
            'n': 'ns-resize',
            's': 'ns-resize',
            'e': 'ew-resize',
            'w': 'ew-resize'
        };
        return cursors[handle] || 'default';
    };

    renderLogo = (ctx, element, x, y) => {
        const width = element.position.width * SCALE_FACTOR;
        const height = element.position.height * SCALE_FACTOR;
        const { logoConfig } = this.state;

        // Check if logo is loaded
        if (logoConfig && (logoConfig.logoBase64 || logoConfig.logoUrl)) {
            const logoSrc = logoConfig.logoBase64 || logoConfig.logoUrl;

            // Check if image is already cached
            if (!this.logoImageCache) {
                this.logoImageCache = new Image();
                this.logoImageCache.onload = () => {
                    this.renderCanvas(); // Re-render when image loads
                };
                this.logoImageCache.src = logoSrc;
            } else if (this.logoImageCache.src !== logoSrc) {
                // Update cache if logo changed
                this.logoImageCache.src = logoSrc;
            }

            // Draw the logo if loaded
            if (this.logoImageCache.complete) {
                ctx.drawImage(this.logoImageCache, x, y, width, height);
                return;
            }
        }

        // Placeholder pour le logo (shown while loading or if no logo)
        ctx.fillStyle = '#f0f0f0';
        ctx.fillRect(x, y, width, height);
        ctx.strokeStyle = '#ccc';
        ctx.strokeRect(x, y, width, height);

        ctx.fillStyle = '#666';
        ctx.font = '12px Arial';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText('LOGO', x + width/2, y + height/2);
    };

    renderPhoto = (ctx, element, x, y) => {
        const width = element.position.width * SCALE_FACTOR;
        const height = element.position.height * SCALE_FACTOR;

        ctx.fillStyle = '#f8f8f8';

        // Dessiner selon la forme
        if (element.properties.shape === 'circle') {
            ctx.beginPath();
            ctx.arc(x + width/2, y + height/2, Math.min(width, height)/2, 0, 2 * Math.PI);
            ctx.fill();
            ctx.strokeStyle = element.properties.borderColor;
            ctx.lineWidth = element.properties.borderWidth;
            ctx.stroke();
        } else if (element.properties.shape === 'roundedRectangle') {
            const radius = 10 * SCALE_FACTOR;
            ctx.beginPath();
            ctx.moveTo(x + radius, y);
            ctx.lineTo(x + width - radius, y);
            ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
            ctx.lineTo(x + width, y + height - radius);
            ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
            ctx.lineTo(x + radius, y + height);
            ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
            ctx.lineTo(x, y + radius);
            ctx.quadraticCurveTo(x, y, x + radius, y);
            ctx.closePath();
            ctx.fill();
            ctx.strokeStyle = element.properties.borderColor;
            ctx.lineWidth = element.properties.borderWidth;
            ctx.stroke();
        } else {
            ctx.fillRect(x, y, width, height);
            ctx.strokeStyle = element.properties.borderColor;
            ctx.lineWidth = element.properties.borderWidth;
            ctx.strokeRect(x, y, width, height);
        }

        // Texte placeholder
        ctx.fillStyle = '#999';
        ctx.font = '10px Arial';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText('PHOTO', x + width/2, y + height/2);
    };

    renderText = (ctx, element, x, y) => {
        ctx.fillStyle = element.properties.color;
        ctx.font = `${element.properties.fontWeight === 'bold' ? 'bold ' : ''}${element.properties.fontSize * SCALE_FACTOR}px ${element.properties.fontFamily}`;

        // Use the textAlign property from element (default to 'left' if not set)
        const textAlign = element.properties.textAlign || 'left';
        ctx.textAlign = textAlign;
        ctx.textBaseline = 'top';

        // Remplacer les variables dynamiques par des valeurs d'exemple
        let displayText = element.properties.text;
        const variablePattern = /\{\{(\w+)\}\}/g;
        displayText = displayText.replace(variablePattern, (match, varName) => {
            // Valeurs d'exemple pour la prévisualisation
            const exampleValues = {
                'lienParente': 'EMPLOYEE',
                'nomComplet': 'Abudu Kamara',
                'assurePrincipal': 'Abudu Kamara',
                'souscripteur': 'STREET CHILD INTERNATIONAL (S.C.I)',
                'dateNaissance': '15/08/1990',
                'numeroPolice': 'GSQ4101383',
                'numeroBeneficiaire': '00226',
                'numeroMembre': '00226',
                'dateExpiration': '31/12/2025',
                'barcodeData': 'SCI 00226',
                'qrcodeData': 'ID:00226|NAME:Abudu Kamara|DOB:1990-08-15|POLICY:GSQ4101383'
            };
            return exampleValues[varName] || varName;
        });

        // Gérer les lignes multiples - supporte \n, \\n, et les vrais retours à la ligne
        const lines = displayText.split(/\r?\n|\\n/);
        const lineHeight = (element.properties.lineHeight || element.properties.fontSize * 1.2) * SCALE_FACTOR;

        // Calculate X position based on alignment and element width
        let drawX = x;
        const elementWidth = (element.position.width || 0) * SCALE_FACTOR;

        if (textAlign === 'center' && elementWidth > 0) {
            drawX = x + (elementWidth / 2);
        } else if (textAlign === 'right' && elementWidth > 0) {
            drawX = x + elementWidth;
        }

        lines.forEach((line, index) => {
            ctx.fillText(line, drawX, y + (index * lineHeight));
        });
    };


    renderQRCode = (ctx, element, x, y) => {
        const width = element.position.width * SCALE_FACTOR;
        const height = element.position.height * SCALE_FACTOR;

        // Vérifier si c'est un barcode ou un QR code
        const isBarcode = element.subType === 'barcode' || element.properties.style === 'barcode';

        if (isBarcode) {
            // Rendu du barcode
            ctx.fillStyle = '#fff';
            ctx.fillRect(x, y, width, height);

            // Dessiner les barres du code-barres
            const barWidth = width / 50;
            ctx.fillStyle = element.properties.color || '#000';

            for (let i = 0; i < 50; i++) {
                // Pattern aléatoire pour simuler un barcode
                const barHeight = height * 0.7;
                if (Math.random() > 0.4) {
                    ctx.fillRect(x + i * barWidth, y + 10, barWidth * 0.8, barHeight);
                }
            }

            // Afficher le texte sous le barcode si displayValue est true
            if (element.properties.displayValue && element.properties.text) {
                ctx.fillStyle = '#000';
                ctx.font = `${(element.properties.fontSize || 12) * SCALE_FACTOR}px monospace`;
                ctx.textAlign = 'center';
                ctx.textBaseline = 'top';
                ctx.fillText(element.properties.text, x + width / 2, y + height * 0.8);
            }
        } else {
            // Rendu du QR code (code original)
            ctx.fillStyle = '#fff';
            ctx.fillRect(x, y, width, height);
            ctx.strokeStyle = '#000';
            ctx.strokeRect(x, y, width, height);

            // Simuler un pattern QR
            const cellSize = width / 20;
            ctx.fillStyle = element.properties.color;
            for (let i = 0; i < 20; i++) {
                for (let j = 0; j < 20; j++) {
                    if (Math.random() > 0.5) {
                        ctx.fillRect(x + i * cellSize, y + j * cellSize, cellSize, cellSize);
                    }
                }
            }
        }
    };

    renderWatermark = (ctx, element) => {
        ctx.save();
        ctx.globalAlpha = element.properties.opacity;
        ctx.fillStyle = element.properties.color;
        ctx.font = `${element.properties.fontSize * SCALE_FACTOR}px Arial`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';

        ctx.translate(this.canvasRef.current.width / 2, this.canvasRef.current.height / 2);
        ctx.rotate((element.properties.rotation * Math.PI) / 180);
        ctx.fillText(element.properties.text, 0, 0);

        ctx.restore();
    };

    renderMicrotext = (ctx, element, x, y) => {
        ctx.save();
        ctx.fillStyle = element.properties.color;
        ctx.font = `${element.properties.fontSize * SCALE_FACTOR}px Arial`;
        ctx.textAlign = 'left';
        ctx.textBaseline = 'top';

        if (element.properties.footerText1) {
            ctx.fillText(element.properties.footerText1, x, y);
        }
        if (element.properties.footerText2) {
            ctx.fillText(element.properties.footerText2, x, y + (element.properties.fontSize * SCALE_FACTOR) + 2);
        }

        ctx.restore();
    };

    renderSecurityBorder = (ctx, element, x, y) => {
        ctx.save();
        ctx.strokeStyle = element.properties.color;
        ctx.lineWidth = element.properties.width * SCALE_FACTOR;

        // Dessiner une bordure autour de toute la carte
        const width = this.canvasRef.current.width;
        const height = this.canvasRef.current.height;

        ctx.strokeRect(0, 0, width, height);

        // Si un pattern est défini, on peut ajouter du texte répété sur la bordure
        if (element.properties.pattern) {
            ctx.font = `${8 * SCALE_FACTOR}px Arial`;
            ctx.fillStyle = element.properties.color;
            ctx.textAlign = 'center';

            // Texte sur le bord supérieur
            const patternText = element.properties.pattern;
            const textWidth = ctx.measureText(patternText).width;
            const spacing = textWidth + 20;

            for (let i = 0; i < width; i += spacing) {
                ctx.fillText(patternText, i, 10 * SCALE_FACTOR);
            }
        }

        ctx.restore();
    };

    // Rendu des formes géométriques (rectangle, cercle, ligne)
    renderShape = (ctx, element, x, y) => {
        const width = element.position.width * SCALE_FACTOR;
        const height = element.position.height * SCALE_FACTOR;

        ctx.save();

        switch (element.subType) {
            case 'rectangle':
                // Dessiner le rectangle avec fill
                if (element.properties.fillColor && element.properties.fillColor !== 'transparent') {
                    ctx.fillStyle = element.properties.fillColor;
                    if (element.properties.cornerRadius) {
                        this.drawRoundedRect(ctx, x, y, width, height, element.properties.cornerRadius * SCALE_FACTOR);
                        ctx.fill();
                    } else {
                        ctx.fillRect(x, y, width, height);
                    }
                }

                // Dessiner la bordure
                if (element.properties.borderWidth > 0 && element.properties.borderColor !== 'transparent') {
                    ctx.strokeStyle = element.properties.borderColor;
                    ctx.lineWidth = element.properties.borderWidth * SCALE_FACTOR;
                    if (element.properties.cornerRadius) {
                        this.drawRoundedRect(ctx, x, y, width, height, element.properties.cornerRadius * SCALE_FACTOR);
                        ctx.stroke();
                    } else {
                        ctx.strokeRect(x, y, width, height);
                    }
                }
                break;

            case 'circle':
                const radius = Math.min(width, height) / 2;
                const centerX = x + width / 2;
                const centerY = y + height / 2;

                ctx.beginPath();
                ctx.arc(centerX, centerY, radius, 0, 2 * Math.PI);

                if (element.properties.fillColor && element.properties.fillColor !== 'transparent') {
                    ctx.fillStyle = element.properties.fillColor;
                    ctx.fill();
                }

                if (element.properties.borderWidth > 0 && element.properties.borderColor !== 'transparent') {
                    ctx.strokeStyle = element.properties.borderColor;
                    ctx.lineWidth = element.properties.borderWidth * SCALE_FACTOR;
                    ctx.stroke();
                }
                break;

            case 'line':
                ctx.strokeStyle = element.properties.borderColor || '#000000';
                ctx.lineWidth = element.properties.borderWidth * SCALE_FACTOR || 1;
                ctx.beginPath();
                ctx.moveTo(x, y);
                ctx.lineTo(x + width, y + height);
                ctx.stroke();
                break;

            case 'ellipse':
                const ellipseCenterX = x + width / 2;
                const ellipseCenterY = y + height / 2;
                const radiusX = width / 2;
                const radiusY = height / 2;

                ctx.beginPath();
                ctx.ellipse(ellipseCenterX, ellipseCenterY, radiusX, radiusY, 0, 0, 2 * Math.PI);

                if (element.properties.fillColor && element.properties.fillColor !== 'transparent') {
                    ctx.fillStyle = element.properties.fillColor;
                    ctx.fill();
                }

                if (element.properties.borderWidth > 0 && element.properties.borderColor !== 'transparent') {
                    ctx.strokeStyle = element.properties.borderColor;
                    ctx.lineWidth = element.properties.borderWidth * SCALE_FACTOR;
                    ctx.stroke();
                }
                break;

            case 'arc':
                // Arc shape for curved headers/sections
                // Properties: arcHeight (depth of curve), arcDirection ('down' or 'up')
                const arcHeight = element.properties.arcHeight || 50;
                const arcDirection = element.properties.arcDirection || 'down';

                ctx.beginPath();
                ctx.moveTo(x, y);
                ctx.lineTo(x + width, y);

                if (arcDirection === 'down') {
                    // Curve going down (for headers)
                    const controlY = y + arcHeight;
                    ctx.quadraticCurveTo(x + width / 2, controlY, x, y);
                } else {
                    // Curve going up (for footers)
                    const controlY = y + height - arcHeight;
                    ctx.lineTo(x + width, y + height);
                    ctx.quadraticCurveTo(x + width / 2, controlY, x, y + height);
                    ctx.lineTo(x, y);
                }

                ctx.closePath();

                if (element.properties.fillColor && element.properties.fillColor !== 'transparent') {
                    ctx.fillStyle = element.properties.fillColor;
                    ctx.fill();
                }

                if (element.properties.borderWidth > 0 && element.properties.borderColor !== 'transparent') {
                    ctx.strokeStyle = element.properties.borderColor;
                    ctx.lineWidth = element.properties.borderWidth * SCALE_FACTOR;
                    ctx.stroke();
                }
                break;

            case 'path':
                // SVG Path support using Path2D API
                if (element.properties.d) {
                    try {
                        const path = new Path2D(element.properties.d);

                        // Apply transformation for position
                        ctx.translate(x, y);

                        // Apply scale if provided
                        if (element.properties.scale) {
                            ctx.scale(element.properties.scale, element.properties.scale);
                        }

                        // Fill
                        if (element.properties.fillColor && element.properties.fillColor !== 'transparent') {
                            ctx.fillStyle = element.properties.fillColor;
                            ctx.fill(path);
                        }

                        // Stroke
                        if (element.properties.borderWidth > 0 && element.properties.borderColor !== 'transparent') {
                            ctx.strokeStyle = element.properties.borderColor;
                            ctx.lineWidth = element.properties.borderWidth * SCALE_FACTOR;
                            ctx.stroke(path);
                        }
                    } catch (error) {
                        console.error('Error rendering SVG path:', error);
                        // Fallback: draw a rectangle
                        ctx.fillStyle = element.properties.fillColor || '#CCCCCC';
                        ctx.fillRect(x, y, width, height);
                    }
                }
                break;

            default:
                break;
        }

        ctx.restore();
    };

    // Helper pour dessiner un rectangle avec coins arrondis
    drawRoundedRect = (ctx, x, y, width, height, radius) => {
        ctx.beginPath();
        ctx.moveTo(x + radius, y);
        ctx.lineTo(x + width - radius, y);
        ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
        ctx.lineTo(x + width, y + height - radius);
        ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
        ctx.lineTo(x + radius, y + height);
        ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
        ctx.lineTo(x, y + radius);
        ctx.quadraticCurveTo(x, y, x + radius, y);
        ctx.closePath();
    };

    // Rendu des images (signature, etc.)
    renderImage = (ctx, element, x, y) => {
        const width = element.position.width * SCALE_FACTOR;
        const height = element.position.height * SCALE_FACTOR;

        // Placeholder pour l'image
        ctx.fillStyle = '#f0f0f0';
        ctx.fillRect(x, y, width, height);
        ctx.strokeStyle = '#cccccc';
        ctx.lineWidth = 1;
        ctx.strokeRect(x, y, width, height);

        // Texte indicatif
        ctx.fillStyle = '#999999';
        ctx.font = `${12 * SCALE_FACTOR}px Arial`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText('Signature', x + width / 2, y + height / 2);

        // TODO: Charger et afficher l'image réelle si imageUrl existe
        if (element.properties.imageUrl) {
            // Implémenter le chargement d'image ici
        }
    };

    // Rendu des barcodes
    renderBarcode = (ctx, element, x, y) => {
        const width = element.position.width * SCALE_FACTOR;
        const height = element.position.height * SCALE_FACTOR;

        // Remplacer les variables dans les données du barcode
        let displayData = element.properties.data || 'BARCODE';
        const variablePattern = /\{\{(\w+)\}\}/g;
        displayData = displayData.replace(variablePattern, (match, varName) => {
            const exampleValues = {
                'barcodeData': 'SCI 00226',
                'souscripteur': 'STREET CHILD INTERNATIONAL (S.C.I)',
                'numeroBeneficiaire': '00226',
                'lienParente': 'EMPLOYEE'
            };
            return exampleValues[varName] || varName;
        });

        // Dessiner les barres du code-barres
        ctx.fillStyle = '#000000';
        const barCount = 30;
        const barWidth = width / barCount;
        const barcodeHeight = element.properties.showText ? height * 0.7 : height;

        for (let i = 0; i < barCount; i++) {
            if (i % 2 === 0) {
                ctx.fillRect(x + i * barWidth, y, barWidth * 0.8, barcodeHeight);
            }
        }

        // Afficher le texte si activé
        if (element.properties.showText !== false) {
            ctx.fillStyle = element.properties.textColor || '#000000';
            ctx.font = `${(element.properties.textSize || 12) * SCALE_FACTOR}px Arial`;
            ctx.textAlign = 'center';
            ctx.textBaseline = 'top';
            ctx.fillText(displayData, x + width / 2, y + barcodeHeight + 2 * SCALE_FACTOR);
        }
    };

    renderFormField = (ctx, element, x, y) => {
        const width = element.position.width * SCALE_FACTOR;
        const height = element.position.height * SCALE_FACTOR;
        const props = element.properties;

        // Draw background
        ctx.fillStyle = props.backgroundColor || '#FFFFFF';
        ctx.fillRect(x, y, width, height);

        // Draw border
        ctx.strokeStyle = props.borderColor || '#CCCCCC';
        ctx.lineWidth = (props.borderWidth || 1) * SCALE_FACTOR;
        ctx.strokeRect(x, y, width, height);

        // Draw placeholder text
        ctx.fillStyle = props.color || '#000000';
        ctx.font = `${props.fontWeight || 'normal'} ${(props.fontSize || 12) * SCALE_FACTOR}px ${props.fontFamily || 'Arial'}`;
        ctx.textAlign = 'left';
        ctx.textBaseline = 'middle';

        const padding = 5 * SCALE_FACTOR;
        let displayText = props.placeholder || props.label || '';

        // Replace variables with example values
        const variablePattern = /\{\{(\w+)\}\}/g;
        displayText = displayText.replace(variablePattern, (match, varName) => {
            const exampleValues = {
                'FIELD_VALUE': 'Exemple',
                'DATE_VALUE': '01/01/2024',
                'SELECT_VALUE': 'Option 1'
            };
            return exampleValues[varName] || varName;
        });

        // Add icon based on subType
        if (element.subType === 'date') {
            // Draw calendar icon
            ctx.save();
            ctx.font = `${12 * SCALE_FACTOR}px 'primeicons'`;
            ctx.fillText('\uE927', x + width - 20 * SCALE_FACTOR, y + height / 2); // calendar icon
            ctx.restore();
            ctx.font = `${props.fontWeight || 'normal'} ${(props.fontSize || 12) * SCALE_FACTOR}px ${props.fontFamily || 'Arial'}`;
        } else if (element.subType === 'select') {
            // Draw dropdown arrow
            ctx.save();
            ctx.beginPath();
            ctx.moveTo(x + width - 15 * SCALE_FACTOR, y + height / 2 - 3 * SCALE_FACTOR);
            ctx.lineTo(x + width - 10 * SCALE_FACTOR, y + height / 2 + 3 * SCALE_FACTOR);
            ctx.lineTo(x + width - 5 * SCALE_FACTOR, y + height / 2 - 3 * SCALE_FACTOR);
            ctx.stroke();
            ctx.restore();
        }

        // Draw text
        ctx.fillText(displayText, x + padding, y + height / 2);
    };

    // ===== VALIDATION =====

    validateTemplate = () => {
        const { elements } = this.state;
        const errors = [];

        elements.forEach(element => {
            if (!element.enabled) return;

            // Valider les positions
            if (element.position.x < VALIDATION_RULES.position.x.min ||
                element.position.x > VALIDATION_RULES.position.x.max) {
                errors.push(`${element.id}: Position X hors limites`);
            }
            if (element.position.y < VALIDATION_RULES.position.y.min ||
                element.position.y > VALIDATION_RULES.position.y.max) {
                errors.push(`${element.id}: Position Y hors limites`);
            }

            // Valider les dimensions si applicables
            if (element.position.width) {
                if (element.position.width < VALIDATION_RULES.position.width.min ||
                    element.position.width > VALIDATION_RULES.position.width.max) {
                    errors.push(`${element.id}: Largeur hors limites`);
                }
            }
            if (element.position.height) {
                if (element.position.height < VALIDATION_RULES.position.height.min ||
                    element.position.height > VALIDATION_RULES.position.height.max) {
                    errors.push(`${element.id}: Hauteur hors limites`);
                }
            }

            // Valider les couleurs
            if (element.properties.color &&
                !VALIDATION_RULES.colors.pattern.test(element.properties.color)) {
                errors.push(`${element.id}: Format de couleur invalide`);
            }
        });

        this.setState({ validationErrors: errors });
        return errors.length === 0;
    };

    // ===== PREVIEW =====

    generatePreview = async () => {
        try {
            this.setState({ isPreviewLoading: true });

            // Use base template ID, default to 'TEMPLATE_CM_001' if not available
            const baseTemplateId = this.props.template?.id || 'TEMPLATE_CM_001';

            // Build TemplateParameters for preview
            const templateParameters = this.buildTemplateParameters();




            console.log('Elements for current side:',
                templateParameters.Elements?.filter(e => e.Side === this.state.currentSide).length || 0);

            const previewPayload = {
                CustomParameters: templateParameters,
                TestData: null, // Use default test data from backend
                Side: this.state.currentSide // 'front' or 'back'
            };



            // Call the preview-custom API endpoint
            const response = await fetch(
                `${process.env.REACT_APP_BASEURL_CLIENT_BACKEND}/api/card/templates/${baseTemplateId}/preview-custom`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(previewPayload)
                }
            );

            if (response.ok) {
                // Get the image blob from response
                const blob = await response.blob();
                const previewUrl = URL.createObjectURL(blob);
                this.setState({ previewUrl: previewUrl });
                this.showToast('success', 'Aperçu', 'Aperçu généré avec succès');
            } else {
                // Fallback to canvas preview if API fails
                const canvasDataUrl = this.canvasRef.current.toDataURL('image/png');
                this.setState({ previewUrl: canvasDataUrl });
                console.error('Preview API failed, using canvas fallback');
                this.showToast('warning', 'Aperçu', 'Utilisation de l\'aperçu local');
            }
        } catch (error) {
            console.error('Erreur lors de la génération de l\'aperçu:', error);
            this.showToast('error', 'Erreur', 'Erreur lors de la génération de l\'aperçu');

            // En cas d'erreur, utiliser le canvas local comme fallback
            try {
                const canvasDataUrl = this.canvasRef.current.toDataURL('image/png');
                this.setState({ previewUrl: canvasDataUrl });
            } catch (canvasError) {
                console.error('Erreur canvas fallback:', canvasError);
            }
        } finally {
            this.setState({ isPreviewLoading: false });
        }
    };

    // ===== INTERFACE UTILISATEUR =====

    renderTemplateGallery = () => {
        const templates = this.getTemplateDefinitions();
        const { showTemplateGallery } = this.state;

        return (
            <Dialog
                header="Galerie de Templates"
                visible={showTemplateGallery}
                style={{ width: '70vw' }}
                onHide={() => this.setState({ showTemplateGallery: false })}
                modal
            >
                <div style={{ padding: '20px' }}>
                    <p style={{ marginBottom: '24px', color: '#6c757d' }}>
                        Choisissez un template pré-designé pour commencer rapidement, puis personnalisez-le selon vos besoins.
                    </p>

                    <Row>
                        {Object.entries(templates).map(([key, template]) => (
                            <Col md={6} key={key} style={{ marginBottom: '20px' }}>
                                <Card
                                    style={{
                                        border: '2px solid #e0e0e0',
                                        borderRadius: '8px',
                                        transition: 'all 0.3s',
                                        cursor: 'pointer',
                                        height: '100%'
                                    }}
                                    className="template-card"
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.borderColor = '#667eea';
                                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.2)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.borderColor = '#e0e0e0';
                                        e.currentTarget.style.boxShadow = 'none';
                                    }}
                                    onClick={() => this.loadTemplate(key)}
                                >
                                    <div style={{ padding: '20px' }}>
                                        {/* Template Preview */}
                                        <div
                                            style={{
                                                width: '100%',
                                                height: '180px',
                                                background: key === 'rdc' ? 'linear-gradient(135deg, #2E5C9A 0%, #7AB436 100%)' :
                                                            key === 'civ' ? 'linear-gradient(135deg, #2E5C9A 0%, #F5F5F5 100%)' :
                                                            key === 'civv2' ? 'linear-gradient(135deg, #2E5C9A 0%, #FFFFFF 100%)' :
                                                            'linear-gradient(135deg, #667EEA 0%, #764BA2 100%)',
                                                borderRadius: '8px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                marginBottom: '16px',
                                                position: 'relative',
                                                overflow: 'hidden'
                                            }}
                                        >
                                            {key === 'rdc' ? (
                                                <div style={{
                                                    color: 'white',
                                                    textAlign: 'center',
                                                    position: 'relative',
                                                    zIndex: 2
                                                }}>
                                                    <i className="pi pi-id-card" style={{ fontSize: '48px', marginBottom: '8px' }}></i>
                                                    <div style={{ fontSize: '14px', fontWeight: 'bold' }}>RDC Template</div>
                                                    <div style={{ fontSize: '10px', opacity: 0.8 }}>22 éléments</div>
                                                </div>
                                            ) : key === 'civ' ? (
                                                <div style={{
                                                    color: 'white',
                                                    textAlign: 'center',
                                                    position: 'relative',
                                                    zIndex: 2
                                                }}>
                                                    <i className="pi pi-id-card" style={{ fontSize: '48px', marginBottom: '8px' }}></i>
                                                    <div style={{ fontSize: '14px', fontWeight: 'bold' }}>CIV Template</div>
                                                    <div style={{ fontSize: '10px', opacity: 0.8 }}>14 éléments</div>
                                                </div>
                                            ) : key === 'civv2' ? (
                                                <div style={{
                                                    color: '#2E5C9A',
                                                    textAlign: 'center',
                                                    position: 'relative',
                                                    zIndex: 2
                                                }}>
                                                    <i className="pi pi-id-card" style={{ fontSize: '48px', marginBottom: '8px' }}></i>
                                                    <div style={{ fontSize: '14px', fontWeight: 'bold' }}>CIV V2 Template</div>
                                                    <div style={{ fontSize: '10px', opacity: 0.8 }}>17 éléments</div>
                                                </div>
                                            ) : (
                                                <div style={{
                                                    color: 'white',
                                                    textAlign: 'center'
                                                }}>
                                                    <i className="pi pi-plus-circle" style={{ fontSize: '48px', marginBottom: '8px' }}></i>
                                                    <div style={{ fontSize: '14px', fontWeight: 'bold' }}>Template Vierge</div>
                                                    <div style={{ fontSize: '10px', opacity: 0.8 }}>Commencer de zéro</div>
                                                </div>
                                            )}
                                        </div>

                                        {/* Template Info */}
                                        <h5 style={{
                                            fontSize: '18px',
                                            fontWeight: 'bold',
                                            marginBottom: '8px',
                                            color: '#2c3e50'
                                        }}>
                                            {template.name}
                                        </h5>
                                        <p style={{
                                            fontSize: '13px',
                                            color: '#6c757d',
                                            marginBottom: '16px',
                                            minHeight: '40px'
                                        }}>
                                            {template.description}
                                        </p>

                                        {/* Load Button */}
                                        <Button
                                            label="Utiliser ce template"
                                            icon="pi pi-check"
                                            className="p-button-sm"
                                            style={{
                                                width: '100%',
                                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                                border: 'none'
                                            }}
                                        />
                                    </div>
                                </Card>
                            </Col>
                        ))}
                    </Row>
                </div>
            </Dialog>
        );
    };

    renderToolbar = () => {
        const { t } = this.props;
        const { historyIndex, history } = this.state;
        const canUndo = historyIndex > 0;
        const canRedo = historyIndex < history.length - 1;

        return (
            <div className="editor-toolbar" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <Button
                        label={t('templateEditor.toolbar.grid')}
                        icon={`pi ${this.state.gridEnabled ? 'pi-check' : 'pi-times'}`}
                        className="p-button-text"
                        onClick={() => this.setState({ gridEnabled: !this.state.gridEnabled })}
                    />
                    <Button
                        label={t('templateEditor.toolbar.snap')}
                        icon={`pi ${this.state.snapToGrid ? 'pi-check' : 'pi-times'}`}
                        className="p-button-text"
                        onClick={() => this.setState({ snapToGrid: !this.state.snapToGrid })}
                    />
                    <Button
                        label={t('templateEditor.toolbar.front')}
                        className={this.state.currentSide === 'front' ? 'p-button-primary' : 'p-button-text'}
                        onClick={() => this.setState({ currentSide: 'front' })}
                    />
                    <Button
                        label={t('templateEditor.toolbar.back')}
                        className={this.state.currentSide === 'back' ? 'p-button-primary' : 'p-button-text'}
                        onClick={() => this.setState({ currentSide: 'back' })}
                    />
                </div>

                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <Button
                        label="Templates"
                        icon="pi pi-th-large"
                        className="p-button-info p-button-outlined"
                        onClick={() => this.setState({ showTemplateGallery: true })}
                        tooltip="Charger un template pré-designé"
                        tooltipOptions={{ position: 'bottom' }}
                    />
                    <Button
                        icon="pi pi-undo"
                        className="p-button-text"
                        onClick={this.undo}
                        disabled={!canUndo}
                        tooltip="Annuler (Ctrl+Z)"
                        tooltipOptions={{ position: 'bottom' }}
                    />
                    <Button
                        icon="pi pi-redo"
                        className="p-button-text"
                        onClick={this.redo}
                        disabled={!canRedo}
                        tooltip="Rétablir (Ctrl+Y)"
                        tooltipOptions={{ position: 'bottom' }}
                    />
                    <Button
                        label={t('templateEditor.toolbar.preview')}
                        icon="pi pi-eye"
                        className="p-button-outlined"
                        onClick={() => {
                            this.generatePreview();
                            this.setState({ showPreviewModal: true });
                        }}
                        loading={this.state.isPreviewLoading}
                    />
                    <Button
                        label={t('templateEditor.toolbar.return')}
                        icon="pi pi-arrow-left"
                        className="p-button-outlined"
                        onClick={this.props.onClose}
                    />
                    <Button
                        label={t('templateEditor.toolbar.save')}
                        icon="pi pi-save"
                        className="p-button-success"
                        onClick={this.saveTemplate}
                        disabled={!this.state.isDirty}
                    />
                </div>
            </div>
        );
    };

    renderElementsList = () => {
        const { elements, selectedElement } = this.state;
        const { t } = this.props;

        return (
            <Panel header={t('templateEditor.panels.elements')} className="elements-panel">
                <div className="elements-list">
                    {elements.map(element => (
                        <div
                            key={element.id}
                            className={`element-item ${selectedElement?.id === element.id ? 'selected' : ''} ${!element.enabled ? 'disabled' : ''}`}
                            onClick={() => this.setState({ selectedElement: element })}
                            draggable={element.enabled}
                            onDragStart={(e) => this.handleDragStart(e, element)}
                            onDragEnd={this.handleDragEnd}
                            style={{ position: 'relative' }}
                        >
                            <Checkbox
                                checked={element.enabled}
                                onChange={(e) => {
                                    const updatedElements = elements.map(el =>
                                        el.id === element.id ? { ...el, enabled: e.checked } : el
                                    );
                                    this.setState({ elements: updatedElements });
                                }}
                                onClick={(e) => e.stopPropagation()}
                            />
                            <i className={this.getElementIcon(element.type)} />
                            <span style={{ flex: 1 }}>{element.id}</span>
                            <Button
                                icon="pi pi-trash"
                                className="p-button-rounded p-button-danger p-button-text p-button-sm"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    this.deleteElement(element.id);
                                }}
                                tooltip="Supprimer"
                                tooltipOptions={{ position: 'left' }}
                                style={{ padding: '0.25rem', width: '1.5rem', height: '1.5rem' }}
                            />
                        </div>
                    ))}
                </div>
            </Panel>
        );
    };

    getElementIcon = (type) => {
        const icons = {
            logo: 'pi pi-image',
            photo: 'pi pi-user',
            text: 'pi pi-align-left',
            qrcode: 'pi pi-qrcode',
            security: 'pi pi-shield',
            formField: 'pi pi-list',
            shape: 'pi pi-stop',
            barcode: 'pi pi-bars',
            image: 'pi pi-pencil'
        };
        return icons[type] || 'pi pi-circle';
    };

    renderPropertiesPanel = () => {
        const { selectedElement, selectedElements, groups } = this.state;
        const { t } = this.props;

        if (!selectedElement && selectedElements.length === 0) {
            return (
                <div style={{
                    padding: '24px',
                    textAlign: 'center',
                    color: '#6c757d',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}>
                    <i className="pi pi-info-circle" style={{ fontSize: '48px', color: '#dee2e6', marginBottom: '16px' }}></i>
                    <p style={{ margin: 0, fontSize: '14px', fontWeight: 500 }}>
                        {t('templateEditor.properties.selectElement')}
                    </p>
                    <p style={{ margin: '8px 0 0 0', fontSize: '12px', color: '#adb5bd' }}>
                        Cliquez sur un élément pour modifier ses propriétés
                    </p>
                </div>
            );
        }

        // Check if selected element is in a group
        const elementGroup = selectedElement ? this.getElementGroup(selectedElement.id) : null;

        return (
            <div className="p-fluid" style={{
                padding: '0',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                background: '#f8f9fa'
            }}>
                {/* Element Header */}
                {selectedElement && (
                    <div style={{
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        padding: '16px',
                        color: 'white',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <div style={{
                                width: '40px',
                                height: '40px',
                                background: 'rgba(255,255,255,0.2)',
                                borderRadius: '8px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}>
                                <i className={this.getElementIcon(selectedElement.type)} style={{ fontSize: '20px' }}></i>
                            </div>
                            <div style={{ flex: 1 }}>
                                <div style={{ fontSize: '14px', fontWeight: 600, marginBottom: '2px' }}>
                                    {this.getElementTypeName(selectedElement.type)}
                                </div>
                                <div style={{ fontSize: '11px', opacity: 0.9 }}>
                                    {selectedElement.id}
                                </div>
                            </div>
                            {elementGroup && (
                                <div style={{
                                    background: 'rgba(255,255,255,0.2)',
                                    padding: '4px 8px',
                                    borderRadius: '4px',
                                    fontSize: '10px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '4px'
                                }}>
                                    <i className="pi pi-link"></i>
                                    <span>{elementGroup.name}</span>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Scrollable Content */}
                <div style={{
                    flex: 1,
                    overflowY: 'auto',
                    padding: '12px'
                }}>
                    {selectedElement && this.renderPropertySections(selectedElement)}
                </div>
            </div>
        );
    };

    getElementTypeName = (type) => {
        const names = {
            text: 'Texte',
            shape: 'Forme',
            image: 'Image',
            logo: 'Logo',
            photo: 'Photo',
            qrcode: 'QR Code',
            security: 'Sécurité',
            formField: 'Champ de formulaire',
            barcode: 'Code-barres'
        };
        return names[type] || type;
    };

    renderPropertySections = (selectedElement) => {
        const { t } = this.props;
        const { selectedElements } = this.state;

        const PropertyCard = ({ title, icon, children }) => (
            <div style={{
                background: 'white',
                borderRadius: '8px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                overflow: 'hidden'
            }}>
                <div style={{
                    padding: '12px 16px',
                    borderBottom: '1px solid #e9ecef',
                    background: '#f8f9fa',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                }}>
                    <i className={icon} style={{ color: '#667eea', fontSize: '14px' }}></i>
                    <span style={{ fontSize: '13px', fontWeight: 600, color: '#495057' }}>{title}</span>
                </div>
                <div style={{ padding: '16px' }}>
                    {children}
                </div>
            </div>
        );

        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {/* Transform Section */}
                <PropertyCard title="Transform & Position" icon="pi pi-arrows-alt">
                    <div className="field" style={{ marginBottom: '12px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <ScrubbyLabel
                                value={selectedElement.position.x}
                                min={VALIDATION_RULES.position.x.min}
                                max={VALIDATION_RULES.position.x.max}
                                step={1}
                                onChange={(newValue) => this.updateElementProperty(selectedElement.id, 'position.x', newValue)}
                                icon="pi pi-arrow-right"
                            >
                                X Position
                            </ScrubbyLabel>
                            <InputNumber
                                value={selectedElement.position.x}
                                onChange={(e) => this.updateElementProperty(selectedElement.id, 'position.x', e.value)}
                                onWheel={(e) => {
                                    const input = e.currentTarget.querySelector('input');
                                    if (input && input === document.activeElement) {
                                        e.preventDefault();
                                        const delta = Math.sign(e.deltaY) * -1;
                                        const currentValue = selectedElement.position.x || 0;
                                        const step = e.shiftKey ? 10 : 1;
                                        const newValue = Math.max(VALIDATION_RULES.position.x.min, Math.min(VALIDATION_RULES.position.x.max, currentValue + (delta * step)));
                                        this.updateElementProperty(selectedElement.id, 'position.x', newValue);
                                    }
                                }}
                                min={VALIDATION_RULES.position.x.min}
                                max={VALIDATION_RULES.position.x.max}
                                inputStyle={{ height: '28px', fontSize: '11px', padding: '4px 8px', textAlign: 'right', borderRadius: '4px' }}
                                style={{ width: '70px' }}
                                suffix=" px"
                            />
                        </div>
                    </div>

                    <div className="field" style={{ marginBottom: '12px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <ScrubbyLabel
                                value={selectedElement.position.y}
                                min={VALIDATION_RULES.position.y.min}
                                max={VALIDATION_RULES.position.y.max}
                                step={1}
                                onChange={(newValue) => this.updateElementProperty(selectedElement.id, 'position.y', newValue)}
                                icon="pi pi-arrow-down"
                            >
                                Y Position
                            </ScrubbyLabel>
                            <InputNumber
                                value={selectedElement.position.y}
                                onChange={(e) => this.updateElementProperty(selectedElement.id, 'position.y', e.value)}
                                onWheel={(e) => {
                                    const input = e.currentTarget.querySelector('input');
                                    if (input && input === document.activeElement) {
                                        e.preventDefault();
                                        const delta = Math.sign(e.deltaY) * -1;
                                        const currentValue = selectedElement.position.y || 0;
                                        const step = e.shiftKey ? 10 : 1;
                                        const newValue = Math.max(VALIDATION_RULES.position.y.min, Math.min(VALIDATION_RULES.position.y.max, currentValue + (delta * step)));
                                        this.updateElementProperty(selectedElement.id, 'position.y', newValue);
                                    }
                                }}
                                min={VALIDATION_RULES.position.y.min}
                                max={VALIDATION_RULES.position.y.max}
                                inputStyle={{ height: '28px', fontSize: '11px', padding: '4px 8px', textAlign: 'right', borderRadius: '4px' }}
                                style={{ width: '70px' }}
                                suffix=" px"
                            />
                        </div>
                    </div>

                    {selectedElement.position.width !== undefined && (
                        <>
                            <div style={{ width: '100%', height: '1px', background: '#e9ecef', margin: '12px 0' }}></div>
                            <div className="field" style={{ marginBottom: '12px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <ScrubbyLabel
                                        value={selectedElement.position.width}
                                        min={VALIDATION_RULES.position.width.min}
                                        max={VALIDATION_RULES.position.width.max}
                                        step={1}
                                        onChange={(newValue) => this.updateElementProperty(selectedElement.id, 'position.width', newValue)}
                                        icon="pi pi-arrows-h"
                                    >
                                        Width
                                    </ScrubbyLabel>
                                    <InputNumber
                                        value={selectedElement.position.width}
                                        onChange={(e) => this.updateElementProperty(selectedElement.id, 'position.width', e.value)}
                                        onWheel={(e) => {
                                            const input = e.currentTarget.querySelector('input');
                                            if (input && input === document.activeElement) {
                                                e.preventDefault();
                                                const delta = Math.sign(e.deltaY) * -1;
                                                const currentValue = selectedElement.position.width || VALIDATION_RULES.position.width.min;
                                                const step = e.shiftKey ? 10 : 1;
                                                const newValue = Math.max(VALIDATION_RULES.position.width.min, Math.min(VALIDATION_RULES.position.width.max, currentValue + (delta * step)));
                                                this.updateElementProperty(selectedElement.id, 'position.width', newValue);
                                            }
                                        }}
                                        min={VALIDATION_RULES.position.width.min}
                                        max={VALIDATION_RULES.position.width.max}
                                        inputStyle={{ height: '28px', fontSize: '11px', padding: '4px 8px', textAlign: 'right', borderRadius: '4px' }}
                                        style={{ width: '70px' }}
                                        suffix=" px"
                                    />
                                </div>
                            </div>
                        </>
                    )}

                    {selectedElement.position.height !== undefined && (
                        <div className="field" style={{ marginBottom: 0 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <ScrubbyLabel
                                    value={selectedElement.position.height}
                                    min={VALIDATION_RULES.position.height.min}
                                    max={VALIDATION_RULES.position.height.max}
                                    step={1}
                                    onChange={(newValue) => this.updateElementProperty(selectedElement.id, 'position.height', newValue)}
                                    icon="pi pi-arrows-v"
                                >
                                    Height
                                </ScrubbyLabel>
                                <InputNumber
                                    value={selectedElement.position.height}
                                    onChange={(e) => this.updateElementProperty(selectedElement.id, 'position.height', e.value)}
                                    onWheel={(e) => {
                                        const input = e.currentTarget.querySelector('input');
                                        if (input && input === document.activeElement) {
                                            e.preventDefault();
                                            const delta = Math.sign(e.deltaY) * -1;
                                            const currentValue = selectedElement.position.height || VALIDATION_RULES.position.height.min;
                                            const step = e.shiftKey ? 10 : 1;
                                            const newValue = Math.max(VALIDATION_RULES.position.height.min, Math.min(VALIDATION_RULES.position.height.max, currentValue + (delta * step)));
                                            this.updateElementProperty(selectedElement.id, 'position.height', newValue);
                                        }
                                    }}
                                    min={VALIDATION_RULES.position.height.min}
                                    max={VALIDATION_RULES.position.height.max}
                                    inputStyle={{ height: '28px', fontSize: '11px', padding: '4px 8px', textAlign: 'right', borderRadius: '4px' }}
                                    style={{ width: '70px' }}
                                    suffix=" px"
                                />
                            </div>
                        </div>
                    )}
                </PropertyCard>

                {/* Appearance Section */}
                <PropertyCard title="Appearance" icon="pi pi-palette">
                    {this.renderElementSpecificProperties(selectedElement)}
                </PropertyCard>

                {/* Actions Section */}
                <PropertyCard title="Actions" icon="pi pi-cog">
                    {/* Group/Ungroup section */}
                    {this.state.selectedElements.length > 0 && (
                        <div style={{ marginBottom: '16px', paddingBottom: '16px', borderBottom: '1px solid #e9ecef' }}>
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                marginBottom: '10px'
                            }}>
                                <label style={{
                                    fontSize: '12px',
                                    fontWeight: 600,
                                    color: '#495057',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px'
                                }}>
                                    <i className="pi pi-link" style={{ fontSize: '11px', color: '#9C27B0' }}></i>
                                    Groupement
                                </label>
                                <span style={{
                                    fontSize: '11px',
                                    color: '#6c757d',
                                    background: '#f8f9fa',
                                    padding: '2px 8px',
                                    borderRadius: '12px'
                                }}>
                                    {this.state.selectedElements.length} élém.
                                </span>
                            </div>
                            <div style={{ display: 'flex', gap: '8px' }}>
                                <Button
                                    icon="pi pi-link"
                                    label="Grouper"
                                    className="p-button-outlined p-button-sm"
                                    onClick={() => this.createGroup()}
                                    tooltip="Grouper les éléments sélectionnés (Ctrl+G)"
                                    tooltipOptions={{ position: 'bottom' }}
                                    style={{ fontSize: '11px', flex: 1 }}
                                />
                                <Button
                                    icon="pi pi-ban"
                                    label="Dégrouper"
                                    className="p-button-outlined p-button-sm"
                                    onClick={() => this.ungroupElements()}
                                    tooltip="Dégrouper (Ctrl+Shift+G)"
                                    tooltipOptions={{ position: 'bottom' }}
                                    style={{ fontSize: '11px', flex: 1 }}
                                />
                            </div>
                        </div>
                    )}

                    {/* Copy/Paste/Duplicate actions */}
                    <div style={{ marginBottom: '16px', paddingBottom: '16px', borderBottom: '1px solid #e9ecef' }}>
                        <label style={{
                            fontSize: '12px',
                            fontWeight: 600,
                            color: '#495057',
                            marginBottom: '10px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px'
                        }}>
                            <i className="pi pi-copy" style={{ fontSize: '11px', color: '#667eea' }}></i>
                            Édition
                        </label>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' }}>
                            <Button
                                icon="pi pi-copy"
                                className="p-button-outlined p-button-sm"
                                onClick={() => this.copyElement()}
                                tooltip="Copier (Ctrl+C)"
                                tooltipOptions={{ position: 'top' }}
                                style={{
                                    height: '36px',
                                    justifyContent: 'center',
                                    fontSize: '16px'
                                }}
                            />
                            <Button
                                icon="pi pi-clone"
                                className="p-button-outlined p-button-sm"
                                onClick={() => this.pasteElement()}
                                tooltip="Coller (Ctrl+V)"
                                tooltipOptions={{ position: 'top' }}
                                style={{
                                    height: '36px',
                                    justifyContent: 'center',
                                    fontSize: '16px'
                                }}
                            />
                            <Button
                                icon="pi pi-plus"
                                className="p-button-outlined p-button-sm"
                                onClick={() => this.duplicateElement()}
                                tooltip="Dupliquer (Ctrl+D)"
                                tooltipOptions={{ position: 'top' }}
                                style={{
                                    height: '36px',
                                    justifyContent: 'center',
                                    fontSize: '16px'
                                }}
                            />
                        </div>
                    </div>

                    {/* Z-Index controls */}
                    <div style={{ marginBottom: '16px', paddingBottom: '16px', borderBottom: '1px solid #e9ecef' }}>
                        <label style={{
                            fontSize: '12px',
                            fontWeight: 600,
                            color: '#495057',
                            marginBottom: '10px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px'
                        }}>
                            <i className="pi pi-sort-alt" style={{ fontSize: '11px', color: '#667eea' }}></i>
                            Ordre des calques
                        </label>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '6px' }}>
                            <Button
                                icon="pi pi-angle-double-up"
                                className="p-button-outlined p-button-sm"
                                onClick={() => this.moveElementToFront(selectedElement.id)}
                                tooltip="Premier plan"
                                tooltipOptions={{ position: 'top' }}
                                style={{
                                    height: '36px',
                                    justifyContent: 'center',
                                    fontSize: '14px'
                                }}
                            />
                            <Button
                                icon="pi pi-angle-up"
                                className="p-button-outlined p-button-sm"
                                onClick={() => this.moveElementUp(selectedElement.id)}
                                tooltip="Avancer"
                                tooltipOptions={{ position: 'top' }}
                                style={{
                                    height: '36px',
                                    justifyContent: 'center',
                                    fontSize: '14px'
                                }}
                            />
                            <Button
                                icon="pi pi-angle-down"
                                className="p-button-outlined p-button-sm"
                                onClick={() => this.moveElementDown(selectedElement.id)}
                                tooltip="Reculer"
                                tooltipOptions={{ position: 'top' }}
                                style={{
                                    height: '36px',
                                    justifyContent: 'center',
                                    fontSize: '14px'
                                }}
                            />
                            <Button
                                icon="pi pi-angle-double-down"
                                className="p-button-outlined p-button-sm"
                                onClick={() => this.moveElementToBack(selectedElement.id)}
                                tooltip="Arrière-plan"
                                tooltipOptions={{ position: 'top' }}
                                style={{
                                    height: '36px',
                                    justifyContent: 'center',
                                    fontSize: '14px'
                                }}
                            />
                        </div>
                    </div>

                    {/* Delete and deselect */}
                    <div>
                        <label style={{
                            fontSize: '12px',
                            fontWeight: 600,
                            color: '#495057',
                            marginBottom: '10px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px'
                        }}>
                            <i className="pi pi-trash" style={{ fontSize: '11px', color: '#dc3545' }}></i>
                            Gestion
                        </label>
                        <div style={{ display: 'flex', gap: '8px' }}>
                            <Button
                                icon="pi pi-trash"
                                label="Supprimer"
                                className="p-button-danger p-button-sm"
                                onClick={() => this.deleteElement(selectedElement.id)}
                                tooltip={t('templateEditor.tooltips.delete')}
                                tooltipOptions={{ position: 'top' }}
                                style={{ flex: 1, justifyContent: 'center', height: '36px', fontSize: '12px' }}
                            />
                            <Button
                                icon="pi pi-times"
                                label="Désélectionner"
                                className="p-button-secondary p-button-sm"
                                onClick={() => this.clearSelection()}
                                tooltip={t('templateEditor.tooltips.deselect')}
                                tooltipOptions={{ position: 'top' }}
                                style={{ flex: 1, justifyContent: 'center', height: '36px', fontSize: '12px' }}
                            />
                        </div>
                    </div>
                </PropertyCard>
            </div>
        );
    };

    renderElementSpecificProperties = (element) => {
        const { t } = this.props;

        // Get fresh element from state to ensure we have the latest values
        const currentElement = this.state.elements.find(el => el.id === element.id) || element;

        switch (currentElement.type) {
            case 'text':
                return (
                    <>
                        <div className="field">
                            <label>{t('templateEditor.properties.text')}</label>
                            <InputTextarea
                                value={this.tempTextValues[currentElement.id] !== undefined
                                    ? this.tempTextValues[currentElement.id]
                                    : (currentElement.properties.text || '')}
                                onChange={(e) => {
                                    const newValue = e.target.value;
                                    const elementId = currentElement.id;

                                    // Store value temporarily to maintain input state
                                    this.tempTextValues[elementId] = newValue;

                                    // Clear existing debounce timer
                                    if (this.textInputDebounceTimer) {
                                        clearTimeout(this.textInputDebounceTimer);
                                    }

                                    // Update state after user stops typing for 300ms
                                    this.textInputDebounceTimer = setTimeout(() => {
                                        console.log('[TEXT CHANGE]', newValue);
                                        this.updateElementProperty(elementId, 'properties.text', newValue);
                                        delete this.tempTextValues[elementId];
                                    }, 300);

                                    // Force re-render to show typed character immediately
                                    this.forceUpdate();
                                }}
                                rows={3}
                                autoResize
                                placeholder="Saisir le texte ou {{variable}}...&#10;Appuyez sur Entrée pour nouvelle ligne"
                                style={{ width: '100%', fontSize: '12px', minHeight: '60px' }}
                            />
                            <small style={{ color: '#666', display: 'block', marginTop: '4px' }}>
                                <span style={{ cursor: 'pointer', color: '#007bff', textDecoration: 'underline' }}
                                      onClick={() => this.setState({ showVariablesHelp: !this.state.showVariablesHelp })}>
                                    {this.state.showVariablesHelp ? '▼' : '▶'} Variables disponibles
                                </span>
                            </small>
                            {this.state.showVariablesHelp && (
                                <div style={{ marginTop: '8px', padding: '8px', background: '#f8f9fa', borderRadius: '4px', fontSize: '11px', border: '1px solid #dee2e6' }}>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '3px', color: '#6c757d' }}>
                                        <div style={{ cursor: 'pointer', padding: '2px 4px', borderRadius: '2px', ':hover': { background: '#e9ecef' } }}
                                             onClick={() => this.updateElementProperty(currentElement.id, 'properties.text', (currentElement.properties.text || '') + '{{nomComplet}}')}>
                                            <code style={{ color: '#d63384', fontSize: '10px' }}>{'{{nomComplet}}'}</code> - Nom complet
                                        </div>
                                        <div style={{ cursor: 'pointer', padding: '2px 4px', borderRadius: '2px' }}
                                             onClick={() => this.updateElementProperty(currentElement.id, 'properties.text', (currentElement.properties.text || '') + '{{souscripteur}}')}>
                                            <code style={{ color: '#d63384', fontSize: '10px' }}>{'{{souscripteur}}'}</code> - Souscripteur
                                        </div>
                                        <div style={{ cursor: 'pointer', padding: '2px 4px', borderRadius: '2px' }}
                                             onClick={() => this.updateElementProperty(currentElement.id, 'properties.text', (currentElement.properties.text || '') + '{{lienParente}}')}>
                                            <code style={{ color: '#d63384', fontSize: '10px' }}>{'{{lienParente}}'}</code> - Lien parenté
                                        </div>
                                        <div style={{ cursor: 'pointer', padding: '2px 4px', borderRadius: '2px' }}
                                             onClick={() => this.updateElementProperty(currentElement.id, 'properties.text', (currentElement.properties.text || '') + '{{assurePrincipal}}')}>
                                            <code style={{ color: '#d63384', fontSize: '10px' }}>{'{{assurePrincipal}}'}</code> - Assuré principal
                                        </div>
                                        <div style={{ cursor: 'pointer', padding: '2px 4px', borderRadius: '2px' }}
                                             onClick={() => this.updateElementProperty(currentElement.id, 'properties.text', (currentElement.properties.text || '') + '{{numeroPolice}}')}>
                                            <code style={{ color: '#d63384', fontSize: '10px' }}>{'{{numeroPolice}}'}</code> - Numéro police
                                        </div>
                                        <div style={{ cursor: 'pointer', padding: '2px 4px', borderRadius: '2px' }}
                                             onClick={() => this.updateElementProperty(currentElement.id, 'properties.text', (currentElement.properties.text || '') + '{{numeroBeneficiaire}}')}>
                                            <code style={{ color: '#d63384', fontSize: '10px' }}>{'{{numeroBeneficiaire}}'}</code> - N° bénéficiaire
                                        </div>
                                        <div style={{ cursor: 'pointer', padding: '2px 4px', borderRadius: '2px' }}
                                             onClick={() => this.updateElementProperty(currentElement.id, 'properties.text', (currentElement.properties.text || '') + '{{dateNaissance}}')}>
                                            <code style={{ color: '#d63384', fontSize: '10px' }}>{'{{dateNaissance}}'}</code> - Date naissance
                                        </div>
                                        <div style={{ cursor: 'pointer', padding: '2px 4px', borderRadius: '2px' }}
                                             onClick={() => this.updateElementProperty(currentElement.id, 'properties.text', (currentElement.properties.text || '') + '{{dateExpiration}}')}>
                                            <code style={{ color: '#d63384', fontSize: '10px' }}>{'{{dateExpiration}}'}</code> - Date expiration
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="field">
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <ScrubbyLabel
                                    value={currentElement.properties.fontSize}
                                    min={Math.min(VALIDATION_RULES.typography.titleFontSize.min, VALIDATION_RULES.typography.infoFontSize.min)}
                                    max={Math.max(VALIDATION_RULES.typography.nameFontSize.max, VALIDATION_RULES.typography.titleFontSize.max)}
                                    step={1}
                                    onChange={(newValue) => this.updateElementProperty(currentElement.id, 'properties.fontSize', newValue)}
                                    icon="pi pi-arrows-h"
                                >
                                    {t('templateEditor.properties.fontSize')}
                                </ScrubbyLabel>
                                <InputNumber
                                    value={currentElement.properties.fontSize}
                                    onChange={(e) => this.updateElementProperty(currentElement.id, 'properties.fontSize', e.value)}
                                    onWheel={(e) => {
                                        const input = e.currentTarget.querySelector('input');
                                        if (input && input === document.activeElement) {
                                            e.preventDefault();
                                            const delta = Math.sign(e.deltaY) * -1;
                                            const currentValue = currentElement.properties.fontSize || 12;
                                            const step = e.shiftKey ? 4 : 1;
                                            // Use comprehensive range from all typography rules (min 8, max 64)
                                            const minSize = Math.min(VALIDATION_RULES.typography.titleFontSize.min, VALIDATION_RULES.typography.infoFontSize.min);
                                            const maxSize = Math.max(VALIDATION_RULES.typography.nameFontSize.max, VALIDATION_RULES.typography.titleFontSize.max);
                                            const newValue = Math.max(minSize, Math.min(maxSize, currentValue + (delta * step)));
                                            this.updateElementProperty(currentElement.id, 'properties.fontSize', newValue);
                                        }
                                    }}
                                    min={Math.min(VALIDATION_RULES.typography.titleFontSize.min, VALIDATION_RULES.typography.infoFontSize.min)}
                                    max={Math.max(VALIDATION_RULES.typography.nameFontSize.max, VALIDATION_RULES.typography.titleFontSize.max)}
                                    inputStyle={{ height: '24px', fontSize: '11px', padding: '2px 6px', textAlign: 'right' }}
                                    style={{ width: '60px' }}
                                    suffix=" px"
                                />
                            </div>
                        </div>
                        <div className="field">
                            <label>{t('templateEditor.properties.fontFamily')}</label>
                            <Dropdown
                                value={currentElement.properties.fontFamily}
                                options={[
                                    { label: 'Arial', value: 'Arial' },
                                    { label: 'Helvetica', value: 'Helvetica' },
                                    { label: 'Times New Roman', value: 'Times New Roman' },
                                    { label: 'Courier New', value: 'Courier New' },
                                    { label: 'Verdana', value: 'Verdana' },
                                    { label: 'Georgia', value: 'Georgia' },
                                    { label: 'Comic Sans MS', value: 'Comic Sans MS' }
                                ]}
                                onChange={(e) => this.updateElementProperty(currentElement.id, 'properties.fontFamily', e.value)}
                                placeholder="Choisir une police"
                            />
                        </div>
                        <div className="field">
                            <label>{t('templateEditor.properties.fontWeight')}</label>
                            <div style={{ display: 'flex', gap: '8px' }}>
                                <Button
                                    label="Normal"
                                    className={currentElement.properties.fontWeight === 'normal' ? 'p-button-primary p-button-sm' : 'p-button-outlined p-button-sm'}
                                    onClick={() => this.updateElementProperty(currentElement.id, 'properties.fontWeight', 'normal')}
                                    style={{ flex: 1 }}
                                />
                                <Button
                                    label="Gras"
                                    className={currentElement.properties.fontWeight === 'bold' ? 'p-button-primary p-button-sm' : 'p-button-outlined p-button-sm'}
                                    onClick={() => this.updateElementProperty(currentElement.id, 'properties.fontWeight', 'bold')}
                                    style={{ flex: 1 }}
                                />
                            </div>
                        </div>
                        <div className="field">
                            <label>Alignement du texte</label>
                            <div style={{ display: 'flex', gap: '8px' }}>
                                <Button
                                    icon="pi pi-align-left"
                                    title="Aligner à gauche"
                                    className={currentElement.properties.textAlign === 'left' ? 'p-button-primary p-button-sm' : 'p-button-outlined p-button-sm'}
                                    onClick={() => this.updateElementProperty(currentElement.id, 'properties.textAlign', 'left')}
                                    style={{ flex: 1 }}
                                />
                                <Button
                                    icon="pi pi-align-center"
                                    title="Centrer"
                                    className={currentElement.properties.textAlign === 'center' ? 'p-button-primary p-button-sm' : 'p-button-outlined p-button-sm'}
                                    onClick={() => this.updateElementProperty(currentElement.id, 'properties.textAlign', 'center')}
                                    style={{ flex: 1 }}
                                />
                                <Button
                                    icon="pi pi-align-right"
                                    title="Aligner à droite"
                                    className={currentElement.properties.textAlign === 'right' ? 'p-button-primary p-button-sm' : 'p-button-outlined p-button-sm'}
                                    onClick={() => this.updateElementProperty(currentElement.id, 'properties.textAlign', 'right')}
                                    style={{ flex: 1 }}
                                />
                            </div>
                        </div>
                        <div className="field">
                            <label>{t('templateEditor.properties.color')}</label>
                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                <ColorPicker
                                    value={currentElement.properties.color}
                                    onChange={(e) => this.updateElementProperty(currentElement.id, 'properties.color', `#${e.value}`)}
                                    style={{ width: '50px' }}
                                />
                                <InputText
                                    value={currentElement.properties.color}
                                    onChange={(e) => this.updateElementProperty(currentElement.id, 'properties.color', e.target.value)}
                                    placeholder="#000000"
                                    style={{ fontSize: '11px', padding: '4px 8px', width: '100px' }}
                                />
                            </div>
                        </div>
                    </>
                );
            case 'photo':
                return (
                    <>
                        <div className="field">
                            <label>{t('templateEditor.properties.shape')}</label>
                            <Dropdown
                                value={element.properties.shape}
                                options={[
                                    { label: t('templateEditor.shapes.rectangle'), value: 'rectangle' },
                                    { label: t('templateEditor.shapes.circle'), value: 'circle' },
                                    { label: t('templateEditor.shapes.roundedRectangle'), value: 'roundedRectangle' }
                                ]}
                                onChange={(e) => this.updateElementProperty(element.id, 'properties.shape', e.value)}
                            />
                        </div>
                        <div className="field">
                            <label>{t('templateEditor.properties.borderColor')}</label>
                            <ColorPicker
                                value={element.properties.borderColor}
                                onChange={(e) => this.updateElementProperty(element.id, 'properties.borderColor', `#${e.value}`)}
                                style={{ width: '50px' }}
                            />
                        </div>
                    </>
                );
            case 'barcode':
                return (
                    <>
                        <div className="field">
                            <label>Données</label>
                            <InputText
                                value={element.properties.data || ''}
                                onChange={(e) => this.updateElementProperty(element.id, 'properties.data', e.target.value)}
                                placeholder="{{barcodeData}} ou texte"
                                style={{ width: '100%', fontSize: '12px' }}
                            />
                            <small style={{ color: '#666', display: 'block', marginTop: '4px' }}>
                                Utilisez {'{{'} et {'}}'}  pour les variables dynamiques
                            </small>
                        </div>
                        <div className="field">
                            <label>Format</label>
                            <Dropdown
                                value={element.properties.format || 'CODE128'}
                                options={[
                                    { label: 'CODE 128', value: 'CODE128' },
                                    { label: 'CODE 39', value: 'CODE39' },
                                    { label: 'EAN-13', value: 'EAN13' }
                                ]}
                                onChange={(e) => this.updateElementProperty(element.id, 'properties.format', e.value)}
                                style={{ width: '100%' }}
                            />
                        </div>
                        <div className="field" style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                            <Checkbox
                                inputId="showText"
                                checked={element.properties.showText !== false}
                                onChange={(e) => this.updateElementProperty(element.id, 'properties.showText', e.checked)}
                            />
                            <label htmlFor="showText" style={{ marginLeft: '8px' }}>Afficher le texte</label>
                        </div>
                        {element.properties.showText !== false && (
                            <>
                                <div className="field">
                                    <ScrubbyLabel
                                        value={element.properties.textSize || 12}
                                        min={8}
                                        max={24}
                                        step={1}
                                        onChange={(newValue) => this.updateElementProperty(element.id, 'properties.textSize', newValue)}
                                        icon="pi pi-arrows-h"
                                    >
                                        Taille texte
                                    </ScrubbyLabel>
                                    <InputNumber
                                        value={element.properties.textSize || 12}
                                        onChange={(e) => this.updateElementProperty(element.id, 'properties.textSize', e.value)}
                                        min={8}
                                        max={24}
                                        style={{ width: '100%' }}
                                    />
                                </div>
                                <div className="field">
                                    <label>Couleur texte</label>
                                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                        <ColorPicker
                                            value={element.properties.textColor || '#000000'}
                                            onChange={(e) => this.updateElementProperty(element.id, 'properties.textColor', `#${e.value}`)}
                                            style={{ width: '50px' }}
                                        />
                                        <InputText
                                            value={element.properties.textColor || '#000000'}
                                            onChange={(e) => this.updateElementProperty(element.id, 'properties.textColor', e.target.value)}
                                            placeholder="#000000"
                                            style={{ fontSize: '11px', padding: '4px 8px', width: '100px' }}
                                        />
                                    </div>
                                </div>
                            </>
                        )}
                    </>
                );
            case 'qrcode':
                return (
                    <div className="field">
                        <label>{t('templateEditor.properties.style')}</label>
                        <Dropdown
                            value={element.properties.style}
                            options={[
                                { label: t('templateEditor.styles.standard'), value: 'standard' },
                                { label: t('templateEditor.styles.rounded'), value: 'rounded' },
                                { label: t('templateEditor.styles.dots'), value: 'dots' },
                                { label: t('templateEditor.styles.square'), value: 'square' }
                            ]}
                            onChange={(e) => this.updateElementProperty(element.id, 'properties.style', e.value)}
                        />
                    </div>
                );
            case 'shape':
                return (
                    <>
                        <div className="field">
                            <label>Couleur de remplissage</label>
                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                <ColorPicker
                                    value={element.properties.fillColor}
                                    onChange={(e) => this.updateElementProperty(element.id, 'properties.fillColor', `#${e.value}`)}
                                    style={{ width: '50px' }}
                                />
                                <InputText
                                    value={element.properties.fillColor}
                                    onChange={(e) => this.updateElementProperty(element.id, 'properties.fillColor', e.target.value)}
                                    placeholder="#DAA520"
                                    style={{ fontSize: '11px', padding: '4px 8px', width: '100px' }}
                                />
                            </div>
                        </div>
                        <div className="field">
                            <label>Couleur de bordure</label>
                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                <ColorPicker
                                    value={element.properties.borderColor}
                                    onChange={(e) => this.updateElementProperty(element.id, 'properties.borderColor', `#${e.value}`)}
                                    style={{ width: '50px' }}
                                />
                                <InputText
                                    value={element.properties.borderColor}
                                    onChange={(e) => this.updateElementProperty(element.id, 'properties.borderColor', e.target.value)}
                                    placeholder="#000000"
                                    style={{ fontSize: '11px', padding: '4px 8px', width: '100px' }}
                                />
                            </div>
                        </div>
                        <div className="field">
                            <ScrubbyLabel
                                value={element.properties.borderWidth}
                                min={0}
                                max={20}
                                step={1}
                                onChange={(newValue) => this.updateElementProperty(element.id, 'properties.borderWidth', newValue)}
                                icon="pi pi-arrows-h"
                            >
                                Épaisseur bordure
                            </ScrubbyLabel>
                            <InputNumber
                                value={element.properties.borderWidth}
                                onChange={(e) => this.updateElementProperty(element.id, 'properties.borderWidth', e.value)}
                                onWheel={(e) => {
                                    const input = e.currentTarget.querySelector('input');
                                    if (input && input === document.activeElement) {
                                        e.preventDefault();
                                        const delta = Math.sign(e.deltaY) * -1;
                                        const currentValue = element.properties.borderWidth || 0;
                                        const step = 1;
                                        const newValue = Math.max(0, Math.min(20, currentValue + (delta * step)));
                                        this.updateElementProperty(element.id, 'properties.borderWidth', newValue);
                                    }
                                }}
                                min={0}
                                max={20}
                                step={1}
                            />
                        </div>
                        {element.subType === 'rectangle' && (
                            <div className="field">
                                <ScrubbyLabel
                                    value={element.properties.cornerRadius || 0}
                                    min={0}
                                    max={50}
                                    step={1}
                                    onChange={(newValue) => this.updateElementProperty(element.id, 'properties.cornerRadius', newValue)}
                                    icon="pi pi-circle"
                                >
                                    Coins arrondis
                                </ScrubbyLabel>
                                <InputNumber
                                    value={element.properties.cornerRadius || 0}
                                    onChange={(e) => this.updateElementProperty(element.id, 'properties.cornerRadius', e.value)}
                                    onWheel={(e) => {
                                        const input = e.currentTarget.querySelector('input');
                                        if (input && input === document.activeElement) {
                                            e.preventDefault();
                                            const delta = Math.sign(e.deltaY) * -1;
                                            const currentValue = element.properties.cornerRadius || 0;
                                            const step = 1;
                                            const newValue = Math.max(0, Math.min(50, currentValue + (delta * step)));
                                            this.updateElementProperty(element.id, 'properties.cornerRadius', newValue);
                                        }
                                    }}
                                    min={0}
                                    max={50}
                                    step={1}
                                />
                            </div>
                        )}

                        {element.subType === 'arc' && (
                            <>
                                <div className="field">
                                    <ScrubbyLabel
                                        value={element.properties.arcHeight || 50}
                                        min={10}
                                        max={300}
                                        step={5}
                                        onChange={(newValue) => this.updateElementProperty(element.id, 'properties.arcHeight', newValue)}
                                        icon="pi pi-arrows-v"
                                    >
                                        Hauteur de courbure
                                    </ScrubbyLabel>
                                    <InputNumber
                                        value={element.properties.arcHeight || 50}
                                        onChange={(e) => this.updateElementProperty(element.id, 'properties.arcHeight', e.value)}
                                        min={10}
                                        max={300}
                                        step={5}
                                        inputStyle={{ height: '28px', fontSize: '11px', padding: '4px 8px', textAlign: 'right', borderRadius: '4px' }}
                                        style={{ width: '70px' }}
                                        suffix=" px"
                                    />
                                </div>
                                <div className="field">
                                    <label style={{ fontSize: '12px', fontWeight: 500, marginBottom: '6px', display: 'block' }}>Direction de la courbure</label>
                                    <div style={{ display: 'flex', gap: '8px' }}>
                                        <Button
                                            label="Vers le bas"
                                            icon="pi pi-arrow-down"
                                            className={element.properties.arcDirection === 'down' ? 'p-button-primary' : 'p-button-outlined'}
                                            onClick={() => this.updateElementProperty(element.id, 'properties.arcDirection', 'down')}
                                            style={{ flex: 1, fontSize: '11px', padding: '6px 12px' }}
                                        />
                                        <Button
                                            label="Vers le haut"
                                            icon="pi pi-arrow-up"
                                            className={element.properties.arcDirection === 'up' ? 'p-button-primary' : 'p-button-outlined'}
                                            onClick={() => this.updateElementProperty(element.id, 'properties.arcDirection', 'up')}
                                            style={{ flex: 1, fontSize: '11px', padding: '6px 12px' }}
                                        />
                                    </div>
                                </div>
                            </>
                        )}
                    </>
                );
            case 'image':
                return (
                    <div className="field">
                        <label>Image</label>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            <input
                                type="file"
                                accept="image/*"
                                id={`imageUpload-${element.id}`}
                                style={{ display: 'none' }}
                                onChange={(e) => this.handleImageUpload(e, element.id)}
                            />
                            <Button
                                label="Télécharger une image"
                                icon="pi pi-upload"
                                className="p-button-sm p-button-outlined"
                                onClick={() => document.getElementById(`imageUpload-${element.id}`).click()}
                                style={{ fontSize: '11px' }}
                            />
                            {element.properties.imageUrl && (
                                <div style={{ marginTop: '5px', position: 'relative' }}>
                                    <img
                                        src={element.properties.imageUrl}
                                        alt="Preview"
                                        style={{ width: '100%', maxHeight: '80px', objectFit: 'contain', background: '#f5f5f5', padding: '5px', borderRadius: '4px' }}
                                    />
                                    <Button
                                        icon="pi pi-times"
                                        className="p-button-sm p-button-text p-button-danger"
                                        onClick={() => this.updateElementProperty(element.id, 'properties.imageUrl', null)}
                                        tooltip="Supprimer"
                                        style={{ fontSize: '10px', marginTop: '5px', padding: '2px 6px' }}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                );
            default:
                return null;
        }
    };

    updateElementProperty = (elementId, path, value) => {
        console.log('[UPDATE] Element:', elementId, 'Path:', path, 'Value:', value);
        const { elements, selectedElement } = this.state;
        let updatedElement = null;

        const updatedElements = elements.map(el => {
            if (el.id === elementId) {
                const pathParts = path.split('.');
                const updated = { ...el };
                let current = updated;

                for (let i = 0; i < pathParts.length - 1; i++) {
                    current[pathParts[i]] = { ...current[pathParts[i]] };
                    current = current[pathParts[i]];
                }

                current[pathParts[pathParts.length - 1]] = value;
                console.log('[UPDATE] Updated element:', updated);
                updatedElement = updated;
                return updated;
            }
            return el;
        });

        // Also update selectedElement if it's the one being modified
        const newState = {
            elements: updatedElements,
            isDirty: true
        };

        if (selectedElement && selectedElement.id === elementId && updatedElement) {
            newState.selectedElement = updatedElement;
        }

        this.setState(newState, () => {
            console.log('[UPDATE] Canvas re-rendered');
            this.renderCanvas(); // Re-render canvas after update
            this.validateTemplate();

            // Debounced history push - wait 500ms after last change
            if (this.propertyUpdateTimer) {
                clearTimeout(this.propertyUpdateTimer);
            }
            this.propertyUpdateTimer = setTimeout(() => {
                this.pushHistory();
            }, 500);
        });
    };

    renderElementPalette = () => {
        return (
            <Panel header="Palette d'éléments" className="element-palette-panel" toggleable>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {/* Basic Elements */}
                    <div>
                        <h6 style={{ fontSize: '11px', fontWeight: 'bold', marginBottom: '8px', color: '#495057' }}>
                            <i className="pi pi-palette mr-2"></i>Éléments de base
                        </h6>
                        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                            <div draggable={true} onDragStart={(e) => this.handleDragStart(e, 'text')} onClick={() => this.addElementToCanvas('text')} title="Texte" className="p-button p-button-rounded p-button-outlined p-button-sm" style={{ cursor: 'grab', width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <i className="pi pi-font" style={{ pointerEvents: 'none', fontSize: '14px' }}></i>
                            </div>
                            <div draggable={true} onDragStart={(e) => this.handleDragStart(e, 'logo')} onClick={() => this.addElementToCanvas('logo')} title="Logo" className="p-button p-button-rounded p-button-outlined p-button-sm" style={{ cursor: 'grab', width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <i className="pi pi-image" style={{ pointerEvents: 'none', fontSize: '14px' }}></i>
                            </div>
                            <div draggable={true} onDragStart={(e) => this.handleDragStart(e, 'photo')} onClick={() => this.addElementToCanvas('photo')} title="Photo" className="p-button p-button-rounded p-button-outlined p-button-sm" style={{ cursor: 'grab', width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <i className="pi pi-user" style={{ pointerEvents: 'none', fontSize: '14px' }}></i>
                            </div>
                            <div draggable={true} onDragStart={(e) => this.handleDragStart(e, 'qrcode')} onClick={() => this.addElementToCanvas('qrcode')} title="QR Code" className="p-button p-button-rounded p-button-outlined p-button-sm" style={{ cursor: 'grab', width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <i className="pi pi-qrcode" style={{ pointerEvents: 'none', fontSize: '14px' }}></i>
                            </div>
                            <div draggable={true} onDragStart={(e) => this.handleDragStart(e, 'barcode')} onClick={() => this.addElementToCanvas('barcode')} title="Code-barres" className="p-button p-button-rounded p-button-outlined p-button-sm" style={{ cursor: 'grab', width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <i className="pi pi-bars" style={{ pointerEvents: 'none', fontSize: '14px' }}></i>
                            </div>
                            <div draggable={true} onDragStart={(e) => this.handleDragStart(e, 'image')} onClick={() => this.addElementToCanvas('image')} title="Image/Signature" className="p-button p-button-rounded p-button-outlined p-button-sm" style={{ cursor: 'grab', width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <i className="pi pi-pencil" style={{ pointerEvents: 'none', fontSize: '14px' }}></i>
                            </div>
                        </div>
                    </div>

                    {/* Shapes */}
                    <div>
                        <h6 style={{ fontSize: '11px', fontWeight: 'bold', marginBottom: '8px', color: '#495057' }}>
                            <i className="pi pi-box mr-2"></i>Formes
                        </h6>
                        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                            <div draggable={true} onDragStart={(e) => this.handleDragStart(e, 'rectangle')} onClick={() => this.addElementToCanvas('rectangle')} title="Rectangle" className="p-button p-button-rounded p-button-outlined p-button-sm" style={{ cursor: 'grab', width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <i className="pi pi-stop" style={{ pointerEvents: 'none', fontSize: '14px' }}></i>
                            </div>
                            <div draggable={true} onDragStart={(e) => this.handleDragStart(e, 'circle')} onClick={() => this.addElementToCanvas('circle')} title="Cercle" className="p-button p-button-rounded p-button-outlined p-button-sm" style={{ cursor: 'grab', width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <i className="pi pi-circle" style={{ pointerEvents: 'none', fontSize: '14px' }}></i>
                            </div>
                            <div draggable={true} onDragStart={(e) => this.handleDragStart(e, 'ellipse')} onClick={() => this.addElementToCanvas('ellipse')} title="Ellipse / Ovale" className="p-button p-button-rounded p-button-outlined p-button-sm" style={{ cursor: 'grab', width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <i className="pi pi-circle" style={{ pointerEvents: 'none', fontSize: '14px', transform: 'scaleX(1.5)' }}></i>
                            </div>
                            <div draggable={true} onDragStart={(e) => this.handleDragStart(e, 'arc')} onClick={() => this.addElementToCanvas('arc')} title="Arc / En-tête courbé" className="p-button p-button-rounded p-button-outlined p-button-sm" style={{ cursor: 'grab', width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <i className="pi pi-sort-amount-up" style={{ pointerEvents: 'none', fontSize: '14px' }}></i>
                            </div>
                            <div draggable={true} onDragStart={(e) => this.handleDragStart(e, 'line')} onClick={() => this.addElementToCanvas('line')} title="Ligne" className="p-button p-button-rounded p-button-outlined p-button-sm" style={{ cursor: 'grab', width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <i className="pi pi-minus" style={{ pointerEvents: 'none', fontSize: '14px' }}></i>
                            </div>
                        </div>
                    </div>

                    {/* Form Fields */}
                    <div>
                        <h6 style={{ fontSize: '11px', fontWeight: 'bold', marginBottom: '8px', color: '#495057' }}>
                            <i className="pi pi-list mr-2"></i>Champs
                        </h6>
                        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                            <div draggable={true} onDragStart={(e) => this.handleDragStart(e, 'inputField')} onClick={() => this.addElementToCanvas('inputField')} title="Champ de texte" className="p-button p-button-rounded p-button-outlined p-button-sm" style={{ cursor: 'grab', width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <i className="pi pi-pencil" style={{ pointerEvents: 'none', fontSize: '14px' }}></i>
                            </div>
                            <div draggable={true} onDragStart={(e) => this.handleDragStart(e, 'dateField')} onClick={() => this.addElementToCanvas('dateField')} title="Champ de date" className="p-button p-button-rounded p-button-outlined p-button-sm" style={{ cursor: 'grab', width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <i className="pi pi-calendar" style={{ pointerEvents: 'none', fontSize: '14px' }}></i>
                            </div>
                            <div draggable={true} onDragStart={(e) => this.handleDragStart(e, 'selectField')} onClick={() => this.addElementToCanvas('selectField')} title="Liste déroulante" className="p-button p-button-rounded p-button-outlined p-button-sm" style={{ cursor: 'grab', width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <i className="pi pi-list" style={{ pointerEvents: 'none', fontSize: '14px' }}></i>
                            </div>
                        </div>
                    </div>
                </div>
            </Panel>
        );
    };

    renderColorSchemePanel = () => {
        const { colorScheme } = this.state;
        const { t } = this.props;

        return (
            <Panel header={t('templateEditor.panels.colorScheme')} toggleable collapsed>
                <div className="p-fluid">
                    <Row>
                        <Col md={6}>
                            <div className="field">
                                <label>{t('templateEditor.colorScheme.primaryColor')}</label>
                                <ColorPicker
                                    value={colorScheme.primaryColor}
                                    onChange={(e) => this.updateColorScheme('primaryColor', `#${e.value}`)}
                                    style={{ width: '50px' }}
                                />
                            </div>
                        </Col>
                        <Col md={6}>
                            <div className="field">
                                <label>{t('templateEditor.colorScheme.secondaryColor')}</label>
                                <ColorPicker
                                    value={colorScheme.secondaryColor}
                                    onChange={(e) => this.updateColorScheme('secondaryColor', `#${e.value}`)}
                                    style={{ width: '50px' }}
                                />
                            </div>
                        </Col>
                    </Row>
                    <div className="field-checkbox mb-3">
                        <Checkbox
                            id="gradientEnabled"
                            checked={colorScheme.gradient.enabled}
                            onChange={(e) => this.updateColorScheme('gradient.enabled', e.checked)}
                        />
                        <label htmlFor="gradientEnabled">{t('templateEditor.colorScheme.enableGradient')}</label>
                    </div>

                    {!colorScheme.gradient.enabled && (
                        <div className="field mb-3">
                            <label>{t('templateEditor.colorScheme.backgroundColor')}</label>
                            <div className="d-flex align-items-center gap-2">
                                <ColorPicker
                                    value={colorScheme.backgroundColor}
                                    onChange={(e) => this.updateColorScheme('backgroundColor', `#${e.value}`)}
                                    style={{ width: '50px' }}
                                />
                                <InputText
                                    value={colorScheme.backgroundColor}
                                    onChange={(e) => this.updateColorScheme('backgroundColor', e.target.value)}
                                    placeholder="#FFFFFF"
                                    style={{ width: '120px' }}
                                />
                            </div>
                        </div>
                    )}

                    {colorScheme.gradient.enabled && (
                        <>
                            <Row>
                                <Col md={6}>
                                    <div className="field">
                                        <label>Couleur début</label>
                                        <div className="d-flex align-items-center gap-2">
                                            <ColorPicker
                                                value={colorScheme.gradient.startColor}
                                                onChange={(e) => this.updateColorScheme('gradient.startColor', `#${e.value}`)}
                                                style={{ width: '50px' }}
                                            />
                                            <InputText
                                                value={colorScheme.gradient.startColor}
                                                onChange={(e) => this.updateColorScheme('gradient.startColor', e.target.value)}
                                                placeholder="#667EEA"
                                                style={{ width: '100px' }}
                                            />
                                        </div>
                                    </div>
                                </Col>
                                <Col md={6}>
                                    <div className="field">
                                        <label>Couleur fin</label>
                                        <div className="d-flex align-items-center gap-2">
                                            <ColorPicker
                                                value={colorScheme.gradient.endColor}
                                                onChange={(e) => this.updateColorScheme('gradient.endColor', `#${e.value}`)}
                                                style={{ width: '50px' }}
                                            />
                                            <InputText
                                                value={colorScheme.gradient.endColor}
                                                onChange={(e) => this.updateColorScheme('gradient.endColor', e.target.value)}
                                                placeholder="#764BA2"
                                                style={{ width: '100px' }}
                                            />
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                            <div className="field">
                                <label>Direction</label>
                                <Dropdown
                                    value={colorScheme.gradient.direction}
                                    options={[
                                        { label: 'Haut vers Bas', value: 'TopToBottom' },
                                        { label: 'Gauche vers Droite', value: 'LeftToRight' },
                                        { label: 'Radial', value: 'Radial' }
                                    ]}
                                    onChange={(e) => this.updateColorScheme('gradient.direction', e.value)}
                                />
                            </div>
                        </>
                    )}
                </div>
            </Panel>
        );
    };

    updateColorScheme = (path, value) => {
        const { colorScheme } = this.state;
        const pathParts = path.split('.');
        const updated = { ...colorScheme };
        let current = updated;

        for (let i = 0; i < pathParts.length - 1; i++) {
            current[pathParts[i]] = { ...current[pathParts[i]] };
            current = current[pathParts[i]];
        }

        current[pathParts[pathParts.length - 1]] = value;
        this.setState({ colorScheme: updated });
    };

    handleLogoUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Validate file type
        if (!file.type.startsWith('image/')) {
            this.showToast('error', 'Erreur', 'Veuillez sélectionner une image');
            return;
        }

        // Validate file size (max 2MB)
        const maxSize = 2 * 1024 * 1024;
        if (file.size > maxSize) {
            this.showToast('error', 'Erreur', 'Le fichier est trop volumineux (max 2MB)');
            return;
        }

        // Convert to Base64
        const reader = new FileReader();
        reader.onloadend = () => {
            // Clear logo cache to force reload
            this.logoImageCache = null;

            this.setState({
                logoConfig: {
                    logoUrl: null,
                    logoBase64: reader.result,
                    useDefaultLogo: false
                }
            }, () => {
                this.validateTemplate();
                this.renderCanvas(); // Re-render canvas with new logo
            });
            this.showToast('success', 'Succès', 'Logo téléchargé avec succès');
        };
        reader.onerror = () => {
            this.showToast('error', 'Erreur', 'Erreur lors du téléchargement du logo');
        };
        reader.readAsDataURL(file);
    };

    handleImageUpload = (e, elementId) => {
        const file = e.target.files[0];
        if (!file) return;

        // Validate file type
        if (!file.type.startsWith('image/')) {
            this.showToast('error', 'Erreur', 'Veuillez sélectionner une image');
            return;
        }

        // Validate file size (max 2MB)
        const maxSize = 2 * 1024 * 1024;
        if (file.size > maxSize) {
            this.showToast('error', 'Erreur', 'Le fichier est trop volumineux (max 2MB)');
            return;
        }

        // Convert to Base64
        const reader = new FileReader();
        reader.onloadend = () => {
            this.updateElementProperty(elementId, 'properties.imageUrl', reader.result);
            this.showToast('success', 'Succès', 'Image téléchargée avec succès');
            this.renderCanvas();
        };
        reader.onerror = () => {
            this.showToast('error', 'Erreur', 'Erreur lors du téléchargement de l\'image');
        };
        reader.readAsDataURL(file);
    };

    addElementToCanvas = (elementType) => {
        // Create new element
        const newElement = this.createNewElement(elementType);

        // Add element to canvas at center position
        const centerX = CARD_WIDTH / 2 - 50;
        const centerY = CARD_HEIGHT / 2 - 10;

        const elementWithPosition = {
            ...newElement,
            enabled: true,
            side: this.state.currentSide,
            position: {
                x: centerX,
                y: centerY,
                width: newElement.position?.width || 100,
                height: newElement.position?.height || 20
            }
        };

        // Add to elements array
        const updatedElements = [...this.state.elements, elementWithPosition];

        this.setState({
            elements: updatedElements,
            selectedElement: elementWithPosition,
            isDirty: true
        }, () => {
            this.pushHistory();
            this.renderCanvas();
            this.validateTemplate();
            this.showToast('success', 'Élément ajouté', `${elementType} ajouté au canvas`);
        });
    };

    deleteElement = (elementId) => {
        const { elements, selectedElement } = this.state;

        // Remove element from array
        const updatedElements = elements.filter(el => el.id !== elementId);

        // Clear selection if deleted element was selected
        const newSelection = selectedElement?.id === elementId ? null : selectedElement;

        this.setState({
            elements: updatedElements,
            selectedElement: newSelection,
            isDirty: true
        }, () => {
            this.pushHistory();
            this.renderCanvas();
            this.validateTemplate();
            this.showToast('success', 'Élément supprimé', 'L\'élément a été supprimé du canvas');
        });
    };

    handleKeyDown = (e) => {
        // Prevent shortcuts if user is typing in an input field
        if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
            return;
        }

        // Undo: Ctrl+Z
        if (e.ctrlKey && e.key === 'z' && !e.shiftKey) {
            e.preventDefault();
            this.undo();
            return;
        }

        // Redo: Ctrl+Y or Ctrl+Shift+Z
        if ((e.ctrlKey && e.key === 'y') || (e.ctrlKey && e.shiftKey && e.key === 'z')) {
            e.preventDefault();
            this.redo();
            return;
        }

        // Copy: Ctrl+C
        if (e.ctrlKey && e.key === 'c') {
            e.preventDefault();
            this.copyElement();
            return;
        }

        // Paste: Ctrl+V
        if (e.ctrlKey && e.key === 'v') {
            e.preventDefault();
            this.pasteElement();
            return;
        }

        // Duplicate: Ctrl+D
        if (e.ctrlKey && e.key === 'd') {
            e.preventDefault();
            this.duplicateElement();
            return;
        }

        // Group: Ctrl+G
        if (e.ctrlKey && e.key === 'g' && !e.shiftKey) {
            e.preventDefault();
            this.createGroup();
            return;
        }

        // Ungroup: Ctrl+Shift+G
        if (e.ctrlKey && e.shiftKey && e.key === 'G') {
            e.preventDefault();
            this.ungroupElements();
            return;
        }

        // Delete: Delete or Backspace
        if ((e.key === 'Delete' || e.key === 'Backspace') && (this.state.selectedElement || this.state.selectedElements.length > 0)) {
            e.preventDefault();
            const elementIds = this.getSelectedElementIds();
            elementIds.forEach(id => this.deleteElement(id));
            this.clearSelection();
            return;
        }

        // Arrow keys for precise movement
        if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key) &&
            (this.state.selectedElement || this.state.selectedElements.length > 0)) {
            e.preventDefault();
            const moveAmount = e.shiftKey ? 10 : 1; // Shift for larger movements
            const elementIds = this.getSelectedElementIds();

            if (elementIds.length === 0) return;

            let deltaX = 0;
            let deltaY = 0;

            switch (e.key) {
                case 'ArrowUp':
                    deltaY = -moveAmount;
                    break;
                case 'ArrowDown':
                    deltaY = moveAmount;
                    break;
                case 'ArrowLeft':
                    deltaX = -moveAmount;
                    break;
                case 'ArrowRight':
                    deltaX = moveAmount;
                    break;
            }

            this.setState(prevState => ({
                elements: prevState.elements.map(el => {
                    if (elementIds.includes(el.id) && el.position) {
                        return {
                            ...el,
                            position: {
                                ...el.position,
                                x: el.position.x + deltaX,
                                y: el.position.y + deltaY
                            }
                        };
                    }
                    return el;
                })
            }), () => {
                this.renderCanvas();
            });
        }
    };

    render() {
        const { visible, onHide, fullPage } = this.props;
        const { validationErrors, previewUrl } = this.state;

        const editorContent = (
            <>
                <Toast ref={this.toast} />

                {/* Template Gallery Modal */}
                {this.renderTemplateGallery()}

                {/* Preview Modal */}
                <Dialog
                    visible={this.state.showPreviewModal}
                    style={{ width: '70vw' }}
                    header={this.props.t('templateEditor.panels.previewTitle')}
                    modal
                    onHide={() => this.setState({ showPreviewModal: false })}
                >
                    {this.state.previewUrl ? (
                        <div style={{ textAlign: 'center' }}>
                            <img
                                src={this.state.previewUrl}
                                alt={this.props.t('templateEditor.messages.previewAlt')}
                                style={{ maxWidth: '100%', height: 'auto', border: '1px solid #dee2e6', borderRadius: '8px' }}
                            />
                            <div className="mt-3">
                                <h6>{this.props.t('templateEditor.panels.availableVariables')}:</h6>
                                <div className="variables-list" style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', justifyContent: 'center' }}>
                                    <code>{'{{NOM}}'}</code>
                                    <code>{'{{PRENOM}}'}</code>
                                    <code>{'{{NUMERO_ASSURE}}'}</code>
                                    <code>{'{{POLICE}}'}</code>
                                    <code>{'{{DATE_EXPIRATION}}'}</code>
                                    <code>{'{{CARD_DATA}}'}</code>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div style={{ textAlign: 'center', padding: '40px' }}>
                            <i className="pi pi-spin pi-spinner" style={{ fontSize: '3rem', color: '#6b7280' }}></i>
                            <p className="text-muted mt-3">Génération de l'aperçu en cours...</p>
                        </div>
                    )}
                </Dialog>

                <div className={`template-editor-container ${fullPage ? 'full-page' : ''}`}>
                    {this.renderToolbar()}

                    <div className="editor-content">
                        {fullPage ? (
                            // Full page layout - Same 3-column structure
                            <Row style={{ height: '100%', margin: 0 }}>
                                {/* Left Panel - Element Palette & Color Scheme */}
                                <Col lg={2} style={{ padding: '10px', maxHeight: '85vh', overflowY: 'auto' }}>
                                    <div className="canvas-main">
                                    <div className="element-palette full-page">
                                        <h5 className="palette-title">
                                            <i className="pi pi-palette mr-2"></i>
                                            {this.props.t('templateEditor.panels.availableElements')}
                                        </h5>

                                        {/* Background Color Section */}
                                        <div className="palette-section" style={{ padding: '15px', borderBottom: '1px solid #e0e0e0' }}>
                                            <h6 style={{ marginBottom: '10px', fontSize: '13px', fontWeight: 'bold' }}>
                                                <i className="pi pi-image mr-2"></i>
                                                {this.props.t('templateEditor.panels.background')}
                                            </h6>
                                            <div className="field-checkbox mb-2">
                                                <Checkbox
                                                    id="gradientEnabledPalette"
                                                    checked={this.state.colorScheme.gradient.enabled}
                                                    onChange={(e) => this.updateColorScheme('gradient.enabled', e.checked)}
                                                />
                                                <label htmlFor="gradientEnabledPalette" style={{ fontSize: '12px' }}>{this.props.t('templateEditor.panels.gradient')}</label>
                                            </div>

                                            {!this.state.colorScheme.gradient.enabled ? (
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                    <ColorPicker
                                                        value={this.state.colorScheme.backgroundColor}
                                                        onChange={(e) => this.updateColorScheme('backgroundColor', `#${e.value}`)}
                                                        style={{ width: '50px' }}
                                                    />
                                                    <InputText
                                                        value={this.state.colorScheme.backgroundColor}
                                                        onChange={(e) => this.updateColorScheme('backgroundColor', e.target.value)}
                                                        placeholder="#FFFFFF"
                                                        style={{ fontSize: '11px', padding: '4px 8px' }}
                                                    />
                                                </div>
                                            ) : (
                                                <div>
                                                    <div style={{ marginBottom: '8px' }}>
                                                        <label style={{ fontSize: '11px', display: 'block', marginBottom: '4px' }}>Début</label>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                            <ColorPicker
                                                                value={this.state.colorScheme.gradient.startColor}
                                                                onChange={(e) => this.updateColorScheme('gradient.startColor', `#${e.value}`)}
                                                            />
                                                            <InputText
                                                                value={this.state.colorScheme.gradient.startColor}
                                                                onChange={(e) => this.updateColorScheme('gradient.startColor', e.target.value)}
                                                                placeholder="#667EEA"
                                                                style={{ fontSize: '11px', padding: '4px 8px' }}
                                                            />
                                                        </div>
                                                    </div>
                                                    <div style={{ marginBottom: '8px' }}>
                                                        <label style={{ fontSize: '11px', display: 'block', marginBottom: '4px' }}>Fin</label>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                            <ColorPicker
                                                                value={this.state.colorScheme.gradient.endColor}
                                                                onChange={(e) => this.updateColorScheme('gradient.endColor', `#${e.value}`)}
                                                            />
                                                            <InputText
                                                                value={this.state.colorScheme.gradient.endColor}
                                                                onChange={(e) => this.updateColorScheme('gradient.endColor', e.target.value)}
                                                                placeholder="#764BA2"
                                                                style={{ fontSize: '11px', padding: '4px 8px' }}
                                                            />
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <label style={{ fontSize: '11px', display: 'block', marginBottom: '4px' }}>Direction</label>
                                                        <Dropdown
                                                            value={this.state.colorScheme.gradient.direction}
                                                            options={[
                                                                { label: 'Haut → Bas', value: 'TopToBottom' },
                                                                { label: 'Gauche → Droite', value: 'LeftToRight' }
                                                            ]}
                                                            onChange={(e) => this.updateColorScheme('gradient.direction', e.value)}
                                                            style={{ fontSize: '11px' }}
                                                        />
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                        {/* Background Image Upload Section */}
                                        <div className="palette-section" style={{ padding: '15px', borderTop: '1px solid #e0e0e0' }}>
                                            <h6 style={{ marginBottom: '10px', fontSize: '13px', fontWeight: 'bold' }}>
                                                <i className="pi pi-image mr-2"></i>
                                                Image de fond
                                            </h6>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                                {/* Front Background */}
                                                <div>
                                                    <label style={{ fontSize: '11px', display: 'block', marginBottom: '4px', fontWeight: 'bold' }}>
                                                        Avant (Front)
                                                    </label>
                                                    <input
                                                        type="file"
                                                        accept="image/png,image/jpeg,image/jpg"
                                                        id="bgImageFrontUpload"
                                                        style={{ display: 'none' }}
                                                        onChange={(e) => this.handleBackgroundImageUpload(e, 'front')}
                                                    />
                                                    <Button
                                                        label="Charger"
                                                        icon="pi pi-upload"
                                                        className="p-button-sm p-button-outlined"
                                                        onClick={() => document.getElementById('bgImageFrontUpload').click()}
                                                        style={{ fontSize: '11px', marginBottom: '5px', width: '100%' }}
                                                    />
                                                    {this.state.backgroundImage.frontImageUrl && (
                                                        <div style={{ marginTop: '5px' }}>
                                                            <img
                                                                src={this.state.backgroundImage.frontImageUrl}
                                                                alt="Fond avant"
                                                                style={{ width: '100%', maxHeight: '60px', objectFit: 'contain', background: '#f5f5f5', padding: '5px', borderRadius: '4px', marginBottom: '5px' }}
                                                            />
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '5px', marginBottom: '5px' }}>
                                                                <ScrubbyLabel
                                                                    value={Math.round(this.state.backgroundImage.frontImageOpacity * 100)}
                                                                    min={0}
                                                                    max={100}
                                                                    step={5}
                                                                    onChange={(newValue) => this.updateBackgroundOpacity('front', newValue / 100)}
                                                                    icon="pi pi-circle"
                                                                    style={{ fontSize: '10px', minWidth: '50px' }}
                                                                >
                                                                    Opacité:
                                                                </ScrubbyLabel>
                                                                <InputNumber
                                                                    value={Math.round(this.state.backgroundImage.frontImageOpacity * 100)}
                                                                    onChange={(e) => this.updateBackgroundOpacity('front', e.value / 100)}
                                                                    onWheel={(e) => {
                                                                        const input = e.currentTarget.querySelector('input');
                                                                        if (input && input === document.activeElement) {
                                                                            e.preventDefault();
                                                                            const delta = Math.sign(e.deltaY) * -1;
                                                                            const currentValue = Math.round(this.state.backgroundImage.frontImageOpacity * 100);
                                                                            const step = e.shiftKey ? 10 : 5;
                                                                            const newValue = Math.max(0, Math.min(100, currentValue + (delta * step)));
                                                                            this.updateBackgroundOpacity('front', newValue / 100);
                                                                        }
                                                                    }}
                                                                    min={0}
                                                                    max={100}
                                                                    suffix="%"
                                                                    inputStyle={{ fontSize: '10px', padding: '2px 4px', textAlign: 'right' }}
                                                                    style={{ flex: 1 }}
                                                                />
                                                            </div>
                                                            <Button
                                                                label="Supprimer"
                                                                icon="pi pi-times"
                                                                className="p-button-sm p-button-text p-button-danger"
                                                                onClick={() => this.removeBackgroundImage('front')}
                                                                style={{ fontSize: '10px', padding: '2px 6px' }}
                                                            />
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Back Background */}
                                                <div>
                                                    <label style={{ fontSize: '11px', display: 'block', marginBottom: '4px', fontWeight: 'bold' }}>
                                                        Arrière (Back)
                                                    </label>
                                                    <input
                                                        type="file"
                                                        accept="image/png,image/jpeg,image/jpg"
                                                        id="bgImageBackUpload"
                                                        style={{ display: 'none' }}
                                                        onChange={(e) => this.handleBackgroundImageUpload(e, 'back')}
                                                    />
                                                    <Button
                                                        label="Charger"
                                                        icon="pi pi-upload"
                                                        className="p-button-sm p-button-outlined"
                                                        onClick={() => document.getElementById('bgImageBackUpload').click()}
                                                        style={{ fontSize: '11px', marginBottom: '5px', width: '100%' }}
                                                    />
                                                    {this.state.backgroundImage.backImageUrl && (
                                                        <div style={{ marginTop: '5px' }}>
                                                            <img
                                                                src={this.state.backgroundImage.backImageUrl}
                                                                alt="Fond arrière"
                                                                style={{ width: '100%', maxHeight: '60px', objectFit: 'contain', background: '#f5f5f5', padding: '5px', borderRadius: '4px', marginBottom: '5px' }}
                                                            />
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '5px', marginBottom: '5px' }}>
                                                                <ScrubbyLabel
                                                                    value={Math.round(this.state.backgroundImage.backImageOpacity * 100)}
                                                                    min={0}
                                                                    max={100}
                                                                    step={5}
                                                                    onChange={(newValue) => this.updateBackgroundOpacity('back', newValue / 100)}
                                                                    icon="pi pi-circle"
                                                                    style={{ fontSize: '10px', minWidth: '50px' }}
                                                                >
                                                                    Opacité:
                                                                </ScrubbyLabel>
                                                                <InputNumber
                                                                    value={Math.round(this.state.backgroundImage.backImageOpacity * 100)}
                                                                    onChange={(e) => this.updateBackgroundOpacity('back', e.value / 100)}
                                                                    onWheel={(e) => {
                                                                        const input = e.currentTarget.querySelector('input');
                                                                        if (input && input === document.activeElement) {
                                                                            e.preventDefault();
                                                                            const delta = Math.sign(e.deltaY) * -1;
                                                                            const currentValue = Math.round(this.state.backgroundImage.backImageOpacity * 100);
                                                                            const step = e.shiftKey ? 10 : 5;
                                                                            const newValue = Math.max(0, Math.min(100, currentValue + (delta * step)));
                                                                            this.updateBackgroundOpacity('back', newValue / 100);
                                                                        }
                                                                    }}
                                                                    min={0}
                                                                    max={100}
                                                                    suffix="%"
                                                                    inputStyle={{ fontSize: '10px', padding: '2px 4px', textAlign: 'right' }}
                                                                    style={{ flex: 1 }}
                                                                />
                                                            </div>
                                                            <Button
                                                                label="Supprimer"
                                                                icon="pi pi-times"
                                                                className="p-button-sm p-button-text p-button-danger"
                                                                onClick={() => this.removeBackgroundImage('back')}
                                                                style={{ fontSize: '10px', padding: '2px 6px' }}
                                                            />
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Logo Upload Section */}
                                        <div className="palette-section" style={{ padding: '15px', borderTop: '1px solid #e0e0e0' }}>
                                            <h6 style={{ marginBottom: '10px', fontSize: '13px', fontWeight: 'bold' }}>
                                                <i className="pi pi-upload mr-2"></i>
                                                {this.props.t('templateEditor.panels.customLogo')}
                                            </h6>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                                <input
                                                    type="file"
                                                    accept="image/*"
                                                    id="logoUpload"
                                                    style={{ display: 'none' }}
                                                    onChange={this.handleLogoUpload}
                                                />
                                                <Button
                                                    label={this.props.t('templateEditor.panels.upload')}
                                                    icon="pi pi-upload"
                                                    className="p-button-sm p-button-outlined"
                                                    onClick={() => document.getElementById('logoUpload').click()}
                                                    style={{ fontSize: '11px' }}
                                                />
                                                {this.state.logoConfig.logoBase64 && (
                                                    <div style={{ marginTop: '5px', position: 'relative' }}>
                                                        <img
                                                            src={this.state.logoConfig.logoBase64}
                                                            alt="Logo"
                                                            style={{ width: '100%', maxHeight: '60px', objectFit: 'contain', background: '#f5f5f5', padding: '5px', borderRadius: '4px' }}
                                                        />
                                                        <Button
                                                            icon="pi pi-times"
                                                            className="p-button-sm p-button-text p-button-danger"
                                                            onClick={() => {
                                                                this.logoImageCache = null;
                                                                this.setState({ logoConfig: { logoUrl: null, logoBase64: null, useDefaultLogo: true } }, () => {
                                                                    this.renderCanvas();
                                                                });
                                                            }}
                                                            tooltip="Supprimer"
                                                            style={{ fontSize: '10px', marginTop: '5px', padding: '2px 6px' }}
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {/* Elements Palette */}
                                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', paddingTop: '10px' }}>
                                            <div
                                                draggable={true}
                                                onDragStart={(e) => this.handleDragStart(e, 'text')}
                                                onClick={() => this.addElementToCanvas('text')}
                                                title="Texte"
                                                className="p-button p-button-rounded p-button-outlined"
                                                style={{
                                                    cursor: 'grab',
                                                    width: '40px',
                                                    height: '40px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    border: '1px solid #ced4da',
                                                    borderRadius: '50%',
                                                    background: 'transparent',
                                                    userSelect: 'none'
                                                }}
                                            >
                                                <i className="pi pi-font" style={{ pointerEvents: 'none' }}></i>
                                            </div>
                                            <div
                                                draggable={true}
                                                onDragStart={(e) => this.handleDragStart(e, 'barcode')}
                                                onClick={() => this.addElementToCanvas('barcode')}
                                                title="Code-barres"
                                                className="p-button p-button-rounded p-button-outlined"
                                                style={{
                                                    cursor: 'grab',
                                                    width: '40px',
                                                    height: '40px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    border: '1px solid #ced4da',
                                                    borderRadius: '50%',
                                                    background: 'transparent',
                                                    userSelect: 'none'
                                                }}
                                            >
                                                <i className="pi pi-bars" style={{ pointerEvents: 'none' }}></i>
                                            </div>
                                            <div
                                                draggable={true}
                                                onDragStart={(e) => this.handleDragStart(e, 'logo')}
                                                onClick={() => this.addElementToCanvas('logo')}
                                                title="Logo"
                                                className="p-button p-button-rounded p-button-outlined"
                                                style={{
                                                    cursor: 'grab',
                                                    width: '40px',
                                                    height: '40px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    border: '1px solid #ced4da',
                                                    borderRadius: '50%',
                                                    background: 'transparent',
                                                    userSelect: 'none'
                                                }}
                                            >
                                                <i className="pi pi-image" style={{ pointerEvents: 'none' }}></i>
                                            </div>
                                            <div
                                                draggable={true}
                                                onDragStart={(e) => this.handleDragStart(e, 'photo')}
                                                onClick={() => this.addElementToCanvas('photo')}
                                                title="Photo"
                                                className="p-button p-button-rounded p-button-outlined"
                                                style={{
                                                    cursor: 'grab',
                                                    width: '40px',
                                                    height: '40px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    border: '1px solid #ced4da',
                                                    borderRadius: '50%',
                                                    background: 'transparent',
                                                    userSelect: 'none'
                                                }}
                                            >
                                                <i className="pi pi-user" style={{ pointerEvents: 'none' }}></i>
                                            </div>
                                            <div
                                                draggable={true}
                                                onDragStart={(e) => this.handleDragStart(e, 'qrcode')}
                                                onClick={() => this.addElementToCanvas('qrcode')}
                                                title="QR Code"
                                                className="p-button p-button-rounded p-button-outlined"
                                                style={{
                                                    cursor: 'grab',
                                                    width: '40px',
                                                    height: '40px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    border: '1px solid #ced4da',
                                                    borderRadius: '50%',
                                                    background: 'transparent',
                                                    userSelect: 'none'
                                                }}
                                            >
                                                <i className="pi pi-qrcode" style={{ pointerEvents: 'none' }}></i>
                                            </div>
                                            <div
                                                draggable={true}
                                                onDragStart={(e) => this.handleDragStart(e, 'image')}
                                                onClick={() => this.addElementToCanvas('image')}
                                                title="Image/Signature"
                                                className="p-button p-button-rounded p-button-outlined"
                                                style={{
                                                    cursor: 'grab',
                                                    width: '40px',
                                                    height: '40px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    border: '1px solid #ced4da',
                                                    borderRadius: '50%',
                                                    background: 'transparent',
                                                    userSelect: 'none'
                                                }}
                                            >
                                                <i className="pi pi-pencil" style={{ pointerEvents: 'none' }}></i>
                                            </div>
                                        </div>

                                        {/* Security Elements Section */}
                                        <div className="palette-section" style={{ padding: '15px 15px 8px', borderTop: '1px solid #e0e0e0' }}>
                                            <h6 style={{ marginBottom: '8px', fontSize: '13px', fontWeight: 'bold' }}>
                                                <i className="pi pi-shield mr-2"></i>
                                                Sécurité
                                            </h6>
                                            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                                <div draggable={true} onDragStart={(e) => this.handleDragStart(e, 'watermark')} onClick={() => this.addElementToCanvas('watermark')} title="Filigrane" className="p-button p-button-rounded p-button-outlined" style={{ cursor: 'grab', width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #ced4da', borderRadius: '50%', background: 'transparent', userSelect: 'none' }}>
                                                    <i className="pi pi-shield" style={{ pointerEvents: 'none' }}></i>
                                                </div>
                                                <div draggable={true} onDragStart={(e) => this.handleDragStart(e, 'microtext')} onClick={() => this.addElementToCanvas('microtext')} title="Micro-texte" className="p-button p-button-rounded p-button-outlined" style={{ cursor: 'grab', width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #ced4da', borderRadius: '50%', background: 'transparent', userSelect: 'none' }}>
                                                    <i className="pi pi-file-edit" style={{ pointerEvents: 'none' }}></i>
                                                </div>
                                                <div draggable={true} onDragStart={(e) => this.handleDragStart(e, 'securityBorder')} onClick={() => this.addElementToCanvas('securityBorder')} title="Bordure sécurisée" className="p-button p-button-rounded p-button-outlined" style={{ cursor: 'grab', width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #ced4da', borderRadius: '50%', background: 'transparent', userSelect: 'none' }}>
                                                    <i className="pi pi-stop" style={{ pointerEvents: 'none' }}></i>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Shapes Section */}
                                        <div className="palette-section" style={{ padding: '15px 15px 8px', borderTop: '1px solid #e0e0e0' }}>
                                            <h6 style={{ marginBottom: '8px', fontSize: '13px', fontWeight: 'bold' }}>
                                                <i className="pi pi-box mr-2"></i>
                                                Formes
                                            </h6>
                                            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                                <div draggable={true} onDragStart={(e) => this.handleDragStart(e, 'rectangle')} onClick={() => this.addElementToCanvas('rectangle')} title="Rectangle" className="p-button p-button-rounded p-button-outlined" style={{ cursor: 'grab', width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #ced4da', borderRadius: '50%', background: 'transparent', userSelect: 'none' }}>
                                                    <i className="pi pi-stop" style={{ pointerEvents: 'none' }}></i>
                                                </div>
                                                <div draggable={true} onDragStart={(e) => this.handleDragStart(e, 'circle')} onClick={() => this.addElementToCanvas('circle')} title="Cercle" className="p-button p-button-rounded p-button-outlined" style={{ cursor: 'grab', width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #ced4da', borderRadius: '50%', background: 'transparent', userSelect: 'none' }}>
                                                    <i className="pi pi-circle" style={{ pointerEvents: 'none' }}></i>
                                                </div>
                                                <div draggable={true} onDragStart={(e) => this.handleDragStart(e, 'line')} onClick={() => this.addElementToCanvas('line')} title="Ligne" className="p-button p-button-rounded p-button-outlined" style={{ cursor: 'grab', width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #ced4da', borderRadius: '50%', background: 'transparent', userSelect: 'none' }}>
                                                    <i className="pi pi-minus" style={{ pointerEvents: 'none' }}></i>
                                                </div>
                                                <div draggable={true} onDragStart={(e) => this.handleDragStart(e, 'ellipse')} onClick={() => this.addElementToCanvas('ellipse')} title="Ellipse / Ovale" className="p-button p-button-rounded p-button-outlined" style={{ cursor: 'grab', width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #ced4da', borderRadius: '50%', background: 'transparent', userSelect: 'none' }}>
                                                    <i className="pi pi-circle" style={{ pointerEvents: 'none', transform: 'scaleX(1.5)' }}></i>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Custom Text Elements Section */}
                                        <div className="palette-section" style={{ padding: '15px 15px 8px', borderTop: '1px solid #e0e0e0' }}>
                                            <h6 style={{ marginBottom: '8px', fontSize: '13px', fontWeight: 'bold' }}>
                                                <i className="pi pi-building mr-2"></i>
                                                Textes personnalisés
                                            </h6>
                                            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                                <div draggable={true} onDragStart={(e) => this.handleDragStart(e, 'organizationTitle')} onClick={() => this.addElementToCanvas('organizationTitle')} title="Titre organisation" className="p-button p-button-rounded p-button-outlined" style={{ cursor: 'grab', width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #ced4da', borderRadius: '50%', background: 'transparent', userSelect: 'none' }}>
                                                    <i className="pi pi-bookmark" style={{ pointerEvents: 'none' }}></i>
                                                </div>
                                                <div draggable={true} onDragStart={(e) => this.handleDragStart(e, 'organizationSubtitle')} onClick={() => this.addElementToCanvas('organizationSubtitle')} title="Sous-titre" className="p-button p-button-rounded p-button-outlined" style={{ cursor: 'grab', width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #ced4da', borderRadius: '50%', background: 'transparent', userSelect: 'none' }}>
                                                    <i className="pi pi-tag" style={{ pointerEvents: 'none' }}></i>
                                                </div>
                                                <div draggable={true} onDragStart={(e) => this.handleDragStart(e, 'securityMessage')} onClick={() => this.addElementToCanvas('securityMessage')} title="Message sécurité" className="p-button p-button-rounded p-button-outlined" style={{ cursor: 'grab', width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #ced4da', borderRadius: '50%', background: 'transparent', userSelect: 'none' }}>
                                                    <i className="pi pi-exclamation-triangle" style={{ pointerEvents: 'none' }}></i>
                                                </div>
                                                <div draggable={true} onDragStart={(e) => this.handleDragStart(e, 'contactMessage')} onClick={() => this.addElementToCanvas('contactMessage')} title="Contact" className="p-button p-button-rounded p-button-outlined" style={{ cursor: 'grab', width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #ced4da', borderRadius: '50%', background: 'transparent', userSelect: 'none' }}>
                                                    <i className="pi pi-phone" style={{ pointerEvents: 'none' }}></i>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Form Fields Section */}
                                        <div className="palette-section" style={{ padding: '15px 15px 8px', borderTop: '1px solid #e0e0e0' }}>
                                            <h6 style={{ marginBottom: '8px', fontSize: '13px', fontWeight: 'bold' }}>
                                                <i className="pi pi-list mr-2"></i>
                                                Champs de formulaire
                                            </h6>
                                            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                                <div draggable={true} onDragStart={(e) => this.handleDragStart(e, 'inputField')} onClick={() => this.addElementToCanvas('inputField')} title="Champ de texte" className="p-button p-button-rounded p-button-outlined" style={{ cursor: 'grab', width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #ced4da', borderRadius: '50%', background: 'transparent', userSelect: 'none' }}>
                                                    <i className="pi pi-pencil" style={{ pointerEvents: 'none' }}></i>
                                                </div>
                                                <div draggable={true} onDragStart={(e) => this.handleDragStart(e, 'dateField')} onClick={() => this.addElementToCanvas('dateField')} title="Champ de date" className="p-button p-button-rounded p-button-outlined" style={{ cursor: 'grab', width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #ced4da', borderRadius: '50%', background: 'transparent', userSelect: 'none' }}>
                                                    <i className="pi pi-calendar" style={{ pointerEvents: 'none' }}></i>
                                                </div>
                                                <div draggable={true} onDragStart={(e) => this.handleDragStart(e, 'selectField')} onClick={() => this.addElementToCanvas('selectField')} title="Liste déroulante" className="p-button p-button-rounded p-button-outlined" style={{ cursor: 'grab', width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #ced4da', borderRadius: '50%', background: 'transparent', userSelect: 'none' }}>
                                                    <i className="pi pi-list" style={{ pointerEvents: 'none' }}></i>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    </div>
                                </Col>

                                {/* Center Panel - Canvas */}
                                <Col lg={6} style={{ padding: '10px' }}>
                                    <Card title={this.props.t('templateEditor.panels.canvasDesign')} className="canvas-card" style={{ height: '100%' }}>
                                        <div
                                            className="canvas-container"
                                            onDragOver={this.handleDragOver}
                                            onDrop={this.handleDrop}
                                            style={{ minHeight: '500px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                        >
                                            <canvas
                                                ref={this.canvasRef}
                                                width={CARD_WIDTH * SCALE_FACTOR}
                                                height={CARD_HEIGHT * SCALE_FACTOR}
                                                className="editor-canvas"
                                                style={{ display: 'block', background: 'white' }}
                                                onClick={this.handleCanvasClick}
                                                onMouseDown={this.handleCanvasMouseDown}
                                                onMouseMove={this.handleCanvasMouseMoveAndHover}
                                                onMouseUp={this.handleCanvasMouseUp}
                                                onMouseLeave={this.handleCanvasMouseUp}
                                            />
                                        </div>

                                        {validationErrors.length > 0 && (
                                            <div className="validation-errors mt-3">
                                                <h6 className="text-danger">Erreurs de validation:</h6>
                                                <ul>
                                                    {validationErrors.map((error, index) => (
                                                        <li key={index} className="text-danger">{error}</li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}
                                    </Card>
                                </Col>

                                {/* Right Panel - Properties (More Space) */}
                                <Col lg={4} style={{ padding: '10px', maxHeight: '85vh', overflowY: 'auto' }}>
                                    <Card
                                        title="Propriétés"
                                        className="properties-card"
                                        style={{ height: '100%' }}
                                    >
                                        {this.renderPropertiesPanel()}
                                    </Card>
                                </Col>
                            </Row>
                        ) : (
                            // Modal layout - Element Palette (Left) | Canvas (Center) | Properties (Right)
                            <Row>
                                {/* Left Panel - Element Palette & Color Scheme */}
                                <Col lg={2}>
                                    {this.renderElementPalette()}
                                    {this.renderElementsList()}
                                    {this.renderColorSchemePanel()}
                                </Col>

                                {/* Center Panel - Canvas */}
                                <Col lg={6}>
                                    <Card title="Canvas" className="canvas-card">
                                        <div
                                            className="canvas-container"
                                            onDragOver={this.handleDragOver}
                                            onDrop={this.handleDrop}
                                        >
                                            <canvas
                                                ref={this.canvasRef}
                                                width={CARD_WIDTH * SCALE_FACTOR}
                                                height={CARD_HEIGHT * SCALE_FACTOR}
                                                className="editor-canvas"
                                                onClick={this.handleCanvasClick}
                                                onMouseDown={this.handleCanvasMouseDown}
                                                onMouseMove={this.handleCanvasMouseMoveAndHover}
                                                onMouseUp={this.handleCanvasMouseUp}
                                                onMouseLeave={this.handleCanvasMouseUp}
                                            />
                                        </div>

                                        {validationErrors.length > 0 && (
                                            <div className="validation-errors mt-3">
                                                <h6 className="text-danger">Erreurs de validation:</h6>
                                                <ul>
                                                    {validationErrors.map((error, index) => (
                                                        <li key={index} className="text-danger">{error}</li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}
                                    </Card>
                                </Col>

                                {/* Right Panel - Properties (More Space) */}
                                <Col lg={4} style={{ maxHeight: '85vh', overflowY: 'auto' }}>
                                    <Card
                                        title="Propriétés"
                                        className="properties-card"
                                        style={{ height: '100%' }}
                                    >
                                        {this.renderPropertiesPanel()}
                                    </Card>
                                </Col>
                            </Row>
                        )}
                    </div>
                </div>
            </>
        );

        // Return full page layout or modal based on props
        if (fullPage) {
            return (
                <div className={`template-drag-drop-editor full-page`}>
                    {editorContent}
                </div>
            );
        }

        return (
            <Dialog
                visible={visible}
                style={{ width: '95vw', height: '90vh' }}
                header={this.props.t('templateEditor.title')}
                modal
                onHide={onHide}
                maximizable
            >
                {editorContent}
            </Dialog>
        );
    }
}

export default withTranslation()(TemplateDragDropEditor);
